// src/components/ConnectorsSection.jsx
import React from "react";
import { showControlRoomTab, useRefreshOnReturn } from "../lib/controlRoomTabs";
import { watchSignInPopup } from "../lib/signInPopup";
import { toast } from "../lib/toast";
import { dialog } from "../lib/dialog";
import asanaLogo from "./../images/asana.svg";
import hubspotLogo from "./../images/hubspot.png";
import jiraLogo from "./../images/jira.svg";
import slackLogo from "./../images/slack.svg";
import PlannerPlanPicker from "./PlannerPlanPicker";
import FancySelect from "./FancySelect";
import Avatar from "./Avatar";
import {
  MAX_CLICKUP_LISTS,
  MAX_MONDAY_BOARDS,
  MAX_NOTION_DATA_SOURCES,
  NOTION_STATUS_TYPES,
  PM_DEFAULT_LOOKBACK_DAYS,
  clickupPrefsShape,
  mondayMapShape,
  mondayPrefsShape,
  notionIdShape,
  notionMapShape,
  notionPrefsShape,
} from "../lib/taskTools";

import teamsLogo from "./../images/microsoftteams.svg";
import {
  ClickUpLogo,
  GitHubLogo,
  LinearLogo,
  MondayLogo,
  NotionLogo,
  PipedriveLogo,
} from "./ToolIcon";
const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.bossdrop.ai";

// Fallback mark for a connector with no logo. The defaults are tokens so the
// glyph stays visible in the light theme too -- the old literals (a white wash
// on white, light text, white inset ring) were invisible there.
function ToolGlyph({
  src,
  label,
  bg = "var(--chip-bg)",
  fg = "var(--text)",
  text = null,
}) {
  if (src) {
    return <img src={src} alt="" className="toolLogo" />;
  }
  return (
    <span
      aria-hidden
      style={{
        width: 22,
        height: 22,
        borderRadius: 6,
        display: "inline-grid",
        placeItems: "center",
        background: bg,
        color: fg,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0,
        lineHeight: 1,
        boxShadow: "0 0 0 1px var(--card-bd) inset",
      }}
      title={label}
    >
      {String(text || label || "?")
        .trim()
        .slice(0, 1)
        .toUpperCase()}
    </span>
  );
}

// The Linear, GitHub, ClickUp, Notion and monday.com marks live in
// ToolIcon.js, shared with the Audit Logs rows.

// A connector's place on the page, for the jump from a custom automation's card.
const connectorAnchor = (name) => `connector-${String(name).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
const CONNECTOR_NAMES = {
  teams: "Microsoft Teams",
  asana: "Asana",
  jira: "Jira",
  linear: "Linear",
  clickup: "ClickUp",
  notion: "Notion",
  monday: "monday.com",
  hubspot: "HubSpot",
  pipedrive: "Pipedrive",
  slack: "Slack",
};

/* ---------- Uniform connector frame (collapses to a compact row when connected) ---------- */
function ConnectorFrame({
  logo,
  name,
  connected,
  account,
  expanded,
  onToggle,
  children,
}) {
  // Collapsed row and the expanded card are the same card at the same depth,
  // so the padding comes from the shared rule — this used to override it and
  // the connector visibly resized when you clicked Manage.
  if (connected && !expanded) {
    return (
      <div
        id={connectorAnchor(name)}
        className="panelSub"
        style={{ display: "flex", alignItems: "center", gap: 10 }}
      >
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          {logo}
          <span style={{ fontWeight: 600 }}>{name}</span>
        </span>
        {account ? (
          <span
            className="muted small"
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {account}
          </span>
        ) : null}
        <span
          className="chip ok"
          style={{ marginLeft: "auto", flex: "0 0 auto" }}
          title="Connection status"
        >
          <span className="chipDot" />
          Connected
        </span>
        <button
          type="button"
          className="btn"
          style={{ flex: "0 0 auto" }}
          onClick={onToggle}
        >
          Manage
        </button>
      </div>
    );
  }
  return (
    <div id={connectorAnchor(name)} style={{ display: "grid", gap: 8 }}>
      {children}
      {connected ? (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="button" className="btn" onClick={onToggle}>
            Done
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* ---------- TEAMS (inner outline) ---------- */
// The server only accepts GUIDs for Teams connect flows (the tenant id is
// matched against the Microsoft token), so catch domains/typos before a popup.
const MS_GUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Every connector's Disconnect asks the same way.
const confirmDisconnect = (title, body) =>
  dialog.confirm({ title, body, confirmLabel: "Disconnect", danger: true });

const MS_GUID_HINT =
  "Use the Directory (tenant) ID and Team (group) ID GUIDs from the Microsoft Entra / Teams admin center.";

const TeamsConnector = React.memo(function TeamsConnector({
  apiBase,
  teamsConnected,
  setTeamsConnected,
  teamsPrefs,
  setTeamsPrefs,
  pullTeams,
}) {
  const [loading, setLoading] = React.useState(false);
  const [channelList, setChannelList] = React.useState([]);
  const [filterQ, setFilterQ] = React.useState("");
  const [pulling, setPulling] = React.useState(false);
  const tenantId = teamsPrefs?.tenantId || "";
  const teamId = teamsPrefs?.teamId || "";
  const [chatIdInput, setChatIdInput] = React.useState("");
  const [adminConsented, setAdminConsented] = React.useState(false);
  // A problem with the Tenant or Team ID, shown under those fields.
  const [fieldMsg, setFieldMsg] = React.useState("");
  const [planList, setPlanList] = React.useState([]);
  const [plansLoading, setPlansLoading] = React.useState(false);
  const [plansErr, setPlansErr] = React.useState(""); // --- NEW: per-chat install status ---
  const [chatInstalled, setChatInstalled] = React.useState({}); // { [chatId]: true/false }
  const [chatChecking, setChatChecking] = React.useState({}); // { [chatId]: true/false }
  const [chatMeta, setChatMeta] = React.useState({});
  const [chatMetaLoading, setChatMetaLoading] = React.useState({});

  async function refreshChatInstalled(chatId) {
    const id = String(chatId || "").trim();
    if (!tenantId || !id) return false;

    setChatChecking((s) => ({ ...s, [id]: true }));
    try {
      const j = await fetch(
        `${apiBase}/api/teams/chat-installed?tenant=${encodeURIComponent(
          tenantId,
        )}&chat=${encodeURIComponent(id)}`,
        { credentials: "include", cache: "no-store" },
      ).then((r) => (r.ok ? r.json() : { installed: false }));

      const ok = !!j.installed;
      setChatInstalled((s) => ({ ...s, [id]: ok }));
      return ok;
    } catch {
      setChatInstalled((s) => ({ ...s, [id]: false }));
      return false;
    } finally {
      setChatChecking((s) => ({ ...s, [id]: false }));
    }
  }

  async function refreshChatMeta(chatId) {
    const id = String(chatId || "").trim();
    if (!tenantId || !id) return null;

    setChatMetaLoading((s) => ({ ...s, [id]: true }));
    try {
        const j = await fetch(
          `${apiBase}/api/teams/chat-meta?tenant=${encodeURIComponent(
            tenantId,
          )}&chat=${encodeURIComponent(id)}`,
        { credentials: "include", cache: "no-store" },
      ).then((r) =>
        r.ok
          ? r.json()
          : { ok: false, installed: false, chat: { id, members: [] } },
      );

        setChatInstalled((s) => ({ ...s, [id]: !!j.installed }));
        setChatMeta((s) => ({
          ...s,
          [id]: {
            ...(j?.chat || { id, topic: "", chatType: "", members: [] }),
            detail: j?.detail || j?.chat?.memberError || "",
          },
        }));
        return j?.chat || null;
      } catch {
        setChatMeta((s) => ({
          ...s,
          [id]: { id, topic: "", chatType: "", members: [], detail: "" },
        }));
        return null;
      } finally {
      setChatMetaLoading((s) => ({ ...s, [id]: false }));
    }
  }

  async function refreshChatInfo(chatId) {
    await refreshChatInstalled(chatId);
    await refreshChatMeta(chatId);
  }

  async function refreshAllChatInstalled() {
    const ids = teamsPrefs.includeChats || [];
    if (!tenantId || !ids.length) return;
    await Promise.all(ids.map((id) => refreshChatInfo(id)));
  }

  async function refreshPlannerPlans() {
    if (!tenantId || !teamId) {
      setPlanList([]);
      return;
    }
    setPlansLoading(true);
    setPlansErr("");
    try {
      // IMPORTANT: make this match whatever endpoint you actually implemented.
      // I’m assuming you’ll implement something like:
      // GET /api/teams/planner/plans?tenant=...&team=...
      const j = await fetch(
        `${apiBase}/api/teams/planner/plans?tenant=${encodeURIComponent(
          tenantId,
        )}&team=${encodeURIComponent(teamId)}`,
        { credentials: "include", cache: "no-store" },
      ).then((r) => (r.ok ? r.json() : Promise.reject(r)));

      // support either {plans:[...]} or [...]:
      const arr = Array.isArray(j) ? j : j?.plans || [];
      setPlanList(arr);
    } catch (e) {
      setPlanList([]);
      setPlansErr("Failed to load Planner plans (check Graph permissions).");
    } finally {
      setPlansLoading(false);
    }
  }
  React.useEffect(() => {
    if (!teamsConnected) return;
    if (!tenantId || !teamId) return;
    refreshPlannerPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsConnected, tenantId, teamId]);
  const planOptions = React.useMemo(
    () =>
      (planList || []).map((p) => ({
        value: p.id,
        label: p.title || p.name || p.id,
      })),
    [planList],
  );
  React.useEffect(() => {
    if (!tenantId) {
      setChatInstalled({});
      setChatChecking({});
      setChatMeta({});
      setChatMetaLoading({});
      return;
    }
    refreshAllChatInstalled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, JSON.stringify(teamsPrefs.includeChats || [])]);

  function parseChatIdFromLink(s) {
    try {
      const str = (s || "").trim();
      if (!str) return "";

      // /l/chat/<CHATID>/...
      let m = str.match(/\/l\/chat\/([^/?#]+)(?:\/|$)/i);
      if (m) return decodeURIComponent(m[1]);

      // ?chatId=<CHATID>
      m = str.match(/[?&]chatId=([^&]+)/i);
      if (m) return decodeURIComponent(m[1]);

      // /chats/<CHATID>
      m = str.match(/\/chats\/([^/?#]+)/i);
      if (m) return decodeURIComponent(m[1]);

      // If they pasted the raw chatId already
      return str;
    } catch {
      return (s || "").trim();
    }
  }

  function addChatFromInput() {
    const id = parseChatIdFromLink(chatIdInput);
    if (!id) return;

    const inc = new Set(teamsPrefs.includeChats || []);
    inc.add(id);

    setTeamsPrefs({ ...teamsPrefs, includeChats: [...inc] });
    setChatInstalled((s) => ({ ...s, [id]: s[id] ?? false })); // initialize if missing
    setChatIdInput("");

    // kick off a status check (non-blocking)
    setTimeout(() => refreshChatInfo(id), 0);
    return id;
  }

  function removeChat(id) {
    const next = (teamsPrefs.includeChats || []).filter((x) => x !== id);
    setTeamsPrefs({ ...teamsPrefs, includeChats: next });

    setChatInstalled((s) => {
      const n = { ...s };
      delete n[id];
      return n;
    });
    setChatMeta((s) => {
      const n = { ...s };
      delete n[id];
      return n;
    });
    setChatChecking((s) => {
      const n = { ...s };
      delete n[id];
      return n;
    });
    setChatMetaLoading((s) => {
      const n = { ...s };
      delete n[id];
      return n;
    });
  }

  function installToThisChat(chatId, options = {}) {
    // A notification, because these buttons sit well below the Tenant ID field.
    if (!tenantId) return toast.warn("Enter Tenant ID first.");
    if (!chatId) return;
    if (!MS_GUID_RE.test(tenantId)) return toast.warn(MS_GUID_HINT);
    // The server requires a connected team in this tenant before chat installs.
    if (!teamsConnected) return toast.warn("Connect Microsoft Teams first.");

    if (chatInstalled[chatId]) {
      if (!options.quietIfInstalled) toast.info("Already installed in that chat.");
      setTimeout(() => refreshChatInfo(chatId), 0);
      return;
    }

    const url =
      `${apiBase}/api/teams/chat-install/start?tenant=${encodeURIComponent(tenantId)}` +
      `&chat=${encodeURIComponent(chatId)}`;

    const pop = window.open(url, "_blank", "width=900,height=800");

    watchSignInPopup(pop, {
      intervalMs: 1200,
      check: async () => {
        const s = await fetch(
          `${apiBase}/api/teams/chat-installed?tenant=${encodeURIComponent(tenantId)}&chat=${encodeURIComponent(chatId)}`,
          { credentials: "include", cache: "no-store" },
        ).then((r) => r.json());
        return !!s.installed;
      },
      onDone: (landed) => {
        if (!landed) return;
        setChatInstalled((m) => ({ ...m, [chatId]: true })); // <-- NEW
        setTimeout(() => refreshChatInfo(chatId), 0);
        toast.success("BossDrop installed into that chat.");
      },
    });
  }

  function addAndInstallChatFromInput() {
    const id = addChatFromInput();
    if (!id) return;
    installToThisChat(id, { quietIfInstalled: true });
  }

  async function refreshAdminConsent() {
    if (!tenantId) {
      setAdminConsented(false);
      return false;
    }
    try {
      const j = await fetch(
        `${apiBase}/api/teams/admin-consent/status?tenant=${encodeURIComponent(
          tenantId,
        )}`,
        { credentials: "include", cache: "no-store" },
      ).then((r) => r.json());
      const ok = !!j.consented;
      setAdminConsented(ok);
      return ok;
    } catch {
      setAdminConsented(false);
      return false;
    }
  }
  async function startAdminConsent() {
    if (!tenantId) return setFieldMsg("Enter Tenant ID first.");
    if (!MS_GUID_RE.test(tenantId)) return setFieldMsg(MS_GUID_HINT);
    const { url } = await fetch(
      `${apiBase}/api/teams/admin-consent/start?tenant=${encodeURIComponent(
        tenantId,
      )}`,
      { credentials: "include" },
    ).then((r) => r.json());

    const pop = window.open(url, "_blank", "width=830,height=650");
    watchSignInPopup(pop, {
      intervalMs: 1200,
      check: refreshAdminConsent,
      onDone: () => refreshAdminConsent(), // <-- crucial final check
    });
  }
  React.useEffect(() => {
    if (tenantId) refreshAdminConsent();
    else setAdminConsented(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);
  React.useEffect(() => {
    const onFocus = () => tenantId && refreshAdminConsent();
    const onVis = () => {
      if (document.visibilityState === "visible" && tenantId)
        refreshAdminConsent();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [tenantId]);

  async function refreshTeamsStatus() {
    if (!tenantId || !teamId) return false;
    setLoading(true);
    try {
      const s = await fetch(
        `${apiBase}/api/teams/connected?tenant=${encodeURIComponent(
          tenantId,
        )}&team=${encodeURIComponent(teamId)}`,
        { credentials: "include", cache: "no-store" },
      ).then((r) => r.json());

      const ok = !!s.connected;
      setTeamsConnected?.(ok);
      if (ok) {
        try {
          const c = await fetch(
            `${apiBase}/api/teams/channels?tenant=${encodeURIComponent(
              tenantId,
            )}&team=${encodeURIComponent(teamId)}`,
            { credentials: "include" },
          ).then((r) => r.json());
          setChannelList(c.channels || []);
        } catch {
          // transient 403 during RSC propagation — show installed state, retry on next poll
        }
      } else {
        setChannelList([]);
      }
      return ok;
    } catch {
      setTeamsConnected?.(false);
      return false;
    } finally {
      setLoading(false);
    }
  }

  async function openTeamsInstall() {
    if (!tenantId || !teamId) {
      setFieldMsg("Enter Tenant ID and Team (Group) ID first.");
      return;
    }
    if (!MS_GUID_RE.test(tenantId) || !MS_GUID_RE.test(teamId)) {
      setFieldMsg(MS_GUID_HINT);
      return;
    }
    const url = `${apiBase}/api/teams/install/start?tenant=${encodeURIComponent(
      tenantId,
    )}&team=${encodeURIComponent(teamId)}`;

    const pop = window.open(url, "_blank", "width=900,height=800");
    watchSignInPopup(pop, {
      intervalMs: 1200,
      check: refreshTeamsStatus,
      onDone: (landed) => {
        if (landed) toast.success("BossDrop installed and permissions granted.");
      },
    });
  }

  async function teamsDisconnect() {
    if (!tenantId || !teamId) return setFieldMsg("Enter Tenant ID and Team (Group) ID first.");
    if (!(await confirmDisconnect("Disconnect Teams from this workspace?"))) return;

    setLoading(true);
    try {
      const r = await fetch(
        `${apiBase}/api/teams/disconnect?tenant=${encodeURIComponent(
          tenantId,
        )}&team=${encodeURIComponent(teamId)}`,
        { method: "DELETE", credentials: "include" },
      );

      const text = await r.text();
      let payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {}

      if (!r.ok) {
        console.error("Teams disconnect failed:", r.status, payload || text);
        toast.error(
          r.status === 403
            ? "Only a workspace admin can disconnect Microsoft Teams, and only for a team connected here."
            : "Couldn't disconnect Microsoft Teams. Try again.",
        );
        return;
      }

      // optimistic UI reset
      setTeamsConnected?.(false);
      setChannelList([]);
      setAdminConsented(false); // <-- add this

      setTeamsPrefs({
        ...teamsPrefs,
        includeChannels: [],
        excludeChannels: [],
        includeChats: [],
        tenantId: "",
        teamId: "",
        planId: "",
      });

      await refreshTeamsStatus();
    } catch (e) {
      console.error(e);
      toast.error("Couldn't disconnect Microsoft Teams. Try again.");
    } finally {
      setLoading(false);
    }
  }

  async function pullTeamsNow(append = false) {
    setPulling(true);
    try {
      await pullTeams?.({
        append,
        includeChannels: teamsPrefs.includeChannels || [],
        excludeChannels: teamsPrefs.excludeChannels || [],
        includeChats: teamsPrefs.includeChats || [],
        days: teamsPrefs.days ?? 14,
      });
    } finally {
      setPulling(false);
    }
  }

  const includeSet = new Set(teamsPrefs.includeChannels || []);
  const excludeSet = new Set(teamsPrefs.excludeChannels || []);
  const q = filterQ.trim().toLowerCase();
  const visible = (channelList || []).filter((c) =>
    q ? c.displayName.toLowerCase().includes(q) : true,
  );
  const includeCandidates = visible.filter((c) => !excludeSet.has(c.id));
  const excludeCandidates = visible.filter((c) => !includeSet.has(c.id));
  const toggleInclude = (id) => {
    const inc = new Set(teamsPrefs.includeChannels || []);
    const exc = new Set(teamsPrefs.excludeChannels || []);
    if (inc.has(id)) {
      inc.delete(id);
    } else {
      inc.add(id);
      exc.delete(id);
    }
    setTeamsPrefs({
      ...teamsPrefs,
      includeChannels: [...inc],
      excludeChannels: [...exc],
    });
  };
  const toggleExclude = (id) => {
    const inc = new Set(teamsPrefs.includeChannels || []);
    const exc = new Set(teamsPrefs.excludeChannels || []);
    if (exc.has(id)) {
      exc.delete(id);
    } else {
      exc.add(id);
      inc.delete(id);
    }
    setTeamsPrefs({
      ...teamsPrefs,
      includeChannels: [...inc],
      excludeChannels: [...exc],
    });
  };

  React.useEffect(() => {
    if (tenantId && teamId) refreshTeamsStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, teamId]);

  React.useEffect(() => setFieldMsg(""), [tenantId, teamId]);

  return (
    <div className="panelSub">
      <h3 className="crCardHead">
        <span className="crCardHead__title">
          <img src={teamsLogo} alt="" className="toolLogo" />
          Microsoft Teams
        </span>
        {teamsConnected ? (
          <span
            className="chip ok"
            style={{ marginLeft: "auto" }}
            title="Connection status"
          >
            <span className="chipDot" />
            Connected
          </span>
        ) : null}
      </h3>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          // Bottom, not centre: each label here is text stacked above its input,
          // so centring put every button half a label-height above the fields it
          // sits next to -- which is what the .movedownabit nudge on Disconnect
          // was there to undo.
          alignItems: "end",
        }}
      >
        <label style={{ display: "grid", gap: 6 }}>
          Tenant ID
          <input
            placeholder="e.g., 72f988bf-86f1-41af-91ab-2d7cd011db47"
            value={tenantId}
            onChange={(e) =>
              setTeamsPrefs({
                ...teamsPrefs,
                tenantId: e.target.value.trim(),
              })
            }
            style={{
              fontSize: 16,
              minHeight: 44,
              width: "36ch",
              maxWidth: "100%",
              fontFamily: "ui-monospace, Menlo, Consolas, monospace",
            }}
          />
        </label>
        <label style={{ display: "grid", gap: 6 }}>
          Team (Group) ID
          <input
            placeholder="GUID for the Team (M365 Group)"
            value={teamId}
            onChange={(e) =>
              setTeamsPrefs({ ...teamsPrefs, teamId: e.target.value.trim() })
            }
            style={{
              fontSize: 16,
              minHeight: 44,
              width: "36ch",
              maxWidth: "100%",
              fontFamily: "ui-monospace, Menlo, Consolas, monospace",
            }}
          />
        </label>

        {!teamsConnected ? (
          <>
            <span className="muted">Not connected</span>
            {!adminConsented ? (
              <button className="btn" type="button" onClick={startAdminConsent}>
                Grant admin consent
              </button>
            ) : (
              <span className="muted">Admin consent on file</span>
            )}
            <button
              className="btn primary"
              type="button"
              onClick={openTeamsInstall}
            >
              Connect Microsoft Teams
            </button>
          </>
        ) : (
          <button
            className="btn btnDanger"
            type="button"
            onClick={teamsDisconnect}
          >
            Disconnect
          </button>
        )}

      </div>

      {fieldMsg ? (
        <div className="connectorFieldMsg" role="alert">
          {fieldMsg}
        </div>
      ) : null}

      {/* Options */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginTop: 10,
          alignItems: "end",
        }}
      >
        <label>
          History window (days)
          <input
            type="number"
            min={1}
            max={3650}
            value={teamsPrefs.days ?? 14}
            onChange={(e) =>
              setTeamsPrefs({
                ...teamsPrefs,
                days: Math.max(1, Math.min(3650, Number(e.target.value) || 14)),
              })
            }
            style={{ fontSize: 16, minHeight: 44, width: 120 }}
          />
        </label>

        <button
          className="btn primary"
          type="button"
          disabled={!teamsConnected}
          onClick={() => pullTeamsNow(false)}
          title="Fetch latest channel messages into BossDrop"
        >
          {pulling ? "Pulling…" : "Pull latest now"}
        </button>
      </div>
      {/* Planner plan */}
      <div
        style={{
          display: "flex",
          gap: 10,
          marginTop: 10,
          alignItems: "end",
          flexWrap: "wrap",
        }}
      >
        <label style={{ display: "grid", gap: 6, minWidth: 320, flex: 1 }}>
          Planner plan (where BossDrop creates tasks)
          <FancySelect
            value={teamsPrefs?.planId || ""}
            onChange={(v) =>
              setTeamsPrefs({
                ...teamsPrefs,
                planId: v,
              })
            }
            options={planOptions}
            placeholder={
              !teamsConnected
                ? "Connect Teams first…"
                : !tenantId || !teamId
                  ? "Enter Tenant + Team ID first…"
                  : plansLoading
                    ? "Loading plans…"
                    : planOptions.length
                      ? "Select a plan…"
                      : "No plans found…"
            }
            ariaLabel="Select Planner plan"
            disabled={
              !teamsConnected ||
              !tenantId ||
              !teamId ||
              plansLoading ||
              !planOptions.length
            }
            emptyText="No plans"
          />
        </label>

        <button
          className="btn"
          type="button"
          onClick={refreshPlannerPlans}
          disabled={!teamsConnected || !tenantId || !teamId || plansLoading}
          title="Refresh Planner plans"
          style={{ minHeight: 44 }}
        >
          {plansLoading ? "Refreshing…" : "Refresh plans"}
        </button>

        {plansErr ? (
          <div className="muted" style={{ fontSize: 12, alignSelf: "center" }}>
            {plansErr}
          </div>
        ) : null}
      </div>

      <div className="muted small" style={{ marginTop: 6 }}>
        If you don’t see your plan: open Planner inside that Team, create a
        plan, then hit Refresh.
      </div>

      {/* Filter + dual lists */}
      <div style={{ marginTop: 10 }}>
        <input
          className="inputFancy"
          placeholder="Filter channels…"
          value={filterQ}
          onChange={(e) => setFilterQ(e.target.value)}
          style={{ fontSize: 16, minHeight: 44 }}
        />
      </div>

      <div className="split" style={{ marginTop: 10 }}>
        <div>
          <h4 className="crSubTitle">
            Include Channels{" "}
            <span className="muted" style={{ fontSize: 12 }}>
              ({includeSet.size})
            </span>
          </h4>
          <div className="list" style={{ maxHeight: 260, overflow: "auto" }}>
            {visible
              .filter((c) => !excludeSet.has(c.id))
              .map((c) => {
                const checked = includeSet.has(c.id);
                return (
                  <label
                    key={c.id}
                    className="listItem"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      className="bd-check"
                      onChange={() => toggleInclude(c.id)}
                      style={{ width: 18, height: 18 }}
                    />
                    <span>#{c.displayName}</span>
                  </label>
                );
              })}
            {!visible.filter((c) => !excludeSet.has(c.id)).length ? (
              <div className="muted" style={{ padding: 8 }}>
                No channels (filtered or excluded).
              </div>
            ) : null}
          </div>
        </div>

        <div>
          <h4 className="crSubTitle">
            Exclude Channels{" "}
            <span className="muted" style={{ fontSize: 12 }}>
              ({excludeSet.size})
            </span>
          </h4>
          <div className="list" style={{ maxHeight: 260, overflow: "auto" }}>
            {visible
              .filter((c) => !includeSet.has(c.id))
              .map((c) => {
                const checked = excludeSet.has(c.id);
                return (
                  <label
                    key={c.id}
                    className="listItem toolOption"
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      className="bd-check"
                      onChange={() => toggleExclude(c.id)}
                      style={{ width: 18, height: 18 }}
                    />
                    <span>#{c.displayName}</span>
                  </label>
                );
              })}
            {!visible.filter((c) => !includeSet.has(c.id)).length ? (
              <div className="muted" style={{ padding: 8 }}>
                No channels (filtered or included).
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Chats (optional) — a labelled group, not a third nested card: a
          third .panelSub renders identically to its own parent, so the
          nesting reads as nothing. */}
      <div className="crSubGroup">
        <h4 className="crSubTitle">Chats (optional)</h4>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="inputFancy"
            placeholder='Paste "Get link to chat" or chatId'
            value={chatIdInput}
            onChange={(e) => setChatIdInput(e.target.value)}
            style={{ minHeight: 44, flex: 1, fontSize: 16 }}
          />
          <button
            className="btn"
            type="button"
            disabled={!chatIdInput || !teamsConnected}
            onClick={addAndInstallChatFromInput}
            title={
              teamsConnected
                ? "Adds this chat to BossDrop and opens Teams to install the app there"
                : "Connect Microsoft Teams first"
            }
          >
            Add & install
          </button>
        </div>

        <div
          className="list"
          style={{ marginTop: 8, maxHeight: 220, overflow: "auto" }}
        >
          {(teamsPrefs.includeChats || []).map((id) => {
            const installed = !!chatInstalled[id];
            const checking = !!chatChecking[id];
            const meta = chatMeta[id] || {};
              const memberLine = (meta?.members || [])
                .map((member) => member?.name || member?.email || "")
                .filter(Boolean)
                .slice(0, 6)
                .join(", ");
              const memberStatus = chatMetaLoading[id]
                ? "Loading chat members..."
                : memberLine
                  ? memberLine
                  : meta?.detail
                    ? `Members not available yet (${String(meta.detail).slice(0, 140)})`
                    : "No member names available yet";

            return (
              <div
                key={id}
                className="listItem"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      alignItems: "center",
                    }}
                  >
                    <code style={{ fontFamily: "ui-monospace" }}>{id}</code>
                    {meta?.topic ? (
                      <span className="muted" style={{ fontSize: 12 }}>
                        {meta.topic}
                      </span>
                    ) : null}
                  </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {memberStatus}
                    </div>
                  </div>

                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    gap: 6,
                    alignItems: "center",
                  }}
                >
                  {checking ? (
                    <span className="muted" style={{ fontSize: 12 }}>
                      Checking…
                    </span>
                  ) : installed ? (
                    <span
                      className="chip ok"
                      title="Chat install status"
                    >
                      <span className="chipDot" />
                      Connected
                    </span>
                  ) : (
                    <span className="muted" style={{ fontSize: 12 }}>
                      Not connected
                    </span>
                  )}

                  {!installed && (
                    <button
                      className="btn"
                      onClick={() => installToThisChat(id)}
                    >
                      Install
                    </button>
                  )}

                  <button
                    className="btn"
                    type="button"
                    onClick={() => refreshChatInfo(id)}
                    title="Re-check install status"
                  >
                    Refresh
                  </button>

                  <button
                    className="btn btnDanger"
                    onClick={() => removeChat(id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

// Server error codes from /api/hubspot/* -> what the HubSpot card says.
const HUBSPOT_SYNC_ERRORS = {
  hubspot_not_configured: "HubSpot isn't enabled on this BossDrop server yet.",
  hubspot_not_connected: "Connect HubSpot first.",
  hubspot_reconnect_required: "HubSpot sign-in expired. Reconnect HubSpot.",
  hubspot_rate_limited:
    "HubSpot is limiting requests right now. Try again in a minute.",
};

// Server error codes from /api/pipedrive/* -> what the Pipedrive card says.
const PIPEDRIVE_SYNC_ERRORS = {
  pipedrive_not_configured: "Pipedrive isn't enabled on this BossDrop server yet.",
  pipedrive_not_connected: "Connect Pipedrive first.",
  pipedrive_reconnect_required: "Pipedrive sign-in expired. Reconnect Pipedrive.",
  pipedrive_rate_limited:
    "Pipedrive is limiting requests right now. Try again in a minute.",
  pipedrive_missing_scope:
    "BossDrop's Pipedrive app is missing a permission. Disconnect and connect again to grant it.",
};

// What changes on /api/pipedrive/connected when a connect or a reconnect lands.
function pipedriveStatusKey(status) {
  return JSON.stringify([
    !!status?.connected,
    !!status?.needsReconnect,
    status?.companyDomain || "",
    status?.scope || "",
  ]);
}

// What changes on /api/hubspot/connected when a connect or a reconnect lands.
function hubspotStatusKey(status) {
  return JSON.stringify([
    !!status?.connected,
    !!status?.needsReconnect,
    status?.hubId || "",
  ]);
}

// Server error codes from /api/asana/* -> what the Asana card says.
const ASANA_MESSAGES = {
  asana_not_configured: "Asana isn't enabled on this BossDrop server yet.",
  asana_not_connected: "Connect Asana first.",
  asana_reconnect_required: "Asana sign-in expired. Reconnect Asana.",
  asana_rate_limited: "Asana is limiting requests right now. Try again in a minute.",
  workspace_required: "Pick a workspace first.",
};

// What changes on /api/asana/connected when a connect or a reconnect lands.
function asanaStatusKey(status) {
  return JSON.stringify([
    !!status?.connected,
    !!status?.needsReconnect,
    status?.me?.gid || "",
  ]);
}

const HubSpotConnector = React.memo(function HubSpotConnector({
  apiBase,
  hubspotConnected,
  setHubspotConnected,
  hubspotPrefs,
  setHubspotPrefs,
}) {
  const [loading, setLoading] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [localConnected, setLocalConnected] = React.useState(!!hubspotConnected);
  const [meta, setMeta] = React.useState(null);
  const [pipelines, setPipelines] = React.useState([]);
  const [pipelinesMsg, setPipelinesMsg] = React.useState("");
  const [pipelineId, setPipelineId] = React.useState(
    hubspotPrefs?.pipelineId || "",
  );
  const [lookbackDays, setLookbackDays] = React.useState(
    Math.max(1, Number(hubspotPrefs?.lookbackDays) || 90),
  );
  const [saveMsg, setSaveMsg] = React.useState("");
  const [syncMsg, setSyncMsg] = React.useState("");
  const hydrated = React.useRef(false);
  const setHubspotPrefsRef = React.useRef(setHubspotPrefs);

  React.useEffect(() => {
    setHubspotPrefsRef.current = setHubspotPrefs;
  }, [setHubspotPrefs]);

  const pipelineOptions = React.useMemo(
    () => [
      { value: "", label: "All deal pipelines" },
      ...(pipelines || []).map((pipeline) => ({
        value: pipeline.id,
        label: pipeline.label || pipeline.id,
      })),
    ],
    [pipelines],
  );
  const selectedPipeline = React.useMemo(
    () =>
      (pipelines || []).find((pipeline) => pipeline.id === pipelineId) || null,
    [pipelines, pipelineId],
  );

  // `fresh` skips the server's short cache. A fresh read that fails leaves the list alone.
  async function loadHubSpotPipelines({ fresh = false } = {}) {
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/hubspot/pipelines${fresh ? "?fresh=1" : ""}`,
    );
    if (!ok) {
      if (fresh) return null;
      setPipelines([]);
      setPipelinesMsg(
        HUBSPOT_SYNC_ERRORS[json?.error] || "Couldn't load HubSpot pipelines. Try again.",
      );
      return [];
    }
    const list = Array.isArray(json?.pipelines) ? json.pipelines : [];
    setPipelinesMsg("");
    setPipelines(list);
    return list;
  }

  useRefreshOnReturn(() => loadHubSpotPipelines({ fresh: true }), localConnected);

  // { configured, connected, hubId, hubDomain, needsReconnect, prefs }, read
  // from the WORKSPACE's grant (any admin's connect serves everyone).
  async function fetchHubSpotStatus() {
    const r = await fetch(`${apiBase}/api/hubspot/connected`, {
      credentials: "include",
      cache: "no-store",
    });
    return r.ok ? r.json() : { connected: false };
  }

  async function refreshHubSpotStatus() {
    try {
      const status = await fetchHubSpotStatus();
      const ok = !!status?.connected;
      setLocalConnected(ok);
      setHubspotConnected?.(ok);
      setMeta(status || null);

      if (ok) {
        const nextPrefs =
          hubspotPrefs && typeof hubspotPrefs === "object"
            ? hubspotPrefs
            : status?.prefs && typeof status.prefs === "object"
              ? status.prefs
              : {};
        const nextPipelineId = String(nextPrefs?.pipelineId || "").trim();
        const nextLookbackDays = Math.max(
          1,
          Number(nextPrefs?.lookbackDays) || 90,
        );
        setPipelineId(nextPipelineId);
        setLookbackDays(nextLookbackDays);
        if ((!hubspotPrefs || typeof hubspotPrefs !== "object") && status?.prefs) {
          setHubspotPrefs?.(status.prefs);
        }
        await loadHubSpotPipelines();
      } else {
        setPipelines([]);
      }
      return ok;
    } catch {
      setLocalConnected(false);
      setHubspotConnected?.(false);
      setMeta(null);
      setPipelines([]);
      return false;
    } finally {
      hydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshHubSpotStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Waits for the status to CHANGE (connect, or a reconnect clearing the
  // flag), so a reconnect does not close the popup before it is done.
  function openHubSpotOAuth() {
    setSyncMsg("");
    const popup = window.open(
      `${apiBase}/api/hubspot/oauth/start?returnTo=/settings`,
      "_blank",
      "width=640,height=760",
    );
    watchOAuthPopup(popup, {
      fetchStatus: fetchHubSpotStatus,
      before: hubspotStatusKey(meta),
      statusKey: hubspotStatusKey,
      onDone: refreshHubSpotStatus,
    });
  }

  async function disconnectHubSpotAccount() {
    if (!(await confirmDisconnect("Disconnect HubSpot for this workspace?"))) return;
    setDisconnecting(true);
    setSyncMsg("");
    try {
      const r = await fetch(`${apiBase}/api/hubspot/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("hubspot_disconnect_failed");
      setLocalConnected(false);
      setHubspotConnected?.(false);
      setMeta(null);
      setPipelines([]);
      setPipelineId("");
      setLookbackDays(90);
      setHubspotPrefs?.(null);
    } catch {
      setSyncMsg("Couldn't disconnect HubSpot. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }

  async function syncHubSpotNow() {
    setLoading(true);
    setSyncMsg("");
    loadHubSpotPipelines({ fresh: true });
    try {
      const url = new URL(`${apiBase}/api/hubspot/deals`);
      if (pipelineId) url.searchParams.set("pipeline", pipelineId);
      url.searchParams.set("lookbackDays", String(lookbackDays));
      url.searchParams.set("limit", "120");
      const r = await fetch(url, {
        credentials: "include",
        cache: "no-store",
      });
      const json = await r.json().catch(() => null);
      if (!r.ok) throw new Error(String(json?.error || "hubspot_deals_failed"));
      const count = Array.isArray(json?.deals) ? json.deals.length : 0;
      setSyncMsg(
        count
          ? `${count} HubSpot ${count === 1 ? "deal" : "deals"} loaded for AI context.`
          : "No open HubSpot deals, and none closed in the selected window.",
      );
    } catch (e) {
      setSyncMsg(HUBSPOT_SYNC_ERRORS[e?.message] || "HubSpot sync failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (!localConnected || !hydrated.current) return;
    const nextPrefs = {
      pipelineId: pipelineId || null,
      pipelineLabel: selectedPipeline?.label || null,
      lookbackDays: Math.max(1, Number(lookbackDays) || 90),
    };
    const currentPrefs = {
      pipelineId:
        hubspotPrefs && typeof hubspotPrefs === "object"
          ? hubspotPrefs.pipelineId || null
          : null,
      pipelineLabel:
        hubspotPrefs && typeof hubspotPrefs === "object"
          ? hubspotPrefs.pipelineLabel || null
          : null,
      lookbackDays:
        hubspotPrefs && typeof hubspotPrefs === "object"
          ? Math.max(1, Number(hubspotPrefs.lookbackDays) || 90)
          : 90,
    };
    if (JSON.stringify(nextPrefs) === JSON.stringify(currentPrefs)) {
      setSaveMsg((prev) => (prev === "Saving..." ? "Saved." : prev));
      return;
    }
    setSaveMsg("Saving...");
    setHubspotPrefsRef.current?.(nextPrefs);
    const timer = setTimeout(() => setSaveMsg("Saved."), 300);
    return () => clearTimeout(timer);
  }, [
    localConnected,
    pipelineId,
    selectedPipeline?.label,
    lookbackDays,
    hubspotPrefs?.pipelineId,
    hubspotPrefs?.pipelineLabel,
    hubspotPrefs?.lookbackDays,
  ]);

  return (
    <div className="panelSub">
      <h3 className="crCardHead">
        <span className="crCardHead__title">
          <ToolGlyph src={hubspotLogo} label="HubSpot" />
          HubSpot
        </span>
        {localConnected ? (
          <span
            className="chip ok"
            style={{ marginLeft: "auto" }}
            title="Connection status"
          >
            <span className="chipDot" />
            Connected
          </span>
        ) : null}
      </h3>

      {!localConnected ? (
        <>
          <div
            className="muted"
            style={{ display: "flex", alignItems: "center", gap: 10 }}
          >
            <span>Not connected</span>
            <button
              className="btn primary"
              onClick={openHubSpotOAuth}
              disabled={meta?.configured === false}
            >
              Connect HubSpot
            </button>
          </div>
          {meta?.configured === false ? (
            <div className="muted small" style={{ marginTop: 8 }}>
              {HUBSPOT_SYNC_ERRORS.hubspot_not_configured}
            </div>
          ) : null}
          {syncMsg ? (
            <div className="muted small" style={{ marginTop: 8 }} aria-live="polite">
              {syncMsg}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="formRow" style={{ alignItems: "center" }}>
            <div
              className="muted"
              style={{
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {meta?.hubId ? `Connected to portal ${meta.hubId}` : "Connected"}
              {meta?.hubDomain ? ` (${meta.hubDomain})` : ""}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                className="btn"
                onClick={syncHubSpotNow}
                disabled={loading || disconnecting}
              >
                {loading ? "Syncing..." : "Sync now"}
              </button>
              <button
                className="btn btnDanger"
                onClick={disconnectHubSpotAccount}
                disabled={loading || disconnecting}
                title="Disconnect HubSpot from BossDrop"
              >
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>

          {meta?.needsReconnect ? (
            <ReconnectNote label="HubSpot" onReconnect={openHubSpotOAuth} />
          ) : null}

          <div className="formRow" style={{ marginTop: 10 }}>
            <label style={{ display: "grid", gap: 6 }}>
              Sales pipeline
              <FancySelect
                value={pipelineId}
                onChange={(value) => setPipelineId(value)}
                options={pipelineOptions}
                placeholder="Select a HubSpot pipeline"
                ariaLabel="Select HubSpot pipeline"
                disabled={!localConnected}
                emptyText="No HubSpot pipelines found"
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              Closed deals window (days)
              <input
                type="number"
                min={1}
                max={3650}
                value={lookbackDays}
                onChange={(e) =>
                  setLookbackDays(
                    Math.max(1, Math.min(3650, Number(e.target.value) || 90)),
                  )
                }
                style={{ fontSize: 16, minHeight: 44, width: 140 }}
              />
            </label>

            <div style={{ alignSelf: "end" }}>
              {saveMsg ? (
                <span
                  className="muted"
                  aria-live="polite"
                  style={{ whiteSpace: "nowrap" }}
                >
                  {saveMsg}
                </span>
              ) : null}
            </div>
          </div>

          {pipelinesMsg ? (
            <div className="muted small" style={{ marginTop: 8 }} aria-live="polite">
              {pipelinesMsg}
            </div>
          ) : null}

          {syncMsg ? (
            <div className="muted" style={{ marginTop: 8 }}>
              {syncMsg}
            </div>
          ) : null}

          <p className="muted" style={{ marginTop: 8 }}>
            BossDrop reads every open deal in the selected sales pipeline (stage,
            owner, amount, close date, next step, last activity) plus deals
            closed in the history window, so the AI can spot past-due and
            stalled deals, missing follow-ups, and missing ownership. Read only:
            BossDrop never changes HubSpot.
          </p>
        </>
      )}
    </div>
  );
});

// The HubSpot card's shape: the workspace's one grant, a pipeline and a history
// window. Read only.
const PipedriveConnector = React.memo(function PipedriveConnector({
  apiBase,
  pipedriveConnected,
  setPipedriveConnected,
  pipedrivePrefs,
  setPipedrivePrefs,
}) {
  const [loading, setLoading] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [localConnected, setLocalConnected] = React.useState(!!pipedriveConnected);
  const [meta, setMeta] = React.useState(null);
  const [pipelines, setPipelines] = React.useState([]);
  const [pipelinesMsg, setPipelinesMsg] = React.useState("");
  const [pipelineId, setPipelineId] = React.useState(pipedrivePrefs?.pipelineId || "");
  const [lookbackDays, setLookbackDays] = React.useState(
    Math.max(1, Number(pipedrivePrefs?.lookbackDays) || 90),
  );
  const [saveMsg, setSaveMsg] = React.useState("");
  const [syncMsg, setSyncMsg] = React.useState("");
  const hydrated = React.useRef(false);
  const setPrefsRef = React.useRef(setPipedrivePrefs);

  React.useEffect(() => {
    setPrefsRef.current = setPipedrivePrefs;
  }, [setPipedrivePrefs]);

  const pipelineOptions = React.useMemo(
    () => [
      { value: "", label: "All deal pipelines" },
      ...(pipelines || []).map((pipeline) => ({
        value: pipeline.id,
        label: pipeline.label || pipeline.id,
      })),
    ],
    [pipelines],
  );
  const selectedPipeline = React.useMemo(
    () => (pipelines || []).find((pipeline) => pipeline.id === pipelineId) || null,
    [pipelines, pipelineId],
  );

  // `fresh` skips the server's short cache. A fresh read that fails leaves the list alone.
  async function loadPipelines({ fresh = false } = {}) {
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/pipedrive/pipelines${fresh ? "?fresh=1" : ""}`,
    );
    if (!ok) {
      if (fresh) return;
      setPipelines([]);
      setPipelinesMsg(
        PIPEDRIVE_SYNC_ERRORS[json?.error] || "Couldn't load Pipedrive pipelines. Try again.",
      );
      return;
    }
    setPipelinesMsg("");
    setPipelines(Array.isArray(json?.pipelines) ? json.pipelines : []);
  }

  useRefreshOnReturn(() => loadPipelines({ fresh: true }), localConnected);

  // { configured, connected, companyName, companyDomain, needsReconnect, prefs },
  // read from the WORKSPACE's grant (any admin's connect serves everyone).
  async function fetchStatus() {
    const r = await fetch(`${apiBase}/api/pipedrive/connected`, {
      credentials: "include",
      cache: "no-store",
    });
    return r.ok ? r.json() : { connected: false };
  }

  async function refreshStatus() {
    try {
      const status = await fetchStatus();
      const ok = !!status?.connected;
      setLocalConnected(ok);
      setPipedriveConnected?.(ok);
      setMeta(status || null);
      if (ok) {
        const nextPrefs =
          pipedrivePrefs && typeof pipedrivePrefs === "object"
            ? pipedrivePrefs
            : status?.prefs && typeof status.prefs === "object"
              ? status.prefs
              : {};
        setPipelineId(String(nextPrefs?.pipelineId || "").trim());
        setLookbackDays(Math.max(1, Number(nextPrefs?.lookbackDays) || 90));
        if ((!pipedrivePrefs || typeof pipedrivePrefs !== "object") && status?.prefs) {
          setPipedrivePrefs?.(status.prefs);
        }
        await loadPipelines();
      } else {
        setPipelines([]);
      }
    } catch {
      setLocalConnected(false);
      setPipedriveConnected?.(false);
      setMeta(null);
      setPipelines([]);
    } finally {
      hydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Waits for the status to CHANGE (connect, or a reconnect clearing the flag), so
  // a reconnect does not close the popup before it is done.
  function openOAuth() {
    setSyncMsg("");
    const popup = window.open(
      `${apiBase}/api/pipedrive/oauth/start?returnTo=/settings`,
      "_blank",
      "width=640,height=760",
    );
    watchOAuthPopup(popup, {
      fetchStatus,
      before: pipedriveStatusKey(meta),
      statusKey: pipedriveStatusKey,
      onDone: refreshStatus,
    });
  }

  async function disconnectAccount() {
    const confirmed = await confirmDisconnect(
      "Disconnect Pipedrive for this workspace?",
      "BossDrop's app is removed from Pipedrive too.",
    );
    if (!confirmed) return;
    setDisconnecting(true);
    setSyncMsg("");
    try {
      const r = await fetch(`${apiBase}/api/pipedrive/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("pipedrive_disconnect_failed");
      setLocalConnected(false);
      setPipedriveConnected?.(false);
      setMeta(null);
      setPipelines([]);
      setPipelineId("");
      setLookbackDays(90);
      setPipedrivePrefs?.(null);
    } catch {
      setSyncMsg("Couldn't disconnect Pipedrive. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }

  async function syncNow() {
    setLoading(true);
    setSyncMsg("");
    loadPipelines({ fresh: true });
    try {
      const url = new URL(`${apiBase}/api/pipedrive/deals`);
      if (pipelineId) url.searchParams.set("pipeline", pipelineId);
      url.searchParams.set("lookbackDays", String(lookbackDays));
      url.searchParams.set("limit", "120");
      const r = await fetch(url, { credentials: "include", cache: "no-store" });
      const json = await r.json().catch(() => null);
      if (!r.ok) throw new Error(String(json?.error || "pipedrive_deals_failed"));
      const count = Array.isArray(json?.deals) ? json.deals.length : 0;
      setSyncMsg(
        count
          ? `${count} Pipedrive ${count === 1 ? "deal" : "deals"} loaded for AI context.`
          : "No open Pipedrive deals, and none closed in the selected window.",
      );
    } catch (e) {
      setSyncMsg(PIPEDRIVE_SYNC_ERRORS[e?.message] || "Pipedrive sync failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (!localConnected || !hydrated.current) return;
    const nextPrefs = {
      pipelineId: pipelineId || null,
      pipelineLabel: selectedPipeline?.label || null,
      lookbackDays: Math.max(1, Number(lookbackDays) || 90),
    };
    const saved = pipedrivePrefs && typeof pipedrivePrefs === "object" ? pipedrivePrefs : {};
    const currentPrefs = {
      pipelineId: saved.pipelineId || null,
      pipelineLabel: saved.pipelineLabel || null,
      lookbackDays: Math.max(1, Number(saved.lookbackDays) || 90),
    };
    if (JSON.stringify(nextPrefs) === JSON.stringify(currentPrefs)) {
      setSaveMsg((prev) => (prev === "Saving..." ? "Saved." : prev));
      return;
    }
    setSaveMsg("Saving...");
    setPrefsRef.current?.(nextPrefs);
    const timer = setTimeout(() => setSaveMsg("Saved."), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localConnected,
    pipelineId,
    selectedPipeline?.label,
    lookbackDays,
    pipedrivePrefs?.pipelineId,
    pipedrivePrefs?.pipelineLabel,
    pipedrivePrefs?.lookbackDays,
  ]);

  return (
    <div className="panelSub">
      <h3 className="crCardHead">
        <span className="crCardHead__title">
          <PipedriveLogo />
          Pipedrive
        </span>
        {localConnected ? (
          <span className="chip ok" style={{ marginLeft: "auto" }} title="Connection status">
            <span className="chipDot" />
            Connected
          </span>
        ) : null}
      </h3>

      {!localConnected ? (
        <>
          <div className="muted" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span>Not connected</span>
            <button className="btn primary" onClick={openOAuth} disabled={meta?.configured === false}>
              Connect Pipedrive
            </button>
          </div>
          {meta?.configured === false ? (
            <div className="muted small" style={{ marginTop: 8 }}>
              {PIPEDRIVE_SYNC_ERRORS.pipedrive_not_configured}
            </div>
          ) : (
            <div className="muted small" style={{ marginTop: 8 }}>
              Connect with an account that can see every deal: BossDrop sees what that person sees in Pipedrive.
            </div>
          )}
          {syncMsg ? (
            <div className="muted small" style={{ marginTop: 8 }} aria-live="polite">
              {syncMsg}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="formRow" style={{ alignItems: "center" }}>
            <div
              className="muted"
              style={{ minHeight: 44, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
            >
              {meta?.companyName ? `Connected to ${meta.companyName}` : "Connected"}
              {meta?.companyDomain ? ` (${meta.companyDomain}.pipedrive.com)` : ""}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button className="btn" onClick={syncNow} disabled={loading || disconnecting}>
                {loading ? "Syncing..." : "Sync now"}
              </button>
              <button
                className="btn btnDanger"
                onClick={disconnectAccount}
                disabled={loading || disconnecting}
                title="Disconnect Pipedrive from BossDrop"
              >
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>

          {meta?.needsReconnect ? <ReconnectNote label="Pipedrive" onReconnect={openOAuth} /> : null}

          <div className="formRow" style={{ marginTop: 10 }}>
            <label style={{ display: "grid", gap: 6 }}>
              Sales pipeline
              <FancySelect
                value={pipelineId}
                onChange={(value) => setPipelineId(value)}
                options={pipelineOptions}
                placeholder="Select a Pipedrive pipeline"
                ariaLabel="Select Pipedrive pipeline"
                disabled={!localConnected}
                emptyText="No Pipedrive pipelines found"
              />
            </label>

            <label style={{ display: "grid", gap: 6 }}>
              Closed deals window (days)
              <input
                type="number"
                min={1}
                max={3650}
                value={lookbackDays}
                onChange={(e) => setLookbackDays(Math.max(1, Math.min(3650, Number(e.target.value) || 90)))}
                style={{ fontSize: 16, minHeight: 44, width: 140 }}
              />
            </label>

            <div style={{ alignSelf: "end" }}>
              {saveMsg ? (
                <span className="muted" aria-live="polite" style={{ whiteSpace: "nowrap" }}>
                  {saveMsg}
                </span>
              ) : null}
            </div>
          </div>

          {pipelinesMsg ? (
            <div className="muted small" style={{ marginTop: 8 }} aria-live="polite">
              {pipelinesMsg}
            </div>
          ) : null}

          {syncMsg ? (
            <div className="muted" style={{ marginTop: 8 }}>
              {syncMsg}
            </div>
          ) : null}

          <p className="muted" style={{ marginTop: 8 }}>
            BossDrop reads every open deal in the selected sales pipeline (stage, owner, value, expected
            close, last update) plus deals closed in the history window, so the AI can spot past-due and
            stalled deals and missing ownership, and a custom automation can start when a deal changes.
            Read only: BossDrop never changes Pipedrive.
          </p>
        </>
      )}
    </div>
  );
});

// Whole-number setting in [min, max]; anything else is the fallback. Mirrors
// the server's clamps so the hub never saves a value the server would change.
function clampInt(value, min, max, fallback) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < min) return fallback;
  return Math.min(max, n);
}

// A text link that opens a popup (it does not navigate), so it is a button.
const LINK_BUTTON_STYLE = {
  background: "none",
  border: 0,
  padding: 0,
  font: "inherit",
  color: "inherit",
  textDecoration: "underline",
  cursor: "pointer",
};

/* ---------- LINEAR ---------- */
// Same caps as sanitizeLinearPrefs in backend/server/linearClient.js.
const MAX_LINEAR_TEAMS = 50;
const LINEAR_DEFAULT_LOOKBACK_DAYS = 60;

// Error codes /api/linear/* may return, in words. Anything else falls back to
// the caller's generic message.
const LINEAR_ERRORS = {
  linear_not_configured: "Linear isn't enabled on this BossDrop server yet.",
  linear_not_connected: "Connect Linear first.",
  linear_reconnect_required: "Linear sign-in expired. Reconnect Linear.",
  linear_rate_limited:
    "Linear is rate limiting BossDrop. Try again in a minute.",
  team_required: "Pick at least one Linear team first.",
  // The server's own per-route limit (createRateLimiter); same words as
  // CONNECTOR_RATE_LIMITED, which is declared further down this file.
  rate_limited: "Too many requests in a short time. Try again in a minute.",
};

// What changes on /api/linear/connected when a connect or a reconnect lands,
// so the OAuth poll waits for it instead of stopping at "already connected".
function linearStatusKey(status) {
  return JSON.stringify([
    !!status?.connected,
    !!status?.needsReconnect,
    status?.orgName || "",
    status?.actor || "",
  ]);
}

// The saved linearPrefs in the shape the server stores (sanitizeLinearPrefs),
// so a prefs round trip compares equal and the autosave does not loop.
function linearPrefsShape(prefs) {
  const src = prefs && typeof prefs === "object" ? prefs : {};
  const list = (value) =>
    (Array.isArray(value) ? value : []).map(String).filter(Boolean);
  const teamIds = list(src.teamIds);
  // The server drops a default team that is not one of teamIds.
  const defaultTeamId = String(src.defaultTeamId || "");
  const defaultKept = !!defaultTeamId && teamIds.includes(defaultTeamId);
  return {
    teamIds,
    teamKeys: list(src.teamKeys),
    defaultTeamId: defaultKept ? defaultTeamId : "",
    defaultTeamKey: defaultKept ? String(src.defaultTeamKey || "") : "",
    lookbackDays: clampInt(
      src.lookbackDays,
      1,
      365,
      LINEAR_DEFAULT_LOOKBACK_DAYS,
    ),
  };
}

const LinearConnector = React.memo(function LinearConnector({
  apiBase,
  linearConnected,
  setLinearConnected,
  setLinearData,
  linearPrefs,
  setLinearPrefs,
}) {
  const [loading, setLoading] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [localConnected, setLocalConnected] = React.useState(!!linearConnected);
  const [meta, setMeta] = React.useState(null);
  const [teams, setTeams] = React.useState([]);
  const [teamsLoaded, setTeamsLoaded] = React.useState(false);
  const [teamsMsg, setTeamsMsg] = React.useState("");
  const [teamFilter, setTeamFilter] = React.useState("");
  const [teamIds, setTeamIds] = React.useState(
    () => linearPrefsShape(linearPrefs).teamIds,
  );
  const [defaultTeamId, setDefaultTeamId] = React.useState(
    () => linearPrefsShape(linearPrefs).defaultTeamId,
  );
  const [lookbackDays, setLookbackDays] = React.useState(
    () => linearPrefsShape(linearPrefs).lookbackDays,
  );
  const [saveMsg, setSaveMsg] = React.useState("");
  const [syncMsg, setSyncMsg] = React.useState("");
  const hydrated = React.useRef(false);
  const setLinearPrefsRef = React.useRef(setLinearPrefs);

  React.useEffect(() => {
    setLinearPrefsRef.current = setLinearPrefs;
  }, [setLinearPrefs]);

  const teamById = React.useMemo(
    () => new Map((teams || []).map((team) => [String(team.id), team])),
    [teams],
  );
  const visibleTeams = React.useMemo(() => {
    const q = teamFilter.trim().toLowerCase();
    return (teams || []).filter((team) =>
      q
        ? `${team.name || ""} ${team.key || ""}`.toLowerCase().includes(q)
        : true,
    );
  }, [teams, teamFilter]);
  // Only a team BossDrop reads can be the default for new issues.
  const defaultTeamOptions = React.useMemo(
    () =>
      teamIds.map((id) => {
        const team = teamById.get(id);
        return {
          value: id,
          label: team
            ? team.key
              ? `${team.name} (${team.key})`
              : team.name || id
            : id,
        };
      }),
    [teamIds, teamById],
  );
  const notConfigured = meta?.configured === false;

  async function loadLinearTeams({ fresh = false } = {}) {
    if (!fresh) setTeamsMsg("");
    try {
      const r = await fetch(`${apiBase}/api/linear/teams`, {
        credentials: "include",
        cache: "no-store",
      });
      const json = await r.json().catch(() => null);
      if (!r.ok) {
        if (fresh) return [];
        setTeams([]);
        setTeamsLoaded(false);
        setTeamsMsg(
          LINEAR_ERRORS[json?.error] || "Couldn't load Linear teams. Try again.",
        );
        return [];
      }
      const list = Array.isArray(json?.teams) ? json.teams : [];
      setTeams(list);
      setTeamsLoaded(true);
      return list;
    } catch {
      if (fresh) return [];
      setTeams([]);
      setTeamsLoaded(false);
      setTeamsMsg("Couldn't load Linear teams. Try again.");
      return [];
    }
  }

  useRefreshOnReturn(() => loadLinearTeams({ fresh: true }), localConnected);

  async function fetchLinearStatus() {
    return fetch(`${apiBase}/api/linear/connected`, {
      credentials: "include",
      cache: "no-store",
    }).then((r) => (r.ok ? r.json() : { connected: false }));
  }

  async function refreshLinearStatus() {
    try {
      const status = await fetchLinearStatus();
      const ok = !!status?.connected;
      setLocalConnected(ok);
      setLinearConnected?.(ok);
      setMeta(status || null);

      if (ok) {
        const saved = linearPrefsShape(linearPrefs);
        setTeamIds(saved.teamIds);
        setDefaultTeamId(saved.defaultTeamId);
        setLookbackDays(saved.lookbackDays);
        const list = await loadLinearTeams();
        // A Linear workspace with a single team has only one sensible
        // choice, the same way Jira preselects the first project.
        if (!saved.teamIds.length && list.length === 1 && list[0]?.id) {
          const onlyId = String(list[0].id);
          setTeamIds([onlyId]);
          setDefaultTeamId(onlyId);
        }
      } else {
        setTeams([]);
        setTeamsLoaded(false);
      }
      return ok;
    } catch {
      setLocalConnected(false);
      setLinearConnected?.(false);
      setMeta(null);
      setTeams([]);
      setTeamsLoaded(false);
      return false;
    } finally {
      hydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshLinearStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // actor "app" (default) installs BossDrop as its own Linear actor and needs
  // a Linear workspace admin. actor "user" is the fallback for everyone else:
  // BossDrop then acts as the person who connected it.
  function openLinearOAuth(actor = "app") {
    setSyncMsg("");
    const popup = window.open(
      `${apiBase}/api/linear/oauth/start?returnTo=/settings${
        actor === "user" ? "&actor=user" : ""
      }`,
      "_blank",
      "width=640,height=760",
    );
    // Waits for the status to CHANGE, so a reconnect (already "connected",
    // needsReconnect set) does not close the popup before it is finished.
    watchOAuthPopup(popup, {
      fetchStatus: fetchLinearStatus,
      before: linearStatusKey(meta),
      statusKey: linearStatusKey,
      onDone: refreshLinearStatus,
    });
  }

  async function disconnectLinearAccount() {
    const confirmed = await confirmDisconnect(
      "Disconnect Linear for this workspace?",
      "BossDrop's access is revoked in Linear too.",
    );
    if (!confirmed) return;
    setDisconnecting(true);
    setSyncMsg("");
    try {
      const r = await fetch(`${apiBase}/api/linear/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("linear_disconnect_failed");
      setLocalConnected(false);
      setLinearConnected?.(false);
      setMeta(null);
      setTeams([]);
      setTeamsLoaded(false);
      setTeamIds([]);
      setDefaultTeamId("");
      setLookbackDays(LINEAR_DEFAULT_LOOKBACK_DAYS);
      setLinearPrefs?.(null);
      setLinearData?.([]);
    } catch {
      toast.error("Couldn't disconnect Linear. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }

  // The server reads the team filter from the workspace owner's saved
  // settings, so this syncs whatever the autosave last stored.
  async function syncLinearNow() {
    if (!teamIds.length) {
      setSyncMsg(LINEAR_ERRORS.team_required);
      return;
    }
    setLoading(true);
    setSyncMsg("");
    try {
      const r = await fetch(`${apiBase}/api/linear/issues`, {
        credentials: "include",
        cache: "no-store",
      });
      const json = await r.json().catch(() => null);
      if (!r.ok) {
        setSyncMsg(
          LINEAR_ERRORS[json?.error] || "Linear sync failed. Check server logs.",
        );
        return;
      }
      const tasks = Array.isArray(json?.tasks) ? json.tasks : [];
      setLinearData?.(tasks);
      setSyncMsg(
        tasks.length
          ? `${tasks.length} Linear ${tasks.length === 1 ? "issue" : "issues"} loaded for AI context.`
          : "No matching Linear issues in the selected teams.",
      );
    } catch {
      setSyncMsg("Linear sync failed. Check server logs.");
    } finally {
      setLoading(false);
    }
  }

  function toggleTeam(id) {
    const key = String(id);
    const selected = teamIds.includes(key);
    if (!selected && teamIds.length >= MAX_LINEAR_TEAMS) return;
    const next = selected
      ? teamIds.filter((teamId) => teamId !== key)
      : [...teamIds, key];
    setTeamIds(next);
    // New issues need a default team: keep it inside the selection.
    if (!next.includes(defaultTeamId)) setDefaultTeamId(next[0] || "");
  }

  React.useEffect(() => {
    if (!localConnected || !hydrated.current) return;
    const currentPrefs = linearPrefsShape(linearPrefs);
    // Team keys are labels for the brain's settings summary. Until the team
    // list has loaded, keep the saved ones rather than blanking them.
    const nextPrefs = {
      teamIds,
      teamKeys: teamsLoaded
        ? teamIds.map((id) => teamById.get(id)?.key).filter(Boolean)
        : currentPrefs.teamKeys,
      defaultTeamId: defaultTeamId || "",
      defaultTeamKey: teamsLoaded
        ? teamById.get(defaultTeamId)?.key || ""
        : currentPrefs.defaultTeamKey,
      lookbackDays: clampInt(
        lookbackDays,
        1,
        365,
        LINEAR_DEFAULT_LOOKBACK_DAYS,
      ),
    };
    if (JSON.stringify(nextPrefs) === JSON.stringify(currentPrefs)) {
      setSaveMsg((prev) => (prev === "Saving..." ? "Saved." : prev));
      return;
    }
    setSaveMsg("Saving...");
    setLinearPrefsRef.current?.(nextPrefs);
    const timer = setTimeout(() => setSaveMsg("Saved."), 300);
    return () => clearTimeout(timer);
  }, [
    localConnected,
    teamIds,
    defaultTeamId,
    lookbackDays,
    teamsLoaded,
    teamById,
    linearPrefs,
  ]);

  return (
    <div className="panelSub">
      <h3 className="crCardHead">
        <span className="crCardHead__title">
          <LinearLogo />
          Linear
        </span>
        {localConnected ? (
          <span
            className="chip ok"
            style={{ marginLeft: "auto" }}
            title="Connection status"
          >
            <span className="chipDot" />
            Connected
          </span>
        ) : null}
      </h3>

      {!localConnected ? (
        <>
          <div
            className="muted"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>Not connected</span>
            <button
              className="btn primary"
              onClick={() => openLinearOAuth("app")}
              disabled={notConfigured}
            >
              Connect Linear
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 8 }}>
            {notConfigured ? (
              LINEAR_ERRORS.linear_not_configured
            ) : (
              <>
                Needs a Linear workspace admin. Changes BossDrop makes show up
                in Linear as BossDrop.{" "}
                <button
                  type="button"
                  style={LINK_BUTTON_STYLE}
                  onClick={() => openLinearOAuth("user")}
                  title="Connect with your own Linear account instead. BossDrop's changes will show up under your name."
                >
                  I'm not a Linear admin
                </button>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="formRow" style={{ alignItems: "center" }}>
            <div
              className="muted"
              style={{
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {meta?.orgName ? `Connected to ${meta.orgName}` : "Connected"}
              {meta?.connected
                ? meta?.actor === "user"
                  ? " (acts as the person who connected it)"
                  : " (acts as BossDrop)"
                : ""}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                className="btn"
                onClick={syncLinearNow}
                disabled={loading || disconnecting}
              >
                {loading ? "Syncing..." : "Sync now"}
              </button>
              <button
                className="btn btnDanger"
                onClick={disconnectLinearAccount}
                disabled={loading || disconnecting}
                title="Disconnect Linear from BossDrop"
              >
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>

          {meta?.needsReconnect ? (
            <ReconnectNote label="Linear" onReconnect={() => openLinearOAuth()} />
          ) : null}

          <div className="crFieldRow" style={{ marginTop: 10 }}>
            <div className="crField">
              <span className="crFieldLabel">
                Teams BossDrop reads{" "}
                <span className="muted small">({teamIds.length})</span>
              </span>
              {teams.length > 8 ? (
                <input
                  className="inputFancy"
                  placeholder="Filter teams..."
                  value={teamFilter}
                  onChange={(e) => setTeamFilter(e.target.value)}
                  aria-label="Filter Linear teams"
                  style={{ fontSize: 16, minHeight: 44 }}
                />
              ) : null}
              <div className="list" style={{ maxHeight: 240, overflow: "auto" }}>
                {visibleTeams.map((team) => {
                  const id = String(team.id);
                  const checked = teamIds.includes(id);
                  return (
                    <label
                      key={id}
                      className="listItem"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        className="bd-check"
                        checked={checked}
                        disabled={
                          !checked && teamIds.length >= MAX_LINEAR_TEAMS
                        }
                        onChange={() => toggleTeam(id)}
                        style={{ width: 18, height: 18 }}
                      />
                      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {team.name || id}
                        {team.key ? ` (${team.key})` : ""}
                      </span>
                      {team.activeCycle?.number != null ? (
                        <span
                          className="muted small"
                          style={{ marginLeft: "auto", whiteSpace: "nowrap" }}
                        >
                          Cycle {team.activeCycle.number}
                        </span>
                      ) : null}
                    </label>
                  );
                })}
                {!visibleTeams.length ? (
                  <div
                    className="muted"
                    style={{
                      padding: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <span>
                      {teamsMsg ||
                        (teamsLoaded
                          ? teams.length
                            ? "No teams match that filter."
                            : "No teams found in this Linear workspace."
                          : "Loading teams...")}
                    </span>
                    {teamsMsg ? (
                      <button
                        className="btn"
                        type="button"
                        onClick={loadLinearTeams}
                      >
                        Try again
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="crField">
              <div className="crFieldLabel">Default team for new issues</div>
              <FancySelect
                value={defaultTeamId}
                onChange={(value) => setDefaultTeamId(value)}
                options={defaultTeamOptions}
                placeholder="Pick a team"
                ariaLabel="Default Linear team for new issues"
                disabled={!teamIds.length}
                emptyText="Pick a team to read first"
                usePortal
              />
            </div>

            <label className="crField">
              <span className="crFieldLabel">History window (days)</span>
              <input
                type="number"
                min={1}
                max={365}
                value={lookbackDays}
                onChange={(e) =>
                  setLookbackDays(
                    clampInt(
                      e.target.value,
                      1,
                      365,
                      LINEAR_DEFAULT_LOOKBACK_DAYS,
                    ),
                  )
                }
                style={{ fontSize: 16, minHeight: 44, width: 140 }}
              />
              <span className="muted small">
                Open issues always count. Finished issues count if they were
                updated within this window.
              </span>
            </label>
          </div>

          {saveMsg ? (
            <div className="muted" aria-live="polite" style={{ marginTop: 8 }}>
              {saveMsg}
            </div>
          ) : null}

          {syncMsg ? (
            <div className="muted" style={{ marginTop: 8 }}>
              {syncMsg}
            </div>
          ) : null}

          <p className="muted" style={{ marginTop: 8 }}>
            BossDrop reads open and recently updated issues from the selected
            teams, and can create issues in the default team and update
            existing ones when Linear is the task tool.
          </p>
        </>
      )}
    </div>
  );
});

/* ---------- Shared by ClickUp, Notion and monday.com ---------- */
// The per-route limit the server puts on connector reads (createRateLimiter).
const CONNECTOR_RATE_LIMITED =
  "Too many requests in a short time. Try again in a minute.";

// GET a connector route as JSON. Never throws: a network failure comes back as
// { ok: false, json: null }, so every caller has one error path.
async function getConnectorJson(url) {
  try {
    const r = await fetch(url, { credentials: "include", cache: "no-store" });
    const json = await r.json().catch(() => null);
    return { ok: r.ok, json };
  } catch {
    return { ok: false, json: null };
  }
}

// Polls a provider's /connected route while someone signs in through its popup, until the
// status CHANGES (a first connect, a reconnect clearing the flag, a newly shared
// workspace), then refreshes the card. Waiting for a change rather than for "connected"
// keeps a reconnect from closing the popup before the person has finished in it.
function watchOAuthPopup(popup, { fetchStatus, before, statusKey, onDone }) {
  watchSignInPopup(popup, {
    check: async () => {
      const status = await fetchStatus();
      return status?.connected && statusKey(status) !== before;
    },
    onDone,
  });
}

function countLabel(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

// A checkbox list for picking containers (lists, databases, boards) or option
// values. `items` are { id, label, hint?, group? }; when `group` changes from
// one item to the next, a header row names the new group.
function PickList({
  items,
  selected,
  onToggle,
  max = Infinity,
  emptyText,
  errorText = "",
  onRetry,
  ariaLabel,
  maxHeight = 240,
}) {
  return (
    <div
      className="list"
      role="group"
      aria-label={ariaLabel}
      style={{ maxHeight, overflow: "auto" }}
    >
      {items.map((item, index) => {
        const checked = selected.includes(item.id);
        const header =
          item.group && item.group !== items[index - 1]?.group ? (
            <div
              className="muted small"
              style={{ padding: "8px 14px 4px", fontWeight: 700 }}
            >
              {item.group}
            </div>
          ) : null;
        return (
          <React.Fragment key={item.id}>
            {header}
            <label
              className="listItem"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 8,
              }}
            >
              <input
                type="checkbox"
                className="bd-check"
                checked={checked}
                disabled={!checked && selected.length >= max}
                onChange={() => onToggle(item.id)}
                style={{ width: 18, height: 18 }}
              />
              <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                {item.label}
              </span>
              {item.hint ? (
                <span
                  className="muted small"
                  style={{ marginLeft: "auto", whiteSpace: "nowrap" }}
                >
                  {item.hint}
                </span>
              ) : null}
            </label>
          </React.Fragment>
        );
      })}
      {!items.length ? (
        <div
          className="muted"
          style={{
            padding: 8,
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <span>{errorText || emptyText}</span>
          {errorText && onRetry ? (
            <button className="btn" type="button" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// The grant still exists but the provider refused it (integration_health
// needs_reconnect). The Reconnect banner says the same thing for every tool;
// this puts the button where the admin is looking.
function ReconnectNote({ label, onReconnect }) {
  return (
    <div
      className="muted small"
      style={{
        marginTop: 8,
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>{label} sign-in expired, so syncing has stopped.</span>
      <button className="btn" type="button" onClick={onReconnect}>
        Reconnect {label}
      </button>
    </div>
  );
}

// A small error line with a retry, for pickers that are not a PickList.
function RetryNote({ text, onRetry }) {
  return (
    <div
      className="muted small"
      style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}
    >
      <span>{text}</span>
      {onRetry ? (
        <button className="btn" type="button" onClick={onRetry}>
          Try again
        </button>
      ) : null}
    </div>
  );
}

// History window input, the same control and copy for every task tool.
function LookbackField({ value, onChange, noun = "tasks" }) {
  return (
    <label className="crField">
      <span className="crFieldLabel">History window (days)</span>
      <input
        type="number"
        min={1}
        max={365}
        value={value}
        onChange={(e) =>
          onChange(clampInt(e.target.value, 1, 365, PM_DEFAULT_LOOKBACK_DAYS))
        }
        style={{ fontSize: 16, minHeight: 44, width: 140 }}
      />
      <span className="muted small">
        Open {noun} always count. Finished {noun} count if they were updated
        within this window.
      </span>
    </label>
  );
}

/* ---------- CLICKUP ---------- */
// Error codes /api/clickup/* may return, in words. Anything else falls back to
// the caller's generic message.
const CLICKUP_ERRORS = {
  clickup_not_configured: "ClickUp isn't enabled on this BossDrop server yet.",
  clickup_not_connected: "Connect ClickUp first.",
  clickup_reconnect_required: "ClickUp sign-in expired. Reconnect ClickUp.",
  clickup_rate_limited:
    "ClickUp is rate limiting BossDrop. Try again in a minute.",
  clickup_team_required: "Pick a ClickUp workspace first.",
  clickup_list_required: "Pick at least one ClickUp list first.",
  clickup_team_not_authorized:
    "BossDrop can't see that ClickUp workspace. Connect again and tick it on the ClickUp screen, or pick another workspace.",
  rate_limited: CONNECTOR_RATE_LIMITED,
};

// What changes on /api/clickup/connected when a connect, a reconnect or a
// newly shared ClickUp workspace lands.
function clickupStatusKey(status) {
  return JSON.stringify([
    !!status?.connected,
    !!status?.needsReconnect,
    Array.isArray(status?.teamNames) ? status.teamNames : [],
    status?.userName || "",
  ]);
}

// "Space / Folder", the way ClickUp's sidebar nests a list.
function clickupListPath(list) {
  return [list?.spaceName, list?.folderName].filter(Boolean).join(" / ");
}

const ClickUpConnector = React.memo(function ClickUpConnector({
  apiBase,
  clickupConnected,
  setClickupConnected,
  setClickupData,
  clickupPrefs,
  setClickupPrefs,
}) {
  const [loading, setLoading] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [localConnected, setLocalConnected] = React.useState(
    !!clickupConnected,
  );
  const [meta, setMeta] = React.useState(null);
  const [teams, setTeams] = React.useState([]);
  const [teamsLoaded, setTeamsLoaded] = React.useState(false);
  const [teamsMsg, setTeamsMsg] = React.useState("");
  const [lists, setLists] = React.useState([]);
  const [listsLoaded, setListsLoaded] = React.useState(false);
  const [listsMsg, setListsMsg] = React.useState("");
  const [listFilter, setListFilter] = React.useState("");
  const [teamId, setTeamId] = React.useState(
    () => clickupPrefsShape(clickupPrefs).teamId,
  );
  const [listIds, setListIds] = React.useState(
    () => clickupPrefsShape(clickupPrefs).listIds,
  );
  const [defaultListId, setDefaultListId] = React.useState(
    () => clickupPrefsShape(clickupPrefs).defaultListId,
  );
  const [lookbackDays, setLookbackDays] = React.useState(
    () => clickupPrefsShape(clickupPrefs).lookbackDays,
  );
  const [saveMsg, setSaveMsg] = React.useState("");
  const [syncMsg, setSyncMsg] = React.useState("");
  const hydrated = React.useRef(false);
  // Bumped per list load, so a slow answer for a workspace the admin already
  // switched away from is dropped.
  const listsRequest = React.useRef(0);
  const setClickupPrefsRef = React.useRef(setClickupPrefs);

  React.useEffect(() => {
    setClickupPrefsRef.current = setClickupPrefs;
  }, [setClickupPrefs]);

  const listById = React.useMemo(
    () => new Map((lists || []).map((list) => [String(list.id), list])),
    [lists],
  );
  const teamOptions = React.useMemo(() => {
    const options = (teams || []).map((team) => ({
      value: String(team.id),
      label: team.name || "Unnamed workspace",
    }));
    // A saved workspace the grant no longer covers stays visible, so the admin
    // sees why nothing syncs instead of a blank picker.
    if (teamId && teamsLoaded && !options.some((o) => o.value === teamId)) {
      options.push({
        value: teamId,
        label: "Saved workspace (no longer shared with BossDrop)",
      });
    }
    return options;
  }, [teams, teamsLoaded, teamId]);
  const listItems = React.useMemo(() => {
    const q = listFilter.trim().toLowerCase();
    const items = (lists || [])
      .filter((list) =>
        q
          ? `${list.name || ""} ${clickupListPath(list)}`
              .toLowerCase()
              .includes(q)
          : true,
      )
      .map((list) => ({
        id: String(list.id),
        label: list.name || "Unnamed list",
        group: clickupListPath(list) || "Lists",
      }));
    // Picked lists ClickUp no longer returns (archived, deleted, out of reach)
    // stay listed and ticked, so they can be removed.
    if (listsLoaded) {
      for (const id of listIds) {
        if (!listById.has(id)) {
          items.push({
            id,
            label: "List no longer available",
            group: "No longer available",
          });
        }
      }
    }
    return items;
  }, [lists, listFilter, listsLoaded, listIds, listById]);
  // Only a list BossDrop reads can be the default for new tasks.
  const defaultListOptions = React.useMemo(
    () =>
      listIds.map((id) => {
        const list = listById.get(id);
        const path = list ? clickupListPath(list) : "";
        return {
          value: id,
          label: list
            ? path
              ? `${list.name || "Unnamed list"} (${path})`
              : list.name || "Unnamed list"
            : listsLoaded
              ? "List no longer available"
              : "Loading lists...",
        };
      }),
    [listIds, listById, listsLoaded],
  );
  const notConfigured = meta?.configured === false;

  async function fetchClickUpStatus() {
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/clickup/connected`,
    );
    return ok && json ? json : { connected: false };
  }

  async function loadClickUpTeams() {
    setTeamsMsg("");
    const { ok, json } = await getConnectorJson(`${apiBase}/api/clickup/teams`);
    if (!ok) {
      setTeams([]);
      setTeamsLoaded(false);
      setTeamsMsg(
        CLICKUP_ERRORS[json?.error] ||
          "Couldn't load ClickUp workspaces. Try again.",
      );
      return { suggestedTeamId: "" };
    }
    setTeams(Array.isArray(json?.teams) ? json.teams : []);
    setTeamsLoaded(true);
    return { suggestedTeamId: String(json?.suggestedTeamId || "") };
  }

  async function loadClickUpLists(team, { fresh = false } = {}) {
    const request = (listsRequest.current += 1);
    if (!fresh) {
      setListsMsg("");
      setLists([]);
      setListsLoaded(false);
    }
    if (!team) return;
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/clickup/lists?teamId=${encodeURIComponent(team)}${fresh ? "&fresh=1" : ""}`,
    );
    if (request !== listsRequest.current) return;
    if (!ok) {
      if (fresh) return;
      setListsMsg(
        CLICKUP_ERRORS[json?.error] || "Couldn't load ClickUp lists. Try again.",
      );
      return;
    }
    setLists(Array.isArray(json?.lists) ? json.lists : []);
    setListsLoaded(true);
  }

  useRefreshOnReturn(() => loadClickUpLists(teamId, { fresh: true }), localConnected && !!teamId);

  async function refreshClickUpStatus() {
    try {
      const status = await fetchClickUpStatus();
      const ok = !!status?.connected;
      setLocalConnected(ok);
      setClickupConnected?.(ok);
      setMeta(status || null);

      if (ok) {
        const saved = clickupPrefsShape(clickupPrefs);
        setTeamId(saved.teamId);
        setListIds(saved.listIds);
        setDefaultListId(saved.defaultListId);
        setLookbackDays(saved.lookbackDays);
        // The saved prefs are on screen; any change from here on, the
        // workspace auto-pick below included, is saved.
        hydrated.current = true;
        // The server suggests the saved workspace while the grant still covers
        // it, else the only one shared. With several, the admin picks.
        const { suggestedTeamId } = await loadClickUpTeams();
        const team = saved.teamId || suggestedTeamId;
        if (!saved.teamId && team) setTeamId(team);
        await loadClickUpLists(team);
      } else {
        setTeams([]);
        setTeamsLoaded(false);
        listsRequest.current += 1;
        setLists([]);
        setListsLoaded(false);
      }
      return ok;
    } catch {
      setLocalConnected(false);
      setClickupConnected?.(false);
      setMeta(null);
      setTeams([]);
      setTeamsLoaded(false);
      setLists([]);
      setListsLoaded(false);
      return false;
    } finally {
      hydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshClickUpStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Also used while connected: re-authorizing is how a ClickUp workspace that
  // was not ticked the first time gets shared.
  function openClickUpOAuth() {
    setSyncMsg("");
    const popup = window.open(
      `${apiBase}/api/clickup/oauth/start?returnTo=/control-room`,
      "_blank",
      "width=640,height=760",
    );
    watchOAuthPopup(popup, {
      fetchStatus: fetchClickUpStatus,
      before: clickupStatusKey(meta),
      statusKey: clickupStatusKey,
      onDone: refreshClickUpStatus,
    });
  }

  async function disconnectClickUpAccount() {
    const confirmed = await confirmDisconnect(
      "Disconnect ClickUp for this workspace?",
      "ClickUp gives BossDrop no way to revoke its own access, so also remove BossDrop in ClickUp under Settings > Apps.",
    );
    if (!confirmed) return;
    setDisconnecting(true);
    setSyncMsg("");
    try {
      const r = await fetch(`${apiBase}/api/clickup/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("clickup_disconnect_failed");
      setLocalConnected(false);
      setClickupConnected?.(false);
      setMeta(null);
      setTeams([]);
      setTeamsLoaded(false);
      listsRequest.current += 1;
      setLists([]);
      setListsLoaded(false);
      setTeamId("");
      setListIds([]);
      setDefaultListId("");
      setLookbackDays(PM_DEFAULT_LOOKBACK_DAYS);
      setClickupPrefs?.(null);
      setClickupData?.([]);
    } catch {
      toast.error("Couldn't disconnect ClickUp. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }

  // The server reads the workspace and lists from the workspace owner's saved
  // settings, so this syncs whatever the autosave last stored.
  async function syncClickUpNow() {
    if (!teamId) {
      setSyncMsg(CLICKUP_ERRORS.clickup_team_required);
      return;
    }
    if (!listIds.length) {
      setSyncMsg(CLICKUP_ERRORS.clickup_list_required);
      return;
    }
    setLoading(true);
    setSyncMsg("");
    try {
      const { ok, json } = await getConnectorJson(
        `${apiBase}/api/clickup/tasks`,
      );
      if (!ok) {
        setSyncMsg(
          CLICKUP_ERRORS[json?.error] || "ClickUp sync failed. Check server logs.",
        );
        return;
      }
      const tasks = Array.isArray(json?.tasks) ? json.tasks : [];
      setClickupData?.(tasks);
      setSyncMsg(
        tasks.length
          ? `${countLabel(tasks.length, "ClickUp task", "ClickUp tasks")} loaded for AI context.`
          : "No matching ClickUp tasks in the selected lists.",
      );
    } finally {
      setLoading(false);
    }
  }

  // Lists belong to one ClickUp workspace, so switching clears the picks.
  async function changeTeam(value) {
    const next = String(value || "");
    if (next === teamId) return;
    if (listIds.length) {
      const confirmed = await dialog.confirm({
        title: "Switch ClickUp workspace?",
        body: "The lists picked below belong to the current one and will be cleared.",
        confirmLabel: "Switch workspace",
      });
      if (!confirmed) return;
    }
    setTeamId(next);
    setListIds([]);
    setDefaultListId("");
    setListFilter("");
    loadClickUpLists(next);
  }

  function toggleList(id) {
    const key = String(id);
    const selected = listIds.includes(key);
    if (!selected && listIds.length >= MAX_CLICKUP_LISTS) return;
    const next = selected
      ? listIds.filter((listId) => listId !== key)
      : [...listIds, key];
    setListIds(next);
    // New tasks need a default list: keep it inside the selection.
    if (!next.includes(defaultListId)) setDefaultListId(next[0] || "");
  }

  React.useEffect(() => {
    if (!localConnected || !hydrated.current) return;
    const currentPrefs = clickupPrefsShape(clickupPrefs);
    const nextPrefs = clickupPrefsShape({
      teamId,
      listIds,
      defaultListId,
      lookbackDays,
    });
    if (JSON.stringify(nextPrefs) === JSON.stringify(currentPrefs)) {
      setSaveMsg((prev) => (prev === "Saving..." ? "Saved." : prev));
      return;
    }
    setSaveMsg("Saving...");
    setClickupPrefsRef.current?.(nextPrefs);
    const timer = setTimeout(() => setSaveMsg("Saved."), 300);
    return () => clearTimeout(timer);
  }, [
    localConnected,
    teamId,
    listIds,
    defaultListId,
    lookbackDays,
    clickupPrefs,
  ]);

  return (
    <div className="panelSub">
      <h3 className="crCardHead">
        <span className="crCardHead__title">
          <ClickUpLogo />
          ClickUp
        </span>
        {localConnected ? (
          <span
            className="chip ok"
            style={{ marginLeft: "auto" }}
            title="Connection status"
          >
            <span className="chipDot" />
            Connected
          </span>
        ) : null}
      </h3>

      {!localConnected ? (
        <>
          <div
            className="muted"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>Not connected</span>
            <button
              className="btn primary"
              onClick={openClickUpOAuth}
              disabled={notConfigured}
            >
              Connect ClickUp
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 8 }}>
            {notConfigured
              ? CLICKUP_ERRORS.clickup_not_configured
              : "Connect as a ClickUp owner or admin, or as a seat set up for BossDrop, and tick the workspace BossDrop should read on the ClickUp screen. BossDrop sees what that person sees, and its comments and edits show up under their name."}
          </div>
        </>
      ) : (
        <>
          <div className="formRow" style={{ alignItems: "center" }}>
            <div
              className="muted"
              style={{
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {meta?.userName ? `Connected as ${meta.userName}` : "Connected"}
              {meta?.orgName ? ` to ${meta.orgName}` : ""}
              {meta?.connected ? " (acts as the person who connected it)" : ""}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                className="btn"
                onClick={syncClickUpNow}
                disabled={loading || disconnecting}
              >
                {loading ? "Syncing..." : "Sync now"}
              </button>
              <button
                className="btn btnDanger"
                onClick={disconnectClickUpAccount}
                disabled={loading || disconnecting}
                title="Disconnect ClickUp from BossDrop"
              >
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>

          {meta?.needsReconnect ? (
            <ReconnectNote label="ClickUp" onReconnect={openClickUpOAuth} />
          ) : null}

          <div className="crFieldRow" style={{ marginTop: 10 }}>
            <div className="crField">
              <div className="crFieldLabel">ClickUp workspace</div>
              <FancySelect
                value={teamId}
                onChange={changeTeam}
                options={teamOptions}
                placeholder={
                  teamsLoaded ? "Pick a workspace" : "Loading workspaces..."
                }
                ariaLabel="ClickUp workspace BossDrop reads"
                disabled={!teamsLoaded}
                emptyText="No ClickUp workspace was shared"
                usePortal
              />
              {teamsMsg ? (
                <RetryNote text={teamsMsg} onRetry={loadClickUpTeams} />
              ) : null}
              <span className="muted small">
                Missing one?{" "}
                <button
                  type="button"
                  style={LINK_BUTTON_STYLE}
                  onClick={openClickUpOAuth}
                >
                  Connect again and tick it on the ClickUp screen
                </button>
              </span>
            </div>

            <div className="crField">
              <span className="crFieldLabel">
                Lists BossDrop reads{" "}
                <span className="muted small">({listIds.length})</span>
              </span>
              {lists.length > 8 ? (
                <input
                  className="inputFancy"
                  placeholder="Filter lists..."
                  value={listFilter}
                  onChange={(e) => setListFilter(e.target.value)}
                  aria-label="Filter ClickUp lists"
                  style={{ fontSize: 16, minHeight: 44 }}
                />
              ) : null}
              <PickList
                items={listItems}
                selected={listIds}
                onToggle={toggleList}
                max={MAX_CLICKUP_LISTS}
                ariaLabel="ClickUp lists BossDrop reads"
                errorText={listsMsg}
                onRetry={() => loadClickUpLists(teamId)}
                emptyText={
                  !teamId
                    ? "Pick a ClickUp workspace first."
                    : listsLoaded
                      ? lists.length
                        ? "No lists match that filter."
                        : "No lists found in this ClickUp workspace."
                      : "Loading lists..."
                }
              />
            </div>

            <div className="crField">
              <div className="crFieldLabel">Default list for new tasks</div>
              <FancySelect
                value={defaultListId}
                onChange={(value) => setDefaultListId(value)}
                options={defaultListOptions}
                placeholder="Pick a list"
                ariaLabel="Default ClickUp list for new tasks"
                disabled={!listIds.length}
                emptyText="Pick a list to read first"
                usePortal
              />
            </div>

            <LookbackField value={lookbackDays} onChange={setLookbackDays} />
          </div>

          {saveMsg ? (
            <div className="muted" aria-live="polite" style={{ marginTop: 8 }}>
              {saveMsg}
            </div>
          ) : null}

          {syncMsg ? (
            <div className="muted" style={{ marginTop: 8 }}>
              {syncMsg}
            </div>
          ) : null}

          <p className="muted" style={{ marginTop: 8 }}>
            BossDrop reads open and recently updated tasks from the selected
            lists, and can create tasks in the default list and update
            existing ones when ClickUp is the task tool. Disconnecting here
            does not remove BossDrop from ClickUp; do that in ClickUp under
            Settings &gt; Apps.
          </p>
        </>
      )}
    </div>
  );
});

/* ---------- NOTION ---------- */
// Error codes /api/notion/* may return, in words. Anything else falls back to
// the caller's generic message.
const NOTION_ERRORS = {
  notion_not_configured: "Notion isn't enabled on this BossDrop server yet.",
  notion_not_connected: "Connect Notion first.",
  notion_reconnect_required: "Notion sign-in expired. Reconnect Notion.",
  notion_rate_limited:
    "Notion is rate limiting BossDrop. Try again in a minute.",
  notion_data_source_required: "Pick at least one Notion database first.",
  data_source_required: "Pick at least one Notion database first.",
  notion_data_source_unavailable:
    "BossDrop can't open that Notion database. In Notion, share the database itself (not a linked view) with BossDrop from its ... menu under Connections.",
  notion_status_mapping_required:
    "Pick a status column for each database, so BossDrop can tell open tasks from finished ones.",
  rate_limited: CONNECTOR_RATE_LIMITED,
};

// What changes on /api/notion/connected when a connect or reconnect lands.
function notionStatusKey(status) {
  return JSON.stringify([
    !!status?.connected,
    !!status?.needsReconnect,
    status?.workspaceName || "",
  ]);
}

// The server's first guesses for a status column (autoMapNotionProperties in
// backend/server/notionClient.js), so a newly picked column starts from the
// same values BossDrop would choose.
function notionIsDoneLike(name) {
  const s = String(name || "");
  // Closed-for-good names first, before the "not" exclusion below would read
  // "Not doing" as open (isDoneLikeName in notionClient.js does the same).
  if (
    /\b(archived|rejected|dropped|abandoned|descoped|won'?t fix|not doing|obsolete)\b/i.test(
      s,
    )
  ) {
    return true;
  }
  return (
    /\b(done|complete|completed|closed|finished|shipped|resolved|released|cancel+ed|won'?t do|duplicate)\b/i.test(
      s,
    ) && !/\b(not|almost|partially|pending|awaiting|to be)\b/i.test(s)
  );
}

function notionGroupNames(prop, re) {
  return (prop?.groups || [])
    .filter((group) => re.test(group?.name || ""))
    .flatMap((group) => group?.optionNames || []);
}

function notionDefaultDone(prop) {
  if (!prop || prop.type === "checkbox") return [];
  if (prop.type === "status") {
    const grouped = notionGroupNames(prop, /complete|done|closed/i);
    if (grouped.length) return grouped.slice(0, 20);
  }
  return (prop.options || [])
    .map((option) => option.name)
    .filter(notionIsDoneLike)
    .slice(0, 20);
}

function notionDefaultInProgress(prop) {
  if (!prop || prop.type === "checkbox") return "";
  if (prop.type === "status") {
    const grouped = notionGroupNames(prop, /in.?progress|started|doing|active/i);
    if (grouped.length) return grouped[0];
  }
  const hit = (prop.options || []).find((option) =>
    /\b(in.?progress|doing|started|active|wip)\b/i.test(option.name || ""),
  );
  return hit ? hit.name : "";
}

function notionDefaultBlocked(prop) {
  return (prop?.options || [])
    .map((option) => option.name)
    .filter((name) => /\b(blocked|stuck)\b/i.test(name || ""))
    .slice(0, 20);
}

function notionKeyPrefix(prop) {
  const prefix = String(prop?.prefix || "")
    .trim()
    .toUpperCase();
  return /^[A-Z][A-Z0-9]{0,9}$/.test(prefix) ? prefix : "";
}

// The mapping BossDrop will actually use for one data source: the saved map
// re-checked against the live columns, or the server's suggestion when there
// is none (resolveNotionPropertyMap on the server).
function notionViewMap(properties, suggested, saved) {
  const auto = notionMapShape(suggested);
  if (!saved) return auto;
  const map = notionMapShape(saved);
  const byId = new Map((properties || []).map((p) => [p.id, p]));
  const pick = (id, types) => {
    const prop = id ? byId.get(id) : null;
    return prop && types.includes(prop.type) ? prop : null;
  };
  const out = notionMapShape({});
  out.title = pick(map.title, ["title"])?.id || auto.title;
  const status = pick(map.status, NOTION_STATUS_TYPES);
  if (status) {
    out.status = status.id;
    out.statusType = status.type;
    if (status.type !== "checkbox") {
      const names = new Map(
        (status.options || []).map((o) => [String(o.name).toLowerCase(), o.name]),
      );
      const keep = (list) =>
        Array.from(
          new Set(
            list.map((name) => names.get(String(name).toLowerCase())).filter(Boolean),
          ),
        );
      out.doneOptions = keep(map.doneOptions);
      if (!out.doneOptions.length) out.doneOptions = notionDefaultDone(status);
      out.inProgressOption =
        keep(map.inProgressOption ? [map.inProgressOption] : [])[0] ||
        notionDefaultInProgress(status);
      out.blockedOptions = keep(map.blockedOptions);
    }
  } else if (auto.status) {
    out.status = auto.status;
    out.statusType = auto.statusType;
    out.doneOptions = auto.doneOptions;
    out.inProgressOption = auto.inProgressOption;
    out.blockedOptions = auto.blockedOptions;
  }
  out.assignee = pick(map.assignee, ["people"])?.id || "";
  out.due = pick(map.due, ["date"])?.id || "";
  out.priority = pick(map.priority, ["select", "status"])?.id || "";
  out.blockedBy = pick(map.blockedBy, ["relation"])?.id || "";
  out.notes = pick(map.notes, ["rich_text"])?.id || "";
  const key = pick(map.key, ["unique_id"]);
  if (key) {
    out.key = key.id;
    out.keyPrefix = notionKeyPrefix(key) || map.keyPrefix;
  }
  return out;
}

const NotionConnector = React.memo(function NotionConnector({
  apiBase,
  notionConnected,
  setNotionConnected,
  setNotionData,
  notionPrefs,
  setNotionPrefs,
}) {
  const [loading, setLoading] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [localConnected, setLocalConnected] = React.useState(!!notionConnected);
  const [meta, setMeta] = React.useState(null);
  const [sources, setSources] = React.useState([]);
  const [sourcesLoaded, setSourcesLoaded] = React.useState(false);
  const [sourcesMsg, setSourcesMsg] = React.useState("");
  const [sourceFilter, setSourceFilter] = React.useState("");
  const [dataSourceIds, setDataSourceIds] = React.useState(
    () => notionPrefsShape(notionPrefs).dataSourceIds,
  );
  const [defaultDataSourceId, setDefaultDataSourceId] = React.useState(
    () => notionPrefsShape(notionPrefs).defaultDataSourceId,
  );
  const [propertyMaps, setPropertyMaps] = React.useState(
    () => notionPrefsShape(notionPrefs).propertyMaps,
  );
  const [lookbackDays, setLookbackDays] = React.useState(
    () => notionPrefsShape(notionPrefs).lookbackDays,
  );
  // Which chosen database's columns are on screen.
  const [mappingId, setMappingId] = React.useState("");
  // dataSourceId -> { dataSource, suggestedMap } from the schema route, and
  // dataSourceId -> error text. Read once per database per visit.
  const [schemas, setSchemas] = React.useState({});
  const [schemaErrors, setSchemaErrors] = React.useState({});
  const schemaLoading = React.useRef(new Set());
  const [workspaceNote, setWorkspaceNote] = React.useState("");
  const lastWorkspace = React.useRef("");
  const [saveMsg, setSaveMsg] = React.useState("");
  const [syncMsg, setSyncMsg] = React.useState("");
  const hydrated = React.useRef(false);
  const setNotionPrefsRef = React.useRef(setNotionPrefs);

  React.useEffect(() => {
    setNotionPrefsRef.current = setNotionPrefs;
  }, [setNotionPrefs]);

  const sourceById = React.useMemo(
    () => new Map((sources || []).map((s) => [notionIdShape(s.id), s])),
    [sources],
  );
  const sourceName = React.useCallback(
    (id) =>
      sourceById.get(id)?.name ||
      (sourcesLoaded ? "Database no longer shared" : "Loading databases..."),
    [sourceById, sourcesLoaded],
  );
  const sourceItems = React.useMemo(() => {
    const q = sourceFilter.trim().toLowerCase();
    const items = (sources || [])
      .filter((s) => (q ? String(s.name || "").toLowerCase().includes(q) : true))
      .map((s) => ({ id: notionIdShape(s.id), label: s.name || "Untitled" }))
      .filter((item) => item.id);
    // Picked databases Notion no longer shows BossDrop stay listed and ticked,
    // so they can be removed.
    if (sourcesLoaded) {
      for (const id of dataSourceIds) {
        if (!sourceById.has(id)) {
          items.push({ id, label: "Database no longer shared with BossDrop" });
        }
      }
    }
    return items;
  }, [sources, sourceFilter, sourcesLoaded, dataSourceIds, sourceById]);
  const chosenOptions = React.useMemo(
    () => dataSourceIds.map((id) => ({ value: id, label: sourceName(id) })),
    [dataSourceIds, sourceName],
  );
  const notConfigured = meta?.configured === false;

  // The column mapping on screen, and what BossDrop will use for it.
  const editId = dataSourceIds.includes(mappingId)
    ? mappingId
    : defaultDataSourceId || dataSourceIds[0] || "";
  const schemaEntry = editId ? schemas[editId] || null : null;
  const properties = React.useMemo(
    () =>
      Array.isArray(schemaEntry?.dataSource?.properties)
        ? schemaEntry.dataSource.properties
        : [],
    [schemaEntry],
  );
  const propById = React.useMemo(
    () => new Map(properties.map((p) => [p.id, p])),
    [properties],
  );
  const savedMap = editId ? propertyMaps[editId] || null : null;
  const viewMap = React.useMemo(
    () =>
      schemaEntry
        ? notionViewMap(properties, schemaEntry.suggestedMap, savedMap)
        : null,
    [schemaEntry, properties, savedMap],
  );
  const statusProp = viewMap ? propById.get(viewMap.status) || null : null;
  const statusOptionNames = (statusProp?.options || [])
    .map((option) => option.name)
    .filter(Boolean);

  async function fetchNotionStatus() {
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/notion/connected`,
    );
    return ok && json ? json : { connected: false };
  }

  async function loadNotionSources({ fresh = false } = {}) {
    if (!fresh) setSourcesMsg("");
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/notion/data-sources`,
    );
    if (!ok) {
      if (fresh) return;
      setSources([]);
      setSourcesLoaded(false);
      setSourcesMsg(
        NOTION_ERRORS[json?.error] ||
          "Couldn't load Notion databases. Try again.",
      );
      return;
    }
    setSources(Array.isArray(json?.dataSources) ? json.dataSources : []);
    setSourcesLoaded(true);
  }

  useRefreshOnReturn(() => loadNotionSources({ fresh: true }), localConnected);

  async function loadNotionSchema(id) {
    if (!id || schemaLoading.current.has(id)) return;
    schemaLoading.current.add(id);
    setSchemaErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const { ok, json } = await getConnectorJson(
        `${apiBase}/api/notion/data-sources/${encodeURIComponent(id)}/schema`,
      );
      if (!ok || !json?.dataSource) {
        setSchemaErrors((prev) => ({
          ...prev,
          [id]:
            NOTION_ERRORS[json?.error] ||
            "Couldn't read this database's columns. Try again.",
        }));
        return;
      }
      setSchemas((prev) => ({
        ...prev,
        [id]: {
          dataSource: json.dataSource,
          suggestedMap: notionMapShape(json.suggestedMap),
        },
      }));
    } finally {
      schemaLoading.current.delete(id);
    }
  }

  async function refreshNotionStatus() {
    try {
      const status = await fetchNotionStatus();
      const ok = !!status?.connected;
      setLocalConnected(ok);
      setNotionConnected?.(ok);
      setMeta(status || null);

      if (ok) {
        const saved = notionPrefsShape(notionPrefs);
        setDataSourceIds(saved.dataSourceIds);
        setDefaultDataSourceId(saved.defaultDataSourceId);
        setPropertyMaps(saved.propertyMaps);
        setLookbackDays(saved.lookbackDays);
        hydrated.current = true;
        // A reconnect can land on another Notion workspace, whose databases
        // have other ids. Say so rather than quietly syncing nothing.
        const name = String(status.workspaceName || "");
        if (
          lastWorkspace.current &&
          name &&
          name !== lastWorkspace.current &&
          saved.dataSourceIds.length
        ) {
          setWorkspaceNote(
            `This is a different Notion workspace (${name}) than before. Databases from the old one no longer sync, so check the picks below.`,
          );
        }
        if (name) lastWorkspace.current = name;
        await loadNotionSources();
      } else {
        setSources([]);
        setSourcesLoaded(false);
      }
      return ok;
    } catch {
      setLocalConnected(false);
      setNotionConnected?.(false);
      setMeta(null);
      setSources([]);
      setSourcesLoaded(false);
      return false;
    } finally {
      hydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshNotionStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read the columns of the database on screen, once per visit.
  React.useEffect(() => {
    if (!localConnected || !editId || schemas[editId] || schemaErrors[editId]) {
      return;
    }
    loadNotionSchema(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localConnected, editId, schemas, schemaErrors]);

  // Also used while connected: Notion's page picker on reconnect is one way to
  // share another database.
  function openNotionOAuth() {
    setSyncMsg("");
    setWorkspaceNote("");
    const popup = window.open(
      `${apiBase}/api/notion/oauth/start?returnTo=/control-room`,
      "_blank",
      "width=640,height=760",
    );
    watchOAuthPopup(popup, {
      fetchStatus: fetchNotionStatus,
      before: notionStatusKey(meta),
      statusKey: notionStatusKey,
      onDone: async () => {
        // Columns read through the earlier grant may be stale.
        setSchemas({});
        setSchemaErrors({});
        await refreshNotionStatus();
      },
    });
  }

  async function disconnectNotionAccount() {
    const confirmed = await confirmDisconnect(
      "Disconnect Notion for this workspace?",
      "BossDrop's access is revoked in Notion too.",
    );
    if (!confirmed) return;
    setDisconnecting(true);
    setSyncMsg("");
    try {
      const r = await fetch(`${apiBase}/api/notion/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("notion_disconnect_failed");
      setLocalConnected(false);
      setNotionConnected?.(false);
      setMeta(null);
      setSources([]);
      setSourcesLoaded(false);
      setDataSourceIds([]);
      setDefaultDataSourceId("");
      setPropertyMaps({});
      setLookbackDays(PM_DEFAULT_LOOKBACK_DAYS);
      setMappingId("");
      setSchemas({});
      setSchemaErrors({});
      setWorkspaceNote("");
      setNotionPrefs?.(null);
      setNotionData?.([]);
    } catch {
      toast.error("Couldn't disconnect Notion. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }

  // The server reads the databases and column maps from the workspace owner's
  // saved settings, so this syncs whatever the autosave last stored.
  async function syncNotionNow() {
    if (!dataSourceIds.length) {
      setSyncMsg(NOTION_ERRORS.data_source_required);
      return;
    }
    setLoading(true);
    setSyncMsg("");
    try {
      const { ok, json } = await getConnectorJson(`${apiBase}/api/notion/tasks`);
      if (!ok) {
        setSyncMsg(
          NOTION_ERRORS[json?.error] || "Notion sync failed. Check server logs.",
        );
        return;
      }
      const tasks = Array.isArray(json?.tasks) ? json.tasks : [];
      setNotionData?.(tasks);
      setSyncMsg(
        tasks.length
          ? `${countLabel(tasks.length, "Notion task", "Notion tasks")} loaded for AI context.`
          : "No matching Notion tasks in the selected databases.",
      );
    } finally {
      setLoading(false);
    }
  }

  function toggleSource(id) {
    const key = String(id);
    const selected = dataSourceIds.includes(key);
    if (!selected && dataSourceIds.length >= MAX_NOTION_DATA_SOURCES) return;
    const next = selected
      ? dataSourceIds.filter((sourceId) => sourceId !== key)
      : [...dataSourceIds, key];
    setDataSourceIds(next);
    // New tasks need a default database: keep it inside the selection.
    if (!next.includes(defaultDataSourceId)) {
      setDefaultDataSourceId(next[0] || "");
    }
    if (selected) {
      setPropertyMaps((prev) => {
        if (!prev[key]) return prev;
        const rest = { ...prev };
        delete rest[key];
        return rest;
      });
    } else {
      // Show the columns BossDrop picked for it, for the admin to confirm.
      setMappingId(key);
    }
  }

  // Any change saves the WHOLE map for this database, starting from what is on
  // screen, so the server never mixes a saved field with a guessed one.
  function editMapping(change) {
    if (!editId || !viewMap) return;
    setPropertyMaps((prev) => ({
      ...prev,
      [editId]: notionMapShape(change({ ...viewMap })),
    }));
  }

  function resetMapping() {
    if (!editId) return;
    setPropertyMaps((prev) => {
      if (!prev[editId]) return prev;
      const rest = { ...prev };
      delete rest[editId];
      return rest;
    });
  }

  function changeStatusProperty(id) {
    const prop = propById.get(id);
    if (!prop) return;
    editMapping((map) => ({
      ...map,
      status: prop.id,
      statusType: prop.type,
      doneOptions: notionDefaultDone(prop),
      inProgressOption: notionDefaultInProgress(prop),
      blockedOptions: notionDefaultBlocked(prop),
      priority: map.priority === prop.id ? "" : map.priority,
    }));
  }

  function changeKeyProperty(id) {
    const prop = id ? propById.get(id) : null;
    editMapping((map) => ({
      ...map,
      key: prop ? prop.id : "",
      keyPrefix: prop ? notionKeyPrefix(prop) : "",
    }));
  }

  function toggleOption(field, name) {
    editMapping((map) => {
      const list = Array.isArray(map[field]) ? map[field] : [];
      return {
        ...map,
        [field]: list.includes(name)
          ? list.filter((item) => item !== name)
          : [...list, name],
      };
    });
  }

  // Columns of the given Notion types, optionally led by "None".
  function propertyOptions(types, { none = true, except = "" } = {}) {
    const options = properties
      .filter((p) => types.includes(p.type) && p.id !== except)
      .map((p) => ({ value: p.id, label: p.name || "Untitled column" }));
    return none ? [{ value: "", label: "None" }, ...options] : options;
  }

  React.useEffect(() => {
    if (!localConnected || !hydrated.current) return;
    const currentPrefs = notionPrefsShape(notionPrefs);
    const nextPrefs = notionPrefsShape({
      dataSourceIds,
      defaultDataSourceId,
      propertyMaps,
      lookbackDays,
    });
    if (JSON.stringify(nextPrefs) === JSON.stringify(currentPrefs)) {
      setSaveMsg((prev) => (prev === "Saving..." ? "Saved." : prev));
      return;
    }
    setSaveMsg("Saving...");
    setNotionPrefsRef.current?.(nextPrefs);
    const timer = setTimeout(() => setSaveMsg("Saved."), 300);
    return () => clearTimeout(timer);
  }, [
    localConnected,
    dataSourceIds,
    defaultDataSourceId,
    propertyMaps,
    lookbackDays,
    notionPrefs,
  ]);

  return (
    <div className="panelSub">
      <h3 className="crCardHead">
        <span className="crCardHead__title">
          <NotionLogo />
          Notion
        </span>
        {localConnected ? (
          <span
            className="chip ok"
            style={{ marginLeft: "auto" }}
            title="Connection status"
          >
            <span className="chipDot" />
            Connected
          </span>
        ) : null}
      </h3>

      {!localConnected ? (
        <>
          <div
            className="muted"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>Not connected</span>
            <button
              className="btn primary"
              onClick={openNotionOAuth}
              disabled={notConfigured}
            >
              Connect Notion
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 8 }}>
            {notConfigured
              ? NOTION_ERRORS.notion_not_configured
              : "On the Notion screen, pick the task databases BossDrop may read. Pick each database itself, not a linked view of it."}
          </div>
        </>
      ) : (
        <>
          <div className="formRow" style={{ alignItems: "center" }}>
            <div
              className="muted"
              style={{
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {meta?.workspaceName
                ? `Connected to ${meta.workspaceName}`
                : "Connected"}
              {meta?.connected ? " (changes show up as BossDrop)" : ""}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                className="btn"
                onClick={syncNotionNow}
                disabled={loading || disconnecting}
              >
                {loading ? "Syncing..." : "Sync now"}
              </button>
              <button
                className="btn btnDanger"
                onClick={disconnectNotionAccount}
                disabled={loading || disconnecting}
                title="Disconnect Notion from BossDrop"
              >
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>

          {meta?.needsReconnect ? (
            <ReconnectNote label="Notion" onReconnect={openNotionOAuth} />
          ) : null}

          {workspaceNote ? (
            <div className="muted small" style={{ marginTop: 8 }}>
              {workspaceNote}
            </div>
          ) : null}

          <div className="crFieldRow" style={{ marginTop: 10 }}>
            <div className="crField">
              <span className="crFieldLabel">
                Databases BossDrop reads{" "}
                <span className="muted small">({dataSourceIds.length})</span>
              </span>
              {sources.length > 8 ? (
                <input
                  className="inputFancy"
                  placeholder="Filter databases..."
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  aria-label="Filter Notion databases"
                  style={{ fontSize: 16, minHeight: 44 }}
                />
              ) : null}
              <PickList
                items={sourceItems}
                selected={dataSourceIds}
                onToggle={toggleSource}
                max={MAX_NOTION_DATA_SOURCES}
                ariaLabel="Notion databases BossDrop reads"
                errorText={sourcesMsg}
                onRetry={loadNotionSources}
                emptyText={
                  sourcesLoaded
                    ? sources.length
                      ? "No databases match that filter."
                      : "No databases are shared with BossDrop yet."
                    : "Loading databases..."
                }
              />
              <span className="muted small">
                Missing one? In Notion, open the database&apos;s ... menu and
                add BossDrop under Connections, then{" "}
                <button
                  type="button"
                  style={LINK_BUTTON_STYLE}
                  onClick={loadNotionSources}
                >
                  check again
                </button>
                . Or{" "}
                <button
                  type="button"
                  style={LINK_BUTTON_STYLE}
                  onClick={openNotionOAuth}
                >
                  connect again
                </button>{" "}
                and pick it on the Notion screen. Linked views can&apos;t be
                read.
              </span>
            </div>

            <div className="crField">
              <div className="crFieldLabel">Default database for new tasks</div>
              <FancySelect
                value={defaultDataSourceId}
                onChange={(value) => setDefaultDataSourceId(value)}
                options={chosenOptions}
                placeholder="Pick a database"
                ariaLabel="Default Notion database for new tasks"
                disabled={!dataSourceIds.length}
                emptyText="Pick a database to read first"
                usePortal
              />
            </div>

            <LookbackField value={lookbackDays} onChange={setLookbackDays} />
          </div>

          {editId ? (
            <div className="panelSub" style={{ marginTop: 10, display: "grid", gap: 8 }}>
              <div className="crFieldLabel">Columns BossDrop uses</div>
              {dataSourceIds.length > 1 ? (
                <FancySelect
                  value={editId}
                  onChange={(value) => setMappingId(value)}
                  options={chosenOptions}
                  ariaLabel="Notion database whose columns are shown"
                  usePortal
                />
              ) : null}
              {schemaErrors[editId] ? (
                <RetryNote
                  text={schemaErrors[editId]}
                  onRetry={() => loadNotionSchema(editId)}
                />
              ) : !viewMap ? (
                <div className="muted">Reading columns...</div>
              ) : (
                <>
                  <div className="muted small">
                    BossDrop picked these from the column names and types.
                    Change any that are wrong.{" "}
                    {savedMap ? (
                      <button
                        type="button"
                        style={LINK_BUTTON_STYLE}
                        onClick={resetMapping}
                      >
                        Go back to BossDrop&apos;s picks
                      </button>
                    ) : null}
                  </div>
                  {!viewMap.status ? (
                    <div className="muted small">
                      {NOTION_ERRORS.notion_status_mapping_required} Until
                      then BossDrop skips this database.
                    </div>
                  ) : null}
                  <div className="crFieldRow">
                    <div className="crField">
                      <div className="crFieldLabel">Status</div>
                      <FancySelect
                        value={viewMap.status}
                        onChange={changeStatusProperty}
                        options={propertyOptions(NOTION_STATUS_TYPES, {
                          none: false,
                        })}
                        placeholder="Pick the status column"
                        ariaLabel="Notion status column"
                        emptyText="No status, select or checkbox column"
                        usePortal
                      />
                      {statusProp?.type === "checkbox" ? (
                        <span className="muted small">
                          A ticked box means the task is done.
                        </span>
                      ) : null}
                    </div>

                    {statusProp && statusProp.type !== "checkbox" ? (
                      <>
                        <div className="crField">
                          <span className="crFieldLabel">Counts as done</span>
                          <PickList
                            items={statusOptionNames.map((name) => ({
                              id: name,
                              label: name,
                            }))}
                            selected={viewMap.doneOptions}
                            onToggle={(name) => toggleOption("doneOptions", name)}
                            max={20}
                            maxHeight={180}
                            ariaLabel="Notion status values that count as done"
                            emptyText="This column has no values yet."
                          />
                          <span className="muted small">
                            With none ticked, BossDrop uses Notion&apos;s
                            Complete group.
                          </span>
                        </div>
                        <div className="crField">
                          <div className="crFieldLabel">In progress</div>
                          <FancySelect
                            value={viewMap.inProgressOption}
                            onChange={(value) =>
                              editMapping((map) => ({
                                ...map,
                                inProgressOption: value,
                              }))
                            }
                            options={statusOptionNames.map((name) => ({
                              value: name,
                              label: name,
                            }))}
                            placeholder="None"
                            ariaLabel="Notion status value for work in progress"
                            usePortal
                          />
                        </div>
                        <div className="crField">
                          <span className="crFieldLabel">Counts as blocked</span>
                          <PickList
                            items={statusOptionNames.map((name) => ({
                              id: name,
                              label: name,
                            }))}
                            selected={viewMap.blockedOptions}
                            onToggle={(name) =>
                              toggleOption("blockedOptions", name)
                            }
                            max={20}
                            maxHeight={180}
                            ariaLabel="Notion status values that count as blocked"
                            emptyText="This column has no values yet."
                          />
                        </div>
                      </>
                    ) : null}

                    <div className="crField">
                      <div className="crFieldLabel">Owner</div>
                      <FancySelect
                        value={viewMap.assignee}
                        onChange={(value) =>
                          editMapping((map) => ({ ...map, assignee: value }))
                        }
                        options={propertyOptions(["people"])}
                        ariaLabel="Notion owner column"
                        usePortal
                      />
                    </div>
                    <div className="crField">
                      <div className="crFieldLabel">Due date</div>
                      <FancySelect
                        value={viewMap.due}
                        onChange={(value) =>
                          editMapping((map) => ({ ...map, due: value }))
                        }
                        options={propertyOptions(["date"])}
                        ariaLabel="Notion due date column"
                        usePortal
                      />
                    </div>
                    <div className="crField">
                      <div className="crFieldLabel">Priority</div>
                      <FancySelect
                        value={viewMap.priority}
                        onChange={(value) =>
                          editMapping((map) => ({ ...map, priority: value }))
                        }
                        options={propertyOptions(["select", "status"], {
                          except: viewMap.status,
                        })}
                        ariaLabel="Notion priority column"
                        usePortal
                      />
                    </div>
                    <div className="crField">
                      <div className="crFieldLabel">Blocked by</div>
                      <FancySelect
                        value={viewMap.blockedBy}
                        onChange={(value) =>
                          editMapping((map) => ({ ...map, blockedBy: value }))
                        }
                        options={propertyOptions(["relation"])}
                        ariaLabel="Notion blocked-by relation column"
                        usePortal
                      />
                    </div>
                    <div className="crField">
                      <div className="crFieldLabel">Task ID</div>
                      <FancySelect
                        value={viewMap.key}
                        onChange={changeKeyProperty}
                        options={propertyOptions(["unique_id"])}
                        ariaLabel="Notion task ID column"
                        usePortal
                      />
                    </div>
                    <div className="crField">
                      <div className="crFieldLabel">Description</div>
                      <FancySelect
                        value={viewMap.notes}
                        onChange={(value) =>
                          editMapping((map) => ({ ...map, notes: value }))
                        }
                        options={propertyOptions(["rich_text"])}
                        ariaLabel="Notion description column"
                        usePortal
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : null}

          {saveMsg ? (
            <div className="muted" aria-live="polite" style={{ marginTop: 8 }}>
              {saveMsg}
            </div>
          ) : null}

          {syncMsg ? (
            <div className="muted" style={{ marginTop: 8 }}>
              {syncMsg}
            </div>
          ) : null}

          <p className="muted" style={{ marginTop: 8 }}>
            BossDrop reads open and recently edited tasks from the selected
            databases, and can create tasks in the default database and update
            existing ones when Notion is the task tool. A task&apos;s owner
            comes only from the Owner column picked above.
          </p>
        </>
      )}
    </div>
  );
});

/* ---------- MONDAY.COM ---------- */
// Error codes /api/monday/* may return, in words. Anything else falls back to
// the caller's generic message.
const MONDAY_ERRORS = {
  monday_not_configured: "monday.com isn't enabled on this BossDrop server yet.",
  monday_not_connected: "Connect monday.com first.",
  monday_reconnect_required:
    "monday.com sign-in expired. Reconnect monday.com.",
  monday_rate_limited:
    "monday.com's API limit was reached. BossDrop keeps using the last synced items and tries again later.",
  monday_board_required: "Pick at least one monday.com board first.",
  board_required: "Pick at least one monday.com board first.",
  monday_board_not_found:
    "BossDrop can't open that board. Check that the person who connected monday.com can see it.",
  monday_board_not_allowed:
    "That board isn't one BossDrop reads. Tick it below first.",
  monday_missing_scope:
    "BossDrop's monday.com app is missing a permission. Connect again to grant it.",
  rate_limited: CONNECTOR_RATE_LIMITED,
};

// What changes on /api/monday/connected when a connect or reconnect lands.
function mondayStatusKey(status) {
  return JSON.stringify([
    !!status?.connected,
    !!status?.needsReconnect,
    status?.orgName || "",
    status?.accountSlug || "",
  ]);
}

const EMPTY_MONDAY_MAP = {
  status: "",
  doneLabels: [],
  inProgressLabel: "",
  blockedLabels: [],
  people: "",
  date: "",
  priority: "",
  dependency: "",
  notes: "",
};

// Client copy of autoMapMondayColumns (backend/server/mondayClient.js): the
// columns and labels BossDrop will use for a board, given the saved picks. A
// saved column wins while it still exists with a fitting type; "" means pick
// automatically.
const MONDAY_PRIORITY_TITLE_RE = /priorit|urgency|severity/i;
const MONDAY_STATUS_TITLE_RE = /status|state|stage|progress/i;
const MONDAY_DUE_TITLE_RE = /due|deadline/i;
const MONDAY_NOTES_TITLE_RE = /desc|note|detail|summary/i;
const MONDAY_PEOPLE_TITLE_RE = /owner|assign|person|people|responsible/i;
const MONDAY_DONE_LABEL_RE =
  /^(done|complete|completed|closed|finished|resolved|shipped)$/i;
const MONDAY_IN_PROGRESS_LABEL_RE = /working on it|in progress/i;
const MONDAY_STARTED_LABEL_RE = /working|progress|doing|started|review|testing/i;
const MONDAY_BLOCKED_LABEL_RE = /\bstuck\b|blocked/i;

function mondayViewMap(board, saved) {
  const columns = Array.isArray(board?.columns) ? board.columns : [];
  const byId = new Map(columns.map((c) => [c.id, c]));
  const s = saved && typeof saved === "object" ? saved : {};
  const savedColumn = (field, types) => {
    const col = byId.get(String(s[field] || ""));
    return col && types.includes(col.type) ? col.id : "";
  };
  const ofType = (...types) => columns.filter((c) => types.includes(c.type));
  const firstId = (cols) => (cols[0] ? cols[0].id : "");
  const titled = (cols, re) => cols.filter((c) => re.test(c.title || ""));

  const statusCols = ofType("status");
  const priority =
    savedColumn("priority", ["status"]) ||
    firstId(titled(statusCols, MONDAY_PRIORITY_TITLE_RE));
  const candidates = statusCols.filter((c) => c.id !== priority);
  const status =
    savedColumn("status", ["status"]) ||
    firstId(candidates.filter((c) => c.id === "status")) ||
    firstId(titled(candidates, MONDAY_STATUS_TITLE_RE)) ||
    firstId(candidates);
  const peopleCols = ofType("people");
  const people =
    savedColumn("people", ["people"]) ||
    firstId(titled(peopleCols, MONDAY_PEOPLE_TITLE_RE)) ||
    firstId(peopleCols);
  const dateCols = ofType("date");
  const timelineCols = ofType("timeline");
  const date =
    savedColumn("date", ["date", "timeline"]) ||
    firstId(titled(dateCols, MONDAY_DUE_TITLE_RE)) ||
    firstId(titled(timelineCols, MONDAY_DUE_TITLE_RE)) ||
    firstId(dateCols) ||
    firstId(timelineCols);
  const dependency =
    savedColumn("dependency", ["dependency"]) || firstId(ofType("dependency"));
  const notes =
    savedColumn("notes", ["long_text"]) ||
    firstId(titled(ofType("long_text"), MONDAY_NOTES_TITLE_RE));

  const labels = (byId.get(status)?.labels || []).filter((l) => l?.label);
  const onColumn = (names) => {
    const wanted = new Set(
      (Array.isArray(names) ? names : [])
        .filter(Boolean)
        .map((name) => String(name).toLowerCase()),
    );
    return labels
      .filter((l) => wanted.has(l.label.toLowerCase()))
      .map((l) => l.label);
  };
  let doneLabels = onColumn(s.doneLabels);
  if (!doneLabels.length) {
    doneLabels = labels.filter((l) => l.is_done).map((l) => l.label);
  }
  if (!doneLabels.length) {
    doneLabels = labels
      .filter((l) => MONDAY_DONE_LABEL_RE.test(l.label))
      .map((l) => l.label);
  }
  const inProgressLabel =
    onColumn([s.inProgressLabel])[0] ||
    labels.find((l) => MONDAY_IN_PROGRESS_LABEL_RE.test(l.label))?.label ||
    labels.find((l) => MONDAY_STARTED_LABEL_RE.test(l.label) && !l.is_done)
      ?.label ||
    "";
  let blockedLabels = onColumn(s.blockedLabels);
  if (!blockedLabels.length) {
    blockedLabels = labels
      .filter((l) => MONDAY_BLOCKED_LABEL_RE.test(l.label))
      .map((l) => l.label);
  }
  return {
    status,
    doneLabels: doneLabels.slice(0, 20),
    inProgressLabel,
    blockedLabels: blockedLabels.slice(0, 20),
    people,
    date,
    priority,
    dependency,
    notes,
  };
}

const MondayConnector = React.memo(function MondayConnector({
  apiBase,
  mondayConnected,
  setMondayConnected,
  setMondayData,
  mondayPrefs,
  setMondayPrefs,
}) {
  const [loading, setLoading] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [localConnected, setLocalConnected] = React.useState(!!mondayConnected);
  const [meta, setMeta] = React.useState(null);
  const [boards, setBoards] = React.useState([]);
  const [boardsLoaded, setBoardsLoaded] = React.useState(false);
  const [boardsMsg, setBoardsMsg] = React.useState("");
  const [boardFilter, setBoardFilter] = React.useState("");
  const [boardIds, setBoardIds] = React.useState(
    () => mondayPrefsShape(mondayPrefs).boardIds,
  );
  const [defaultBoardId, setDefaultBoardId] = React.useState(
    () => mondayPrefsShape(mondayPrefs).defaultBoardId,
  );
  const [defaultGroupId, setDefaultGroupId] = React.useState(
    () => mondayPrefsShape(mondayPrefs).defaultGroupId,
  );
  const [columnMaps, setColumnMaps] = React.useState(
    () => mondayPrefsShape(mondayPrefs).columnMaps,
  );
  const [lookbackDays, setLookbackDays] = React.useState(
    () => mondayPrefsShape(mondayPrefs).lookbackDays,
  );
  // Which chosen board's columns are on screen.
  const [mappingId, setMappingId] = React.useState("");
  // boardId -> { columns, groups, ... } from the columns route, and boardId ->
  // error text. Read once per board per visit: monday.com counts every call
  // against a daily limit.
  const [boardMeta, setBoardMeta] = React.useState({});
  const [boardMetaErrors, setBoardMetaErrors] = React.useState({});
  const boardMetaLoading = React.useRef(new Set());
  const [saveMsg, setSaveMsg] = React.useState("");
  const [syncMsg, setSyncMsg] = React.useState("");
  const hydrated = React.useRef(false);
  const setMondayPrefsRef = React.useRef(setMondayPrefs);

  React.useEffect(() => {
    setMondayPrefsRef.current = setMondayPrefs;
  }, [setMondayPrefs]);

  const boardById = React.useMemo(
    () => new Map((boards || []).map((b) => [String(b.id), b])),
    [boards],
  );
  const boardName = React.useCallback(
    (id) =>
      boardById.get(id)?.name ||
      boardMeta[id]?.name ||
      (boardsLoaded ? "Board no longer available" : "Loading boards..."),
    [boardById, boardMeta, boardsLoaded],
  );
  const boardItems = React.useMemo(() => {
    const q = boardFilter.trim().toLowerCase();
    // Grouped by monday.com workspace; a stable sort keeps the server's
    // recently-used order inside each group.
    const items = [...(boards || [])]
      .filter((b) =>
        q
          ? `${b.name || ""} ${b.workspaceName || ""}`.toLowerCase().includes(q)
          : true,
      )
      .sort((a, b) =>
        String(a.workspaceName || "").localeCompare(String(b.workspaceName || "")),
      )
      .map((b) => ({
        id: String(b.id),
        label: b.name || "Unnamed board",
        hint: b.kind === "private" ? "Private" : "",
        group: b.workspaceName || "Main workspace",
      }));
    // Picked boards monday.com no longer returns stay listed and ticked, so
    // they can be removed.
    if (boardsLoaded) {
      for (const id of boardIds) {
        if (!boardById.has(id)) {
          items.push({
            id,
            label: "Board no longer available",
            group: "No longer available",
          });
        }
      }
    }
    return items;
  }, [boards, boardFilter, boardsLoaded, boardIds, boardById]);
  const chosenOptions = React.useMemo(
    () => boardIds.map((id) => ({ value: id, label: boardName(id) })),
    [boardIds, boardName],
  );
  const notConfigured = meta?.configured === false;

  // The column mapping on screen, and what BossDrop will use for it.
  const editId = boardIds.includes(mappingId)
    ? mappingId
    : defaultBoardId || boardIds[0] || "";
  const editBoard = editId ? boardMeta[editId] || null : null;
  const savedColumns = editId ? columnMaps[editId] || null : null;
  const viewMap = React.useMemo(
    () => (editBoard ? mondayViewMap(editBoard, savedColumns) : null),
    [editBoard, savedColumns],
  );
  const autoMap = React.useMemo(
    () => (editBoard ? mondayViewMap(editBoard, null) : null),
    [editBoard],
  );
  const columnById = React.useMemo(
    () =>
      new Map(
        (Array.isArray(editBoard?.columns) ? editBoard.columns : []).map((c) => [
          c.id,
          c,
        ]),
      ),
    [editBoard],
  );
  const statusLabels = (columnById.get(viewMap?.status)?.labels || [])
    .map((l) => l?.label)
    .filter(Boolean);
  const defaultBoardMeta = defaultBoardId ? boardMeta[defaultBoardId] || null : null;
  const groupOptions = React.useMemo(() => {
    const groups = Array.isArray(defaultBoardMeta?.groups)
      ? defaultBoardMeta.groups
      : [];
    const options = [
      { value: "", label: "The board's top group" },
      ...groups.map((g) => ({ value: g.id, label: g.title || "Untitled group" })),
    ];
    if (
      defaultGroupId &&
      defaultBoardMeta &&
      !groups.some((g) => g.id === defaultGroupId)
    ) {
      options.push({
        value: defaultGroupId,
        label: "Saved group (no longer on this board)",
      });
    }
    return options;
  }, [defaultBoardMeta, defaultGroupId]);

  async function fetchMondayStatus() {
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/monday/connected`,
    );
    return ok && json ? json : { connected: false };
  }

  async function loadMondayBoards({ fresh = false } = {}) {
    if (!fresh) setBoardsMsg("");
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/monday/boards${fresh ? "?fresh=1" : ""}`,
    );
    if (!ok) {
      if (fresh) return;
      setBoards([]);
      setBoardsLoaded(false);
      setBoardsMsg(
        MONDAY_ERRORS[json?.error] ||
          "Couldn't load monday.com boards. Try again.",
      );
      return;
    }
    setBoards(Array.isArray(json?.boards) ? json.boards : []);
    setBoardsLoaded(true);
  }

  useRefreshOnReturn(() => loadMondayBoards({ fresh: true }), localConnected);

  async function loadBoardMeta(id) {
    if (!id || boardMetaLoading.current.has(id)) return;
    boardMetaLoading.current.add(id);
    setBoardMetaErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    try {
      const { ok, json } = await getConnectorJson(
        `${apiBase}/api/monday/boards/${encodeURIComponent(id)}/columns`,
      );
      if (!ok || !json?.board) {
        setBoardMetaErrors((prev) => ({
          ...prev,
          [id]:
            MONDAY_ERRORS[json?.error] ||
            "Couldn't read this board's columns. Try again.",
        }));
        return;
      }
      setBoardMeta((prev) => ({ ...prev, [id]: json.board }));
    } finally {
      boardMetaLoading.current.delete(id);
    }
  }

  async function refreshMondayStatus() {
    try {
      const status = await fetchMondayStatus();
      const ok = !!status?.connected;
      setLocalConnected(ok);
      setMondayConnected?.(ok);
      setMeta(status || null);

      if (ok) {
        const saved = mondayPrefsShape(mondayPrefs);
        setBoardIds(saved.boardIds);
        setDefaultBoardId(saved.defaultBoardId);
        setDefaultGroupId(saved.defaultGroupId);
        setColumnMaps(saved.columnMaps);
        setLookbackDays(saved.lookbackDays);
        hydrated.current = true;
        await loadMondayBoards();
      } else {
        setBoards([]);
        setBoardsLoaded(false);
      }
      return ok;
    } catch {
      setLocalConnected(false);
      setMondayConnected?.(false);
      setMeta(null);
      setBoards([]);
      setBoardsLoaded(false);
      return false;
    } finally {
      hydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshMondayStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read the columns and groups of the board on screen and of the default
  // board (its groups), once per visit. Usually the same board.
  React.useEffect(() => {
    if (!localConnected) return;
    for (const id of [editId, defaultBoardId]) {
      if (id && !boardMeta[id] && !boardMetaErrors[id]) loadBoardMeta(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localConnected, editId, defaultBoardId, boardMeta, boardMetaErrors]);

  function openMondayOAuth() {
    setSyncMsg("");
    const popup = window.open(
      `${apiBase}/api/monday/oauth/start?returnTo=/control-room`,
      "_blank",
      "width=640,height=760",
    );
    watchOAuthPopup(popup, {
      fetchStatus: fetchMondayStatus,
      before: mondayStatusKey(meta),
      statusKey: mondayStatusKey,
      onDone: async () => {
        // Columns read through the earlier grant may be stale.
        setBoardMeta({});
        setBoardMetaErrors({});
        await refreshMondayStatus();
      },
    });
  }

  async function disconnectMondayAccount() {
    const confirmed = await confirmDisconnect(
      "Disconnect monday.com for this workspace?",
      "BossDrop's access is revoked in monday.com too.",
    );
    if (!confirmed) return;
    setDisconnecting(true);
    setSyncMsg("");
    try {
      const r = await fetch(`${apiBase}/api/monday/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("monday_disconnect_failed");
      setLocalConnected(false);
      setMondayConnected?.(false);
      setMeta(null);
      setBoards([]);
      setBoardsLoaded(false);
      setBoardIds([]);
      setDefaultBoardId("");
      setDefaultGroupId("");
      setColumnMaps({});
      setLookbackDays(PM_DEFAULT_LOOKBACK_DAYS);
      setMappingId("");
      setBoardMeta({});
      setBoardMetaErrors({});
      setMondayPrefs?.(null);
      setMondayData?.([]);
    } catch {
      toast.error("Couldn't disconnect monday.com. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }

  // The server reads the boards and column maps from the workspace owner's
  // saved settings, so this syncs whatever the autosave last stored.
  async function syncMondayNow() {
    if (!boardIds.length) {
      setSyncMsg(MONDAY_ERRORS.board_required);
      return;
    }
    setLoading(true);
    setSyncMsg("");
    try {
      const { ok, json } = await getConnectorJson(`${apiBase}/api/monday/tasks`);
      if (!ok) {
        setSyncMsg(
          MONDAY_ERRORS[json?.error] ||
            "monday.com sync failed. Check server logs.",
        );
        return;
      }
      const tasks = Array.isArray(json?.tasks) ? json.tasks : [];
      setMondayData?.(tasks);
      setSyncMsg(
        tasks.length
          ? `${countLabel(tasks.length, "monday.com item", "monday.com items")} loaded for AI context.`
          : "No matching monday.com items on the selected boards.",
      );
    } finally {
      setLoading(false);
    }
  }

  function toggleBoard(id) {
    const key = String(id);
    const selected = boardIds.includes(key);
    if (!selected && boardIds.length >= MAX_MONDAY_BOARDS) return;
    const next = selected
      ? boardIds.filter((boardId) => boardId !== key)
      : [...boardIds, key];
    setBoardIds(next);
    // New items need a default board: keep it inside the selection. A group
    // belongs to one board, so it resets with it.
    if (!next.includes(defaultBoardId)) {
      setDefaultBoardId(next[0] || "");
      setDefaultGroupId("");
    }
    if (selected) {
      setColumnMaps((prev) => {
        if (!prev[key]) return prev;
        const rest = { ...prev };
        delete rest[key];
        return rest;
      });
    } else {
      // Show the columns BossDrop picked for it, for the admin to confirm.
      setMappingId(key);
    }
  }

  function changeDefaultBoard(value) {
    const next = String(value || "");
    if (next === defaultBoardId) return;
    setDefaultBoardId(next);
    setDefaultGroupId("");
  }

  // Edits the saved picks for the board on screen. "" and [] stay "pick
  // automatically", so a board that gains a better column later still gets it.
  function editColumns(change) {
    if (!editId) return;
    setColumnMaps((prev) => {
      const nextMap = mondayMapShape(
        change({ ...EMPTY_MONDAY_MAP, ...(prev[editId] || {}) }),
      );
      const out = { ...prev };
      if (nextMap) out[editId] = nextMap;
      else delete out[editId];
      return out;
    });
  }

  function resetColumns() {
    if (!editId) return;
    setColumnMaps((prev) => {
      if (!prev[editId]) return prev;
      const rest = { ...prev };
      delete rest[editId];
      return rest;
    });
  }

  // A new status column has its own labels, so its label picks start over.
  function changeStatusColumn(value) {
    editColumns((map) => ({
      ...map,
      status: value,
      doneLabels: [],
      inProgressLabel: "",
      blockedLabels: [],
    }));
  }

  // Ticking starts from the labels in use now. Unticking the last one goes
  // back to the labels monday.com itself marks as done (or Stuck).
  function toggleLabel(field, label) {
    const current = Array.isArray(viewMap?.[field]) ? viewMap[field] : [];
    const next = current.includes(label)
      ? current.filter((item) => item !== label)
      : [...current, label];
    editColumns((map) => ({ ...map, [field]: next }));
  }

  // The saved pick for a field while that column still exists, else "" (auto).
  function savedColumnValue(field, types) {
    const col = columnById.get(String(savedColumns?.[field] || ""));
    return col && types.includes(col.type) ? col.id : "";
  }

  // Columns of the given types, led by "Auto (<what auto picks>)".
  function columnOptions(field, types) {
    const autoCol = columnById.get(autoMap?.[field] || "");
    return [
      {
        value: "",
        label: `Auto (${autoCol ? autoCol.title || "Untitled column" : "none"})`,
      },
      ...(Array.isArray(editBoard?.columns) ? editBoard.columns : [])
        .filter((c) => types.includes(c.type))
        .map((c) => ({ value: c.id, label: c.title || "Untitled column" })),
    ];
  }

  function columnField(field, label, types) {
    return (
      <div className="crField" key={field}>
        <div className="crFieldLabel">{label}</div>
        <FancySelect
          value={savedColumnValue(field, types)}
          onChange={(value) =>
            field === "status"
              ? changeStatusColumn(value)
              : editColumns((map) => ({ ...map, [field]: value }))
          }
          options={columnOptions(field, types)}
          ariaLabel={`monday.com ${label.toLowerCase()} column`}
          usePortal
        />
      </div>
    );
  }

  React.useEffect(() => {
    if (!localConnected || !hydrated.current) return;
    const currentPrefs = mondayPrefsShape(mondayPrefs);
    const nextPrefs = mondayPrefsShape({
      boardIds,
      defaultBoardId,
      defaultGroupId,
      columnMaps,
      lookbackDays,
    });
    if (JSON.stringify(nextPrefs) === JSON.stringify(currentPrefs)) {
      setSaveMsg((prev) => (prev === "Saving..." ? "Saved." : prev));
      return;
    }
    setSaveMsg("Saving...");
    setMondayPrefsRef.current?.(nextPrefs);
    const timer = setTimeout(() => setSaveMsg("Saved."), 300);
    return () => clearTimeout(timer);
  }, [
    localConnected,
    boardIds,
    defaultBoardId,
    defaultGroupId,
    columnMaps,
    lookbackDays,
    mondayPrefs,
  ]);

  return (
    <div className="panelSub">
      <h3 className="crCardHead">
        <span className="crCardHead__title">
          <MondayLogo />
          monday.com
        </span>
        {localConnected ? (
          <span
            className="chip ok"
            style={{ marginLeft: "auto" }}
            title="Connection status"
          >
            <span className="chipDot" />
            Connected
          </span>
        ) : null}
      </h3>

      {!localConnected ? (
        <>
          <div
            className="muted"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>Not connected</span>
            <button
              className="btn primary"
              onClick={openMondayOAuth}
              disabled={notConfigured}
            >
              Connect monday.com
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 8 }}>
            {notConfigured
              ? MONDAY_ERRORS.monday_not_configured
              : "Connect as a monday.com account admin: monday asks them to install BossDrop first. Anyone else stops on monday's \"App is not installed\" page until an admin has installed it. BossDrop's comments and changes show up under the name of the person who connected it."}
          </div>
        </>
      ) : (
        <>
          <div className="formRow" style={{ alignItems: "center" }}>
            <div
              className="muted"
              style={{
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {meta?.orgName ? `Connected to ${meta.orgName}` : "Connected"}
              {meta?.connected ? " (acts as the person who connected it)" : ""}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                className="btn"
                onClick={syncMondayNow}
                disabled={loading || disconnecting}
              >
                {loading ? "Syncing..." : "Sync now"}
              </button>
              <button
                className="btn btnDanger"
                onClick={disconnectMondayAccount}
                disabled={loading || disconnecting}
                title="Disconnect monday.com from BossDrop"
              >
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>

          {meta?.needsReconnect ? (
            <ReconnectNote label="monday.com" onReconnect={openMondayOAuth} />
          ) : null}

          <div className="crFieldRow" style={{ marginTop: 10 }}>
            <div className="crField">
              <span className="crFieldLabel">
                Boards BossDrop reads{" "}
                <span className="muted small">({boardIds.length})</span>
              </span>
              {boards.length > 8 ? (
                <input
                  className="inputFancy"
                  placeholder="Filter boards..."
                  value={boardFilter}
                  onChange={(e) => setBoardFilter(e.target.value)}
                  aria-label="Filter monday.com boards"
                  style={{ fontSize: 16, minHeight: 44 }}
                />
              ) : null}
              <PickList
                items={boardItems}
                selected={boardIds}
                onToggle={toggleBoard}
                max={MAX_MONDAY_BOARDS}
                ariaLabel="monday.com boards BossDrop reads"
                errorText={boardsMsg}
                onRetry={loadMondayBoards}
                emptyText={
                  boardsLoaded
                    ? boards.length
                      ? "No boards match that filter."
                      : "No boards found for the person who connected monday.com."
                    : "Loading boards..."
                }
              />
            </div>

            <div className="crField">
              <div className="crFieldLabel">Default board for new items</div>
              <FancySelect
                value={defaultBoardId}
                onChange={changeDefaultBoard}
                options={chosenOptions}
                placeholder="Pick a board"
                ariaLabel="Default monday.com board for new items"
                disabled={!boardIds.length}
                emptyText="Pick a board to read first"
                usePortal
              />
              <div className="crFieldLabel" style={{ marginTop: 6 }}>
                Group for new items
              </div>
              <FancySelect
                value={defaultGroupId}
                onChange={(value) => setDefaultGroupId(value)}
                options={groupOptions}
                placeholder={
                  defaultBoardMeta ? "Pick a group" : "Loading groups..."
                }
                ariaLabel="monday.com group for new items"
                disabled={!defaultBoardId || !defaultBoardMeta}
                usePortal
              />
              {defaultBoardId && boardMetaErrors[defaultBoardId] ? (
                <RetryNote
                  text={boardMetaErrors[defaultBoardId]}
                  onRetry={() => loadBoardMeta(defaultBoardId)}
                />
              ) : null}
            </div>

            <LookbackField
              value={lookbackDays}
              onChange={setLookbackDays}
              noun="items"
            />
          </div>

          {editId ? (
            <div className="panelSub" style={{ marginTop: 10, display: "grid", gap: 8 }}>
              <div className="crFieldLabel">Columns BossDrop uses</div>
              {boardIds.length > 1 ? (
                <FancySelect
                  value={editId}
                  onChange={(value) => setMappingId(value)}
                  options={chosenOptions}
                  ariaLabel="monday.com board whose columns are shown"
                  usePortal
                />
              ) : null}
              {boardMetaErrors[editId] ? (
                <RetryNote
                  text={boardMetaErrors[editId]}
                  onRetry={() => loadBoardMeta(editId)}
                />
              ) : !viewMap ? (
                <div className="muted">Reading columns...</div>
              ) : (
                <>
                  <div className="muted small">
                    Auto picks a column by its type and name, and follows the
                    board if its columns change. Pick one to pin it.{" "}
                    {savedColumns ? (
                      <button
                        type="button"
                        style={LINK_BUTTON_STYLE}
                        onClick={resetColumns}
                      >
                        Set every column back to Auto
                      </button>
                    ) : null}
                  </div>
                  {!viewMap.status ? (
                    <div className="muted small">
                      This board has no status column, so BossDrop can&apos;t
                      tell open items from finished ones on it.
                    </div>
                  ) : null}
                  <div className="crFieldRow">
                    {columnField("status", "Status", ["status"])}
                    {viewMap.status ? (
                      <>
                        <div className="crField">
                          <span className="crFieldLabel">Counts as done</span>
                          <PickList
                            items={statusLabels.map((label) => ({
                              id: label,
                              label,
                            }))}
                            selected={viewMap.doneLabels}
                            onToggle={(label) => toggleLabel("doneLabels", label)}
                            max={20}
                            maxHeight={180}
                            ariaLabel="monday.com status labels that count as done"
                            emptyText="This column has no labels yet."
                          />
                          <span className="muted small">
                            With none ticked, BossDrop uses the labels
                            monday.com marks as done.
                          </span>
                        </div>
                        <div className="crField">
                          <div className="crFieldLabel">In progress</div>
                          <FancySelect
                            value={viewMap.inProgressLabel}
                            onChange={(value) =>
                              editColumns((map) => ({
                                ...map,
                                inProgressLabel: value,
                              }))
                            }
                            options={statusLabels.map((label) => ({
                              value: label,
                              label,
                            }))}
                            placeholder="None"
                            ariaLabel="monday.com status label for work in progress"
                            usePortal
                          />
                        </div>
                        <div className="crField">
                          <span className="crFieldLabel">Counts as blocked</span>
                          <PickList
                            items={statusLabels.map((label) => ({
                              id: label,
                              label,
                            }))}
                            selected={viewMap.blockedLabels}
                            onToggle={(label) =>
                              toggleLabel("blockedLabels", label)
                            }
                            max={20}
                            maxHeight={180}
                            ariaLabel="monday.com status labels that count as blocked"
                            emptyText="This column has no labels yet."
                          />
                        </div>
                      </>
                    ) : null}
                    {columnField("people", "Owner", ["people"])}
                    {columnField("date", "Due date", ["date", "timeline"])}
                    {columnField("priority", "Priority", ["status"])}
                    {columnField("dependency", "Dependencies", ["dependency"])}
                    {columnField("notes", "Description", ["long_text"])}
                  </div>
                </>
              )}
            </div>
          ) : null}

          {saveMsg ? (
            <div className="muted" aria-live="polite" style={{ marginTop: 8 }}>
              {saveMsg}
            </div>
          ) : null}

          {syncMsg ? (
            <div className="muted" style={{ marginTop: 8 }}>
              {syncMsg}
            </div>
          ) : null}

          <p className="muted" style={{ marginTop: 8 }}>
            BossDrop reads open and recently updated items from the selected
            boards, and can create items on the default board and update
            existing ones when monday.com is the task tool. monday.com limits
            how many API calls an account gets a day, so new items can take up
            to 15 minutes to show up here (30 minutes with more than 6 boards
            selected).
          </p>
        </>
      )}
    </div>
  );
});

/* ---------- GITHUB ---------- */
// Same caps as sanitizeGitHubPrefs in backend/server/githubClient.js.
const MAX_GITHUB_REPOS = 10;
const GITHUB_DEFAULT_STALE_DAYS = 3;
const GITHUB_MAX_QUESTION_CHARS = 1000;
// Uninstalling or changing repo access happens on GitHub; disconnecting here
// only drops this workspace's binding.
const GITHUB_INSTALLATIONS_URL = "https://github.com/settings/installations";

// Error codes /api/github/* may return, in words. Anything else falls back to
// the caller's generic message.
const GITHUB_ERRORS = {
  github_not_configured: "GitHub isn't enabled on this BossDrop server yet.",
  github_rate_limited:
    "GitHub is rate limiting BossDrop. Try again in a minute.",
  github_code_search_rate_limited:
    "Code search allows a few questions a minute. Try again shortly.",
  github_code_disabled: "Code reading is turned off on this BossDrop server.",
  github_code_reading_off: "Code reading is off for this workspace.",
  github_code_reading_admins_only:
    "Only workspace admins can ask about the code.",
  github_repo_not_allowed:
    "Pick one of the repos selected above. A new pick can take a moment to save.",
  github_repo_required: "Pick a repo first.",
  github_question_required: "Type a question first.",
  github_install_removed:
    "BossDrop is no longer installed on that GitHub account. Install it again.",
  github_install_suspended:
    "BossDrop is suspended on GitHub. Unsuspend it there, then try again.",
  rate_limited: "Too many questions in a short time. Try again in a few minutes.",
};

// { repo: email } kept only for repos in `repos`, with an email-shaped value.
// The server re-checks both and that the email is on the roster.
function githubRepoOwnersShape(value, repos) {
  const src = value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const out = {};
  for (const repo of repos) {
    const email = String(src[repo] || "")
      .trim()
      .toLowerCase();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) out[repo] = email;
  }
  return out;
}

// The saved githubPrefs in the shape the server stores (sanitizeGitHubPrefs).
// Every field the server keeps must be here, or the autosave below drops it.
function githubPrefsShape(prefs) {
  const src = prefs && typeof prefs === "object" ? prefs : {};
  const repos = (Array.isArray(src.repos) ? src.repos : [])
    .map(String)
    .filter(Boolean)
    .slice(0, MAX_GITHUB_REPOS);
  return {
    repos,
    staleDays: clampInt(src.staleDays, 1, 60, GITHUB_DEFAULT_STALE_DAYS),
    // 'admins' (default) or 'off'. The server has no "everyone" setting.
    codeReading: src.codeReading === "off" ? "off" : "admins",
    // Who hears about a failing check or an unowned review on each repo.
    repoOwners: githubRepoOwnersShape(src.repoOwners, repos),
  };
}

// Which GitHub accounts are bound, for spotting a finished install.
function githubInstallsKey(status) {
  return JSON.stringify(
    (Array.isArray(status?.installs) ? status.installs : []).map((install) => [
      install?.account_login || "",
      install?.repository_selection || "",
      !!install?.suspended,
    ]),
  );
}

// File links in a code answer come from GitHub's API; only ever link to GitHub.
function isGitHubUrl(url) {
  return /^https:\/\/github\.com\//.test(String(url || ""));
}

const GitHubConnector = React.memo(function GitHubConnector({
  apiBase,
  githubConnected,
  setGithubConnected,
  githubPrefs,
  setGithubPrefs,
  teamMembers = [],
  canManageWorkspace,
}) {
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [localConnected, setLocalConnected] = React.useState(!!githubConnected);
  const [meta, setMeta] = React.useState(null);
  const [repos, setRepos] = React.useState([]);
  const [reposLoaded, setReposLoaded] = React.useState(false);
  const [reposMsg, setReposMsg] = React.useState("");
  const [repoFilter, setRepoFilter] = React.useState("");
  const [selectedRepos, setSelectedRepos] = React.useState(
    () => githubPrefsShape(githubPrefs).repos,
  );
  const [staleDays, setStaleDays] = React.useState(
    () => githubPrefsShape(githubPrefs).staleDays,
  );
  const [codeReading, setCodeReading] = React.useState(
    () => githubPrefsShape(githubPrefs).codeReading,
  );
  const [repoOwners, setRepoOwners] = React.useState(
    () => githubPrefsShape(githubPrefs).repoOwners,
  );
  const repoOwnerOptions = React.useMemo(
    () => [
      { value: "", label: "Workspace owner (default)" },
      ...(Array.isArray(teamMembers) ? teamMembers : [])
        .filter((member) => String(member?.email || "").includes("@"))
        .map((member) => ({
          value: String(member.email).trim().toLowerCase(),
          label: member.name ? `${member.name} (${member.email})` : member.email,
          icon: <Avatar name={member.name} email={member.email} size={22} />,
        })),
    ],
    [teamMembers],
  );
  const [saveMsg, setSaveMsg] = React.useState("");
  const [askRepo, setAskRepo] = React.useState("");
  const [askQuestion, setAskQuestion] = React.useState("");
  const [asking, setAsking] = React.useState(false);
  const [askResult, setAskResult] = React.useState(null);
  const [askErr, setAskErr] = React.useState("");
  const hydrated = React.useRef(false);
  const setGithubPrefsRef = React.useRef(setGithubPrefs);

  React.useEffect(() => {
    setGithubPrefsRef.current = setGithubPrefs;
  }, [setGithubPrefs]);

  const installs = Array.isArray(meta?.installs) ? meta.installs : [];
  const notConfigured = meta?.configured === false;
  const visibleRepos = React.useMemo(() => {
    const q = repoFilter.trim().toLowerCase();
    return (repos || []).filter((repo) =>
      q ? String(repo.full_name || "").toLowerCase().includes(q) : true,
    );
  }, [repos, repoFilter]);
  // The server only answers about repos in the saved allowlist, so the ask
  // box offers exactly those.
  const askRepoOptions = React.useMemo(
    () => selectedRepos.map((name) => ({ value: name, label: name })),
    [selectedRepos],
  );
  const askRepoValue = selectedRepos.includes(askRepo)
    ? askRepo
    : selectedRepos[0] || "";

  async function fetchGitHubStatus() {
    return fetch(`${apiBase}/api/github/connected`, {
      credentials: "include",
      cache: "no-store",
    }).then((r) => (r.ok ? r.json() : { connected: false, installs: [] }));
  }

  async function loadGitHubRepos({ fresh = false } = {}) {
    if (!fresh) setReposMsg("");
    try {
      const r = await fetch(`${apiBase}/api/github/repos${fresh ? "?fresh=1" : ""}`, {
        credentials: "include",
        cache: "no-store",
      });
      const json = await r.json().catch(() => null);
      if (!r.ok) {
        if (fresh) return [];
        setRepos([]);
        setReposLoaded(false);
        setReposMsg(
          GITHUB_ERRORS[json?.error] ||
            "Couldn't load repos from GitHub. Try again.",
        );
        return [];
      }
      const list = Array.isArray(json?.repos) ? json.repos : [];
      setRepos(list);
      setReposLoaded(true);
      return list;
    } catch {
      if (fresh) return [];
      setRepos([]);
      setReposLoaded(false);
      setReposMsg("Couldn't load repos from GitHub. Try again.");
      return [];
    }
  }

  useRefreshOnReturn(() => loadGitHubRepos({ fresh: true }), localConnected);

  async function refreshGitHubStatus() {
    try {
      const status = await fetchGitHubStatus();
      const ok = !!status?.connected;
      setLocalConnected(ok);
      setGithubConnected?.(ok);
      setMeta(status || null);

      if (ok) {
        const saved = githubPrefsShape(githubPrefs);
        setSelectedRepos(saved.repos);
        setStaleDays(saved.staleDays);
        setCodeReading(saved.codeReading);
        setRepoOwners(saved.repoOwners);
        await loadGitHubRepos();
      } else {
        setRepos([]);
        setReposLoaded(false);
      }
      return ok;
    } catch {
      setLocalConnected(false);
      setGithubConnected?.(false);
      setMeta(null);
      setRepos([]);
      setReposLoaded(false);
      return false;
    } finally {
      hydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshGitHubStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // mode "install" opens GitHub's install page; mode "link" only runs the
  // GitHub sign-in, to bind an installation that already exists. The poll
  // waits for the set of bound accounts to CHANGE, not just for "connected",
  // so adding a second account to a connected workspace keeps polling. The
  // popup closes itself on success; when some accounts were skipped it stays
  // open so its page can say why.
  function openGitHubFlow(mode = "install") {
    const before = githubInstallsKey(meta);
    const popup = window.open(
      `${apiBase}/api/github/oauth/start?returnTo=/settings${
        mode === "link" ? "&mode=link" : ""
      }`,
      "_blank",
      "width=1000,height=800",
    );
    watchSignInPopup(popup, {
      check: async () => {
        const status = await fetchGitHubStatus();
        return status?.connected && githubInstallsKey(status) !== before;
      },
      onDone: refreshGitHubStatus,
      keepPopupOpen: true,
    });
  }

  async function disconnectGitHubAccount() {
    const confirmed = await confirmDisconnect(
      "Disconnect GitHub from this workspace?",
      "BossDrop stays installed on GitHub until you uninstall it there.",
    );
    if (!confirmed) return;
    setDisconnecting(true);
    try {
      const r = await fetch(`${apiBase}/api/github/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("github_disconnect_failed");
      setLocalConnected(false);
      setGithubConnected?.(false);
      setMeta(null);
      setRepos([]);
      setReposLoaded(false);
      setSelectedRepos([]);
      setStaleDays(GITHUB_DEFAULT_STALE_DAYS);
      setCodeReading("admins");
      setRepoOwners({});
      setAskResult(null);
      setAskErr("");
      setGithubPrefs?.(null);
    } catch {
      toast.error("Couldn't disconnect GitHub. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }

  function toggleRepo(fullName) {
    const name = String(fullName);
    if (selectedRepos.includes(name)) {
      setSelectedRepos(selectedRepos.filter((repo) => repo !== name));
    } else if (selectedRepos.length < MAX_GITHUB_REPOS) {
      setSelectedRepos([...selectedRepos, name]);
    }
  }

  // One separate, tool-less AI call on the server. The answer is shown here
  // only; it never reaches the planner and cannot create actions.
  async function askAboutCode() {
    const question = askQuestion.trim();
    if (!askRepoValue) {
      setAskErr(GITHUB_ERRORS.github_repo_required);
      return;
    }
    if (!question) {
      setAskErr(GITHUB_ERRORS.github_question_required);
      return;
    }
    setAsking(true);
    setAskErr("");
    setAskResult(null);
    try {
      const r = await fetch(`${apiBase}/api/github/code/ask`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ repo: askRepoValue, question }),
      });
      const json = await r.json().catch(() => null);
      if (!r.ok || !json?.ok) {
        setAskErr(
          GITHUB_ERRORS[json?.error] ||
            "BossDrop couldn't answer that. Try again in a minute.",
        );
        return;
      }
      setAskResult({
        answer: String(json.answer || ""),
        files: Array.isArray(json.files) ? json.files : [],
      });
    } catch {
      setAskErr("BossDrop couldn't answer that. Try again in a minute.");
    } finally {
      setAsking(false);
    }
  }

  React.useEffect(() => {
    if (!localConnected || !hydrated.current) return;
    const currentPrefs = githubPrefsShape(githubPrefs);
    const nextPrefs = {
      repos: selectedRepos,
      staleDays: clampInt(staleDays, 1, 60, GITHUB_DEFAULT_STALE_DAYS),
      codeReading: codeReading === "off" ? "off" : "admins",
      repoOwners: githubRepoOwnersShape(repoOwners, selectedRepos),
    };
    if (JSON.stringify(nextPrefs) === JSON.stringify(currentPrefs)) {
      setSaveMsg((prev) => (prev === "Saving..." ? "Saved." : prev));
      return;
    }
    setSaveMsg("Saving...");
    setGithubPrefsRef.current?.(nextPrefs);
    const timer = setTimeout(() => setSaveMsg("Saved."), 300);
    return () => clearTimeout(timer);
  }, [
    localConnected,
    selectedRepos,
    staleDays,
    codeReading,
    repoOwners,
    githubPrefs,
  ]);

  return (
    <div className="panelSub">
      <h3 className="crCardHead">
        <span className="crCardHead__title">
          <GitHubLogo />
          GitHub
        </span>
        {localConnected ? (
          <span
            className="chip ok"
            style={{ marginLeft: "auto" }}
            title="Connection status"
          >
            <span className="chipDot" />
            Connected
          </span>
        ) : null}
      </h3>

      {!localConnected ? (
        <>
          <div
            className="muted"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span>Not connected</span>
            <button
              className="btn primary"
              onClick={() => openGitHubFlow("install")}
              disabled={notConfigured}
            >
              Install on GitHub
            </button>
            <button
              className="btn"
              onClick={() => openGitHubFlow("link")}
              disabled={notConfigured}
              title="BossDrop is already installed on your GitHub account or organization"
            >
              Already installed? Link it
            </button>
          </div>
          <div className="muted small" style={{ marginTop: 8 }}>
            {notConfigured
              ? GITHUB_ERRORS.github_not_configured
              : "Read-only, and you choose which repos BossDrop can see. To connect an organization, an owner of that organization has to install or link it."}
          </div>
        </>
      ) : (
        <>
          <div className="formRow" style={{ alignItems: "center" }}>
            <div
              className="muted"
              style={{
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                overflowWrap: "anywhere",
              }}
            >
              {installs.length
                ? `Connected to ${installs
                    .map((install) => install.account_login)
                    .filter(Boolean)
                    .join(", ")}`
                : "Connected"}
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button
                className="btn btnDanger"
                onClick={disconnectGitHubAccount}
                disabled={disconnecting}
                title="Disconnect GitHub from BossDrop"
              >
                {disconnecting ? "Disconnecting..." : "Disconnect"}
              </button>
            </div>
          </div>

          <div className="crSubGroup">
            <h4 className="crSubTitle">GitHub accounts</h4>
            {installs.length ? (
              <div className="list">
                {installs.map((install, idx) => (
                  <div
                    key={`${install.account_login || "account"}:${idx}`}
                    className="listItem"
                    style={{ alignItems: "center", flexWrap: "wrap" }}
                  >
                    <span style={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                      {install.account_login || "GitHub account"}
                    </span>
                    <span className="muted small">
                      {install.account_type === "Organization"
                        ? "Organization"
                        : "Personal account"}
                      {" · "}
                      {install.repository_selection === "all"
                        ? "All repos"
                        : "Selected repos"}
                      {install.suspended ? " · Suspended on GitHub" : ""}
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginTop: 10,
              }}
            >
              <button
                className="btn"
                type="button"
                onClick={() => openGitHubFlow("install")}
              >
                Install on another account
              </button>
              <button
                className="btn"
                type="button"
                onClick={() => openGitHubFlow("link")}
              >
                Already installed? Link it
              </button>
              <a
                className="btn"
                href={GITHUB_INSTALLATIONS_URL}
                target="_blank"
                rel="noreferrer"
              >
                Manage repo access on GitHub
              </a>
            </div>
          </div>

          <div className="crFieldRow" style={{ marginTop: 10 }}>
            <div className="crField">
              <span className="crFieldLabel">
                Repos BossDrop reads{" "}
                <span className="muted small">
                  ({selectedRepos.length}/{MAX_GITHUB_REPOS})
                </span>
              </span>
              {repos.length > 8 ? (
                <input
                  className="inputFancy"
                  placeholder="Filter repos..."
                  value={repoFilter}
                  onChange={(e) => setRepoFilter(e.target.value)}
                  aria-label="Filter GitHub repos"
                  style={{ fontSize: 16, minHeight: 44 }}
                />
              ) : null}
              <div className="list" style={{ maxHeight: 240, overflow: "auto" }}>
                {visibleRepos.map((repo) => {
                  const name = String(repo.full_name || "");
                  const checked = selectedRepos.includes(name);
                  return (
                    <label
                      key={name}
                      className="listItem"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        className="bd-check"
                        checked={checked}
                        disabled={
                          !checked && selectedRepos.length >= MAX_GITHUB_REPOS
                        }
                        onChange={() => toggleRepo(name)}
                        style={{ width: 18, height: 18 }}
                      />
                      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                        {name}
                      </span>
                      {repo.private ? (
                        <span
                          className="muted small"
                          style={{ marginLeft: "auto", whiteSpace: "nowrap" }}
                        >
                          Private
                        </span>
                      ) : null}
                    </label>
                  );
                })}
                {!visibleRepos.length ? (
                  <div
                    className="muted"
                    style={{
                      padding: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <span>
                      {reposMsg ||
                        (reposLoaded
                          ? repos.length
                            ? "No repos match that filter."
                            : "No repos shared with BossDrop yet. Add some under Manage repo access on GitHub."
                          : "Loading repos...")}
                    </span>
                    {reposMsg ? (
                      <button
                        className="btn"
                        type="button"
                        onClick={loadGitHubRepos}
                      >
                        Try again
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            <label className="crField">
              <span className="crFieldLabel">Flag pull requests open for (days)</span>
              <input
                type="number"
                min={1}
                max={60}
                value={staleDays}
                onChange={(e) =>
                  setStaleDays(
                    clampInt(
                      e.target.value,
                      1,
                      60,
                      GITHUB_DEFAULT_STALE_DAYS,
                    ),
                  )
                }
                style={{ fontSize: 16, minHeight: 44, width: 140 }}
              />
              <span className="muted small">
                A pull request with no review after this many days counts as
                stale.
              </span>
            </label>

            {selectedRepos.length ? (
              <div className="crField">
                <div className="crFieldLabel">Repo owners</div>
                {selectedRepos.map((name) => (
                  <div
                    key={name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                      marginBottom: 6,
                    }}
                  >
                    <span
                      className="small"
                      style={{ minWidth: 0, flex: "1 1 140px", overflowWrap: "anywhere" }}
                    >
                      {name}
                    </span>
                    <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                      <FancySelect
                        value={repoOwners[name] || ""}
                        onChange={(value) =>
                          setRepoOwners((prev) => {
                            const next = { ...(prev || {}) };
                            if (value) next[name] = String(value);
                            else delete next[name];
                            return next;
                          })
                        }
                        options={repoOwnerOptions}
                        placeholder="Workspace owner (default)"
                        ariaLabel={`Owner of ${name}`}
                        usePortal
                      />
                    </div>
                  </div>
                ))}
                <span className="muted small">
                  Who hears about a failing check on the main branch, or a pull
                  request that only a GitHub team was asked to review. Add each
                  person's GitHub username in the team directory so BossDrop
                  can match reviewers.
                </span>
              </div>
            ) : null}

            {canManageWorkspace ? (
              <div className="crField">
                <div className="crFieldLabel">Code reading</div>
                <FancySelect
                  value={codeReading}
                  onChange={(value) =>
                    setCodeReading(value === "off" ? "off" : "admins")
                  }
                  options={[
                    { value: "admins", label: "Admins only" },
                    { value: "off", label: "Off" },
                  ]}
                  placeholder="Select..."
                  ariaLabel="Who can ask BossDrop about the code"
                  usePortal
                />
                <span className="muted small">
                  Lets workspace admins ask questions about the selected repos.
                  BossDrop reads a few files per question and never adds code
                  to automations.
                </span>
              </div>
            ) : null}
          </div>

          {saveMsg ? (
            <div className="muted" aria-live="polite" style={{ marginTop: 8 }}>
              {saveMsg}
            </div>
          ) : null}

          <p className="muted" style={{ marginTop: 8 }}>
            BossDrop reads pull requests waiting on review, failing checks,
            recent merges, and commit activity from the selected repos, so the
            AI can spot stuck work. Reviews waiting show up in daily status
            pings, and a check that stays red or a review that stalls is
            escalated. It never reads file contents for this and
            cannot change anything on GitHub.
          </p>

          {canManageWorkspace && codeReading !== "off" ? (
            <div className="crSubGroup">
              <h4 className="crSubTitle">Ask about the code</h4>
              {!selectedRepos.length ? (
                <div className="muted small">
                  Select at least one repo above to ask about its code.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  <div className="crFieldRow">
                    <div className="crField">
                      <div className="crFieldLabel">Repo</div>
                      <FancySelect
                        value={askRepoValue}
                        onChange={(value) => setAskRepo(value)}
                        options={askRepoOptions}
                        placeholder="Pick a repo"
                        ariaLabel="Repo to ask about"
                        usePortal
                      />
                    </div>
                  </div>
                  <label className="crField">
                    <span className="crFieldLabel">Question</span>
                    <textarea
                      className="textareaFancy"
                      rows={3}
                      maxLength={GITHUB_MAX_QUESTION_CHARS}
                      value={askQuestion}
                      onChange={(e) => setAskQuestion(e.target.value)}
                      placeholder="For example: where do we retry failed payment webhooks?"
                    />
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      className="btn primary"
                      type="button"
                      onClick={askAboutCode}
                      disabled={asking || !askQuestion.trim()}
                    >
                      {asking ? "Reading the code..." : "Ask"}
                    </button>
                    <span className="muted small">
                      Only you see the answer. Secret and key files are
                      skipped, and tokens are redacted.
                    </span>
                  </div>
                  {askErr ? (
                    <div className="muted" role="alert">
                      {askErr}
                    </div>
                  ) : null}
                  {askResult ? (
                    <div className="list" style={{ padding: 12 }}>
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {askResult.answer}
                      </div>
                      {askResult.files.length ? (
                        <div className="muted small" style={{ marginTop: 10 }}>
                          Files read:{" "}
                          {askResult.files.map((file, idx) => (
                            <React.Fragment key={`${file.path}:${idx}`}>
                              {idx ? ", " : ""}
                              {isGitHubUrl(file.url) ? (
                                <a
                                  href={file.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ textDecoration: "underline" }}
                                >
                                  {file.path}
                                </a>
                              ) : (
                                file.path
                              )}
                              {file.truncated ? " (first part only)" : ""}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
});
/* ---------- JIRA ---------- */
// Error codes /api/jira/* may return, in words. Anything else falls back to
// the caller's generic message. The grant belongs to the workspace (keyed by
// the owner, like Linear), so every admin sees the same connection.
const JIRA_ERRORS = {
  jira_not_configured: "Jira isn't enabled on this BossDrop server yet.",
  jira_not_connected: "Connect Jira first.",
  jira_reconnect_required: "Jira sign-in expired. Reconnect Jira.",
  jira_rate_limited: "Jira is rate limiting BossDrop. Try again in a minute.",
  jira_site_required: "Pick a Jira site first.",
  jira_project_required: "Pick a Jira project first.",
  jira_site_not_authorized:
    "BossDrop can't see that Jira site. Connect again and pick it on the Atlassian screen, or pick another site.",
  rate_limited: CONNECTOR_RATE_LIMITED,
};

// What changes on /api/jira/connected when a connect or a reconnect lands.
function jiraStatusKey(status) {
  return JSON.stringify([
    !!status?.connected,
    !!status?.needsReconnect,
    status?.accountName || "",
  ]);
}

/* ---------- Connectors Section ---------- */
export default function ConnectorsSection({
  // Asana
  asanaConnected,
  setAsanaConnected,
  setAsanaData,
  // Jira
  jiraConnected,
  setJiraConnected,
  setJiraData,
  // HubSpot
  hubspotConnected,
  setHubspotConnected,
  // Pipedrive (keyed by workspace owner, like HubSpot)
  pipedriveConnected,
  setPipedriveConnected,
  pipedrivePrefs,
  setPipedrivePrefs,
  // Linear
  linearConnected,
  setLinearConnected,
  setLinearData,
  linearPrefs,
  setLinearPrefs,
  // ClickUp, Notion and monday.com (keyed by workspace owner, like Linear)
  clickupConnected,
  setClickupConnected,
  setClickupData,
  clickupPrefs,
  setClickupPrefs,
  notionConnected,
  setNotionConnected,
  setNotionData,
  notionPrefs,
  setNotionPrefs,
  mondayConnected,
  setMondayConnected,
  setMondayData,
  mondayPrefs,
  setMondayPrefs,
  // GitHub
  githubConnected,
  setGithubConnected,
  githubPrefs,
  setGithubPrefs,
  // Roster, for picking a repo owner.
  teamMembers = [],
  // Admin-only controls (GitHub code reading) are hidden, not just disabled.
  canManageWorkspace = false,
  // Slack
  slackConnected,
  setSlackConnected,
  slackPrefs,
  setAsanaPrefs,
  setJiraPrefs,
  hubspotPrefs,
  setHubspotPrefs,
  setSlackPrefs,
  pullSlack,
  // Teams
  teamsConnected,
  setTeamsConnected,
  teamsPrefs,
  setTeamsPrefs,
  pullTeams,
  jiraPrefs,
  apiBase = API,
}) {
  /* ----------------- ASANA (logic) ----------------- */
  const [loading, setLoading] = React.useState(false);
  const [disconnecting, setDisconnecting] = React.useState(false);
  const [connected, setConnected] = React.useState(false);
  const [me, setMe] = React.useState(null);
  const [workspaces, setWorkspaces] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [ws, setWs] = React.useState("");
  const [proj, setProj] = React.useState("");
  const [saveMsg, setSaveMsg] = React.useState("");
  // { configured, connected, me, needsReconnect } from /api/asana/connected,
  // read from the WORKSPACE's grant (any admin's connect serves everyone).
  const [asanaMeta, setAsanaMeta] = React.useState(null);
  const [asanaMsg, setAsanaMsg] = React.useState("");
  const hydrated = React.useRef(false);
  const [jiraLoading, setJiraLoading] = React.useState(false);
  const [jiraDisconnecting, setJiraDisconnecting] = React.useState(false);
  const [jiraLocalConnected, setJiraLocalConnected] = React.useState(false);
  const [jiraMe, setJiraMe] = React.useState(null);
  const [jiraSites, setJiraSites] = React.useState([]);
  const [jiraProjects, setJiraProjects] = React.useState([]);
  const [jiraIssueTypes, setJiraIssueTypes] = React.useState([]);
  const [jiraSiteId, setJiraSiteId] = React.useState(jiraPrefs?.siteId || "");
  const [jiraProjectId, setJiraProjectId] = React.useState(
    jiraPrefs?.projectId || "",
  );
  const [jiraIssueTypeId, setJiraIssueTypeId] = React.useState(
    jiraPrefs?.issueTypeId || "",
  );
  const [jiraSaveMsg, setJiraSaveMsg] = React.useState("");
  // { configured, connected, accountName, needsReconnect } from /connected.
  const [jiraMeta, setJiraMeta] = React.useState(null);
  const [jiraSyncMsg, setJiraSyncMsg] = React.useState("");
  const jiraHydrated = React.useRef(false);
  const setJiraPrefsRef = React.useRef(setJiraPrefs);

  React.useEffect(() => {
    setJiraPrefsRef.current = setJiraPrefs;
  }, [setJiraPrefs]);

  const [teamsVerifiedConnected, setTeamsVerifiedConnected] =
    React.useState(false);
  const [slackVerifiedConnected, setSlackVerifiedConnected] =
    React.useState(false);

  const teamsTenantId = teamsPrefs?.tenantId || "";
  const teamsTeamId = teamsPrefs?.teamId || "";

  async function fetchAsanaStatus() {
    const r = await fetch(`${apiBase}/api/asana/connected`, {
      credentials: "include",
      cache: "no-store",
    });
    return r.ok ? r.json() : { connected: false };
  }

  // Loads the connection, then the pickers for the saved workspace. Used on
  // mount and after the OAuth popup finishes.
  async function refreshAsanaStatus() {
    try {
      const s = await fetchAsanaStatus();
      setAsanaMeta(s || null);
      if (s.connected) {
        setConnected(true);
        setAsanaConnected?.(true);
        setMe(s.me || null);

        const wsInit = s.prefs?.workspace_gid ?? "";
        const projInit = s.prefs?.project_gid ?? "";
        if (wsInit) setWs(wsInit);
        if (projInit) setProj(projInit);
        if (s.prefs?.workspace_gid || s.prefs?.project_gid) {
          setAsanaPrefs?.({
            workspace_gid: s.prefs?.workspace_gid || null,
            project_gid: s.prefs?.project_gid || null,
            project_name: s.prefs?.project_name || null,
          });
        }

        await loadWorkspaces();
        if (wsInit) await loadProjects(wsInit);
      } else {
        setConnected(false);
        setAsanaConnected?.(false);
      }
      return !!s.connected;
    } catch {
      setConnected(false);
      setAsanaConnected?.(false);
      return false;
    } finally {
      hydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshAsanaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Waits for the status to CHANGE (connect, or a reconnect clearing the
  // flag), so a reconnect does not close the popup before it is done.
  function openOAuth() {
    setAsanaMsg("");
    const popup = window.open(
      `${apiBase}/api/asana/oauth/start?returnTo=/settings`,
      "_blank",
      "width=520,height=700",
    );
    watchOAuthPopup(popup, {
      fetchStatus: fetchAsanaStatus,
      before: asanaStatusKey(asanaMeta),
      statusKey: asanaStatusKey,
      onDone: refreshAsanaStatus,
    });
  }

  async function loadWorkspaces() {
    const j = await fetch(`${apiBase}/api/asana/workspaces`, {
      credentials: "include",
    }).then((r) => r.json());
    setWorkspaces(j.workspaces || []);
  }

  async function loadProjects(workspaceGid, { fresh = false } = {}) {
    if (!workspaceGid) return;
    const r = await fetch(
      `${apiBase}/api/asana/projects?workspace=${encodeURIComponent(
        workspaceGid,
      )}`,
      { credentials: "include" },
    ).catch(() => null);
    if (fresh && !r?.ok) return;
    const j = await r.json();
    setProjects(j.projects || []);
  }

  async function syncNow() {
    if (!ws) {
      setAsanaMsg("Pick a workspace first.");
      return;
    }
    setLoading(true);
    setAsanaMsg("");
    try {
      const url = new URL(`${apiBase}/api/asana/tasks`);
      url.searchParams.set("workspace", ws);
      if (proj) url.searchParams.set("project", proj);
      const r = await fetch(url, { credentials: "include" });
      const j = await r.json().catch(() => null);
      if (!r.ok) throw new Error(String(j?.error || "asana_tasks_failed"));
      setAsanaData?.(j?.tasks || []);
    } catch (e) {
      setAsanaMsg(ASANA_MESSAGES[e?.message] || "Asana sync failed. Try again.");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (!connected || !hydrated.current) return;
    setSaveMsg("Saving…");
    setAsanaPrefs?.({ workspace_gid: ws || null, project_gid: proj || null });
    const t = setTimeout(() => setSaveMsg("Saved."), 300);
    return () => clearTimeout(t);
  }, [ws, proj, connected]);

  async function handleDisconnect() {
    if (!(await confirmDisconnect("Disconnect Asana for this workspace?"))) return;
    setDisconnecting(true);
    setAsanaMsg("");
    try {
      const r = await fetch(`${apiBase}/api/asana/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("asana_disconnect_failed");
      setConnected(false);
      setAsanaConnected?.(false);
      setMe(null);
      setAsanaMeta((prev) => ({ configured: prev?.configured !== false, connected: false }));
      setWorkspaces([]);
      setProjects([]);
      setWs("");
      setProj("");
      setAsanaData?.([]);
    } catch {
      setAsanaMsg("Couldn't disconnect Asana. Try again.");
    } finally {
      setDisconnecting(false);
    }
  }

  const wsOptions = React.useMemo(
    () => workspaces.map((w) => ({ value: w.gid, label: w.name })),
    [workspaces],
  );
  const projectOptions = React.useMemo(
    () => projects.map((p) => ({ value: p.gid, label: p.name })),
    [projects],
  );

  /* ----------------- JIRA (logic) ----------------- */
  // Every /api/jira route is keyed by the workspace owner on the server, and
  // /issues reads the site and project from the owner's saved prefs.
  async function loadJiraSites() {
    const { ok, json } = await getConnectorJson(`${apiBase}/api/jira/sites`);
    if (!ok) {
      setJiraSites([]);
      setJiraSyncMsg(JIRA_ERRORS[json?.error] || "Couldn't load Jira sites. Try again.");
      return [];
    }
    const sites = Array.isArray(json?.sites) ? json.sites : [];
    setJiraSites(sites);
    return sites;
  }

  async function loadJiraProjects(siteId, { fresh = false } = {}) {
    if (!siteId) {
      setJiraProjects([]);
      return [];
    }
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/jira/projects?site=${encodeURIComponent(siteId)}`,
    );
    if (!ok) {
      if (fresh) return [];
      setJiraProjects([]);
      setJiraSyncMsg(JIRA_ERRORS[json?.error] || "Couldn't load Jira projects. Try again.");
      return [];
    }
    const projects = Array.isArray(json?.projects) ? json.projects : [];
    setJiraProjects(projects);
    return projects;
  }

  async function loadJiraIssueTypes(siteId, projectId) {
    if (!siteId || !projectId) {
      setJiraIssueTypes([]);
      return [];
    }
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/jira/issue-types?site=${encodeURIComponent(siteId)}&project=${encodeURIComponent(projectId)}`,
    );
    if (!ok) {
      setJiraIssueTypes([]);
      setJiraSyncMsg(JIRA_ERRORS[json?.error] || "Couldn't load Jira issue types. Try again.");
      return [];
    }
    const issueTypes = Array.isArray(json?.issueTypes) ? json.issueTypes : [];
    setJiraIssueTypes(issueTypes);
    return issueTypes;
  }

  async function fetchJiraStatus() {
    const { ok, json } = await getConnectorJson(`${apiBase}/api/jira/connected`);
    return ok && json ? json : { connected: false };
  }

  // Loads the connection, then the pickers for the saved (or first) site and
  // project. Used on mount and after the OAuth popup finishes.
  async function refreshJiraStatus() {
    try {
      const s = await fetchJiraStatus();
      setJiraMeta(s);

      if (!s.connected) {
        setJiraLocalConnected(false);
        setJiraConnected?.(false);
        setJiraMe(null);
        return false;
      }

      setJiraLocalConnected(true);
      setJiraConnected?.(true);
      setJiraMe(s.me || (s.accountName ? { name: s.accountName } : null));
      setJiraSyncMsg("");

      const nextSiteId = jiraPrefs?.siteId || s?.prefs?.siteId || "";
      const nextProjectId = jiraPrefs?.projectId || s?.prefs?.projectId || "";
      const nextProjectKey = jiraPrefs?.projectKey || s?.prefs?.projectKey || "";
      const nextIssueTypeId =
        jiraPrefs?.issueTypeId || s?.prefs?.issueTypeId || "";

      if (!jiraPrefs && s?.prefs) {
        setJiraPrefs?.(s.prefs);
      }
      if (nextSiteId) setJiraSiteId(nextSiteId);
      if (nextProjectId) setJiraProjectId(nextProjectId);
      if (nextIssueTypeId) setJiraIssueTypeId(nextIssueTypeId);

      // A grant that covers exactly one site preselects it, the same way the
      // first project is preselected; with several, the admin picks.
      const sites = await loadJiraSites();
      const activeSiteId =
        nextSiteId || (sites.length === 1 && sites[0]?.id ? String(sites[0].id) : "");
      if (activeSiteId) {
        if (!nextSiteId) setJiraSiteId(activeSiteId);
        const projectList = await loadJiraProjects(activeSiteId);
        const activeProjectId =
          nextProjectId ||
          String(
            projectList.find(
              (project) => String(project.key || "") === String(nextProjectKey || ""),
            )?.id || "",
          ) ||
          (projectList[0]?.id ? String(projectList[0].id) : "");
        if (activeProjectId) setJiraProjectId(activeProjectId);
        if (activeProjectId) {
          await loadJiraIssueTypes(activeSiteId, activeProjectId);
        }
      }
      return true;
    } catch {
      setJiraLocalConnected(false);
      setJiraConnected?.(false);
      return false;
    } finally {
      jiraHydrated.current = true;
    }
  }

  React.useEffect(() => {
    refreshJiraStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Waits for the status to CHANGE (connect, or a reconnect clearing the
  // flag), so a reconnect does not close the popup before it is done.
  function openJiraOAuth() {
    setJiraSyncMsg("");
    const popup = window.open(
      `${apiBase}/api/jira/oauth/start?returnTo=/settings`,
      "_blank",
      "width=580,height=760",
    );
    watchOAuthPopup(popup, {
      fetchStatus: fetchJiraStatus,
      before: jiraStatusKey(jiraMeta),
      statusKey: jiraStatusKey,
      onDone: refreshJiraStatus,
    });
  }

  // The server reads the site and project from the workspace owner's saved
  // settings, so this syncs whatever the autosave last stored.
  async function syncJiraNow() {
    if (!jiraSiteId) {
      setJiraSyncMsg(JIRA_ERRORS.jira_site_required);
      return;
    }
    if (!(jiraProjectId || jiraPrefs?.projectKey)) {
      setJiraSyncMsg(JIRA_ERRORS.jira_project_required);
      return;
    }
    setJiraLoading(true);
    setJiraSyncMsg("");
    try {
      const { ok, json } = await getConnectorJson(`${apiBase}/api/jira/issues`);
      if (!ok) {
        setJiraSyncMsg(
          JIRA_ERRORS[json?.error] || "Jira sync failed. Check server logs.",
        );
        return;
      }
      const tasks = Array.isArray(json?.tasks) ? json.tasks : [];
      setJiraData?.(tasks);
      setJiraSyncMsg(
        tasks.length
          ? `${countLabel(tasks.length, "Jira issue", "Jira issues")} loaded for AI context.`
          : "No matching Jira issues in the selected project.",
      );
    } finally {
      setJiraLoading(false);
    }
  }

  async function disconnectJiraAccount() {
    const confirmed = await confirmDisconnect(
      "Disconnect Jira for this workspace?",
      "Atlassian gives BossDrop no way to revoke its own access, so also remove BossDrop under Connected apps in your Atlassian account settings.",
    );
    if (!confirmed) return;
    setJiraDisconnecting(true);
    setJiraSyncMsg("");
    try {
      const r = await fetch(`${apiBase}/api/jira/disconnect`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error("jira_disconnect_failed");
      setJiraLocalConnected(false);
      setJiraConnected?.(false);
      setJiraMe(null);
      setJiraMeta((prev) => ({ configured: prev?.configured !== false, connected: false }));
      setJiraSites([]);
      setJiraProjects([]);
      setJiraIssueTypes([]);
      setJiraSiteId("");
      setJiraProjectId("");
      setJiraIssueTypeId("");
      setJiraPrefs?.(null);
      setJiraData?.([]);
    } catch {
      setJiraSyncMsg("Couldn't disconnect Jira. Try again.");
    } finally {
      setJiraDisconnecting(false);
    }
  }

  React.useEffect(() => {
    if (!jiraLocalConnected || !jiraHydrated.current) return;
    const selectedSite =
      (jiraSites || []).find((site) => String(site.id) === jiraSiteId) || null;
    const selectedProject =
      (jiraProjects || []).find((project) => String(project.id) === jiraProjectId) ||
      null;
    const selectedIssueType =
      (jiraIssueTypes || []).find(
        (issueType) => String(issueType.id) === jiraIssueTypeId,
      ) || null;

    const nextPrefs = {
      siteId: jiraSiteId || null,
      siteName: selectedSite?.name || null,
      siteUrl: selectedSite?.url || null,
      projectId: jiraProjectId || null,
      projectKey: selectedProject?.key || jiraPrefs?.projectKey || null,
      projectName: selectedProject?.name || null,
      issueTypeId: jiraIssueTypeId || null,
      issueTypeName: selectedIssueType?.name || null,
    };
    const currentPrefs = {
      siteId:
        jiraPrefs && typeof jiraPrefs === "object" ? jiraPrefs.siteId || null : null,
      siteName:
        jiraPrefs && typeof jiraPrefs === "object" ? jiraPrefs.siteName || null : null,
      siteUrl:
        jiraPrefs && typeof jiraPrefs === "object" ? jiraPrefs.siteUrl || null : null,
      projectId:
        jiraPrefs && typeof jiraPrefs === "object" ? jiraPrefs.projectId || null : null,
      projectKey:
        jiraPrefs && typeof jiraPrefs === "object"
          ? jiraPrefs.projectKey || null
          : null,
      projectName:
        jiraPrefs && typeof jiraPrefs === "object"
          ? jiraPrefs.projectName || null
          : null,
      issueTypeId:
        jiraPrefs && typeof jiraPrefs === "object"
          ? jiraPrefs.issueTypeId || null
          : null,
      issueTypeName:
        jiraPrefs && typeof jiraPrefs === "object"
          ? jiraPrefs.issueTypeName || null
          : null,
    };
    if (JSON.stringify(nextPrefs) === JSON.stringify(currentPrefs)) {
      setJiraSaveMsg((prev) => (prev === "Saving…" ? "Saved." : prev));
      return;
    }

    setJiraSaveMsg("Saving…");
    setJiraPrefsRef.current?.(nextPrefs);
    const t = setTimeout(() => setJiraSaveMsg("Saved."), 300);
    return () => clearTimeout(t);
  }, [
    jiraLocalConnected,
    jiraSiteId,
    jiraProjectId,
    jiraIssueTypeId,
    jiraSites,
    jiraProjects,
    jiraIssueTypes,
    jiraPrefs?.siteId,
    jiraPrefs?.siteName,
    jiraPrefs?.siteUrl,
    jiraPrefs?.projectId,
    jiraPrefs?.projectKey,
    jiraPrefs?.projectName,
    jiraPrefs?.issueTypeId,
    jiraPrefs?.issueTypeName,
  ]);

  const jiraSiteOptions = React.useMemo(
    () =>
      (jiraSites || []).map((site) => ({
        value: String(site.id),
        label: site.url ? `${site.name} (${site.url})` : site.name,
      })),
    [jiraSites],
  );
  const jiraProjectOptions = React.useMemo(
    () =>
      (jiraProjects || []).map((project) => ({
        value: String(project.id),
        label: project.key ? `${project.name} (${project.key})` : project.name,
      })),
    [jiraProjects],
  );
  const jiraIssueTypeOptions = React.useMemo(
    () =>
      (jiraIssueTypes || []).map((issueType) => ({
        value: String(issueType.id),
        label: issueType.name,
      })),
    [jiraIssueTypes],
  );

  /* ----------------- SLACK (logic) ----------------- */
  const [slackLoading, setSlackLoading] = React.useState(false);
  const [channelList, setChannelList] = React.useState([]);
  const [pulling, setPulling] = React.useState(false);
  const [filterQ, setFilterQ] = React.useState("");
  const teamId = slackPrefs?.teamId || "";
  // A problem with the Team ID, shown under that field.
  const [slackFieldMsg, setSlackFieldMsg] = React.useState("");
  React.useEffect(() => setSlackFieldMsg(""), [teamId]);

  async function refreshSlackStatus() {
    if (!teamId) {
      setSlackVerifiedConnected(false);
      setSlackConnected(false);
      return false;
    }
    setSlackLoading(true);
    try {
      const s = await fetch(
        `${apiBase}/api/slack/connected?team=${encodeURIComponent(teamId)}`,
        { credentials: "include", cache: "no-store" },
      ).then((r) => r.json());

      const ok = !!s.connected;
      setSlackVerifiedConnected(ok);
      setSlackConnected(ok);
      if (ok && s.team_name && s.team_name !== slackPrefs.teamName) {
        setSlackPrefs({ ...slackPrefs, teamName: s.team_name });
      }

      if (ok) {
        const c = await fetch(
          `${apiBase}/api/slack/channels?team=${encodeURIComponent(teamId)}`,
          { credentials: "include" },
        ).then((r) => r.json());
        setChannelList(c.channels || []);
      } else {
        setChannelList([]);
      }
      return ok;
    } catch {
      setSlackVerifiedConnected(false);
      setSlackConnected(false);
      return false;
    } finally {
      setSlackLoading(false);
    }
  }

  function startSlackOAuth() {
    if (!teamId) {
      setSlackFieldMsg("Enter your Slack Workspace Team ID first.");
      return;
    }
    const returnTo = "/settings";
    const pop = window.open(
      `${apiBase}/api/slack/oauth/start?returnTo=${encodeURIComponent(
        returnTo,
      )}`,
      "_blank",
      "width=600,height=750",
    );
    watchSignInPopup(pop, { intervalMs: 900, check: refreshSlackStatus });
  }

  async function slackDisconnect() {
    if (!teamId) return setSlackFieldMsg("Enter your Slack Workspace Team ID first.");
    if (!(await confirmDisconnect("Disconnect Slack from this workspace?"))) return;
    try {
      const r = await fetch(
        `${apiBase}/api/slack/disconnect?team=${encodeURIComponent(teamId)}`,
        { method: "DELETE", credentials: "include" },
      );
      // Clear the card only once the server removed the install; a refused or
      // failed disconnect leaves Slack connected and says so.
      if (!r.ok) {
        toast.error(
          r.status === 403
            ? "Only a workspace admin can disconnect Slack, and only for a Slack workspace connected here."
            : "Couldn't disconnect Slack. Try again.",
        );
        return;
      }
      setSlackVerifiedConnected(false);
      setSlackConnected(false);
      setChannelList([]);
      setSlackPrefs({
        ...slackPrefs,
        teamName: "",
        includeChannels: [],
        excludeChannels: [],
      });
    } catch {
      toast.error("Couldn't disconnect Slack. Try again.");
    }
  }

  function toggleInclude(id) {
    const inc = new Set(slackPrefs.includeChannels || []);
    const exc = new Set(slackPrefs.excludeChannels || []);
    if (inc.has(id)) {
      inc.delete(id);
    } else {
      inc.add(id);
      exc.delete(id);
    }
    setSlackPrefs({
      ...slackPrefs,
      includeChannels: Array.from(inc),
      excludeChannels: Array.from(exc),
    });
  }
  function toggleExclude(id) {
    const inc = new Set(slackPrefs.includeChannels || []);
    const exc = new Set(slackPrefs.excludeChannels || []);
    if (exc.has(id)) {
      exc.delete(id);
    } else {
      exc.add(id);
      inc.delete(id);
    }
    setSlackPrefs({
      ...slackPrefs,
      includeChannels: Array.from(inc),
      excludeChannels: Array.from(exc),
    });
  }

  async function pullSlackNow(append = false) {
    setPulling(true);
    try {
      await pullSlack?.({ append });
    } finally {
      setPulling(false);
    }
  }

  React.useEffect(() => {
    if (teamId) refreshSlackStatus();
    else setSlackVerifiedConnected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!teamsTenantId || !teamsTeamId) {
        setTeamsVerifiedConnected(false);
        setTeamsConnected?.(false);
        return;
      }
      try {
        const s = await fetch(
          `${apiBase}/api/teams/connected?tenant=${encodeURIComponent(
            teamsTenantId,
          )}&team=${encodeURIComponent(teamsTeamId)}`,
          { credentials: "include", cache: "no-store" },
        ).then((r) => (r.ok ? r.json() : { connected: false }));
        if (cancelled) return;
        const ok = !!s.connected;
        setTeamsVerifiedConnected(ok);
        setTeamsConnected?.(ok);
      } catch {
        if (cancelled) return;
        setTeamsVerifiedConnected(false);
        setTeamsConnected?.(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, teamsTenantId, teamsTeamId]);

  const includeSet = React.useMemo(
    () => new Set(slackPrefs.includeChannels || []),
    [slackPrefs.includeChannels],
  );
  const excludeSet = React.useMemo(() => {
    const s = new Set(slackPrefs.excludeChannels || []);
    for (const id of includeSet) s.delete(id);
    return s;
  }, [slackPrefs.excludeChannels, includeSet]);

  const q = filterQ.trim().toLowerCase();
  const visible = React.useMemo(
    () =>
      (channelList || []).filter((c) =>
        q ? c.name.toLowerCase().includes(q) : true,
      ),
    [channelList, q],
  );
  const includeCandidates = React.useMemo(
    () => visible.filter((c) => !excludeSet.has(c.id)),
    [visible, excludeSet],
  );
  const excludeCandidates = React.useMemo(
    () => visible.filter((c) => !includeSet.has(c.id)),
    [visible, includeSet],
  );

  /* ---------- NEW: selectable connector list ---------- */
  const CONNECTORS = React.useMemo(
    () => [
      { key: "teams", label: "Microsoft Teams", logo: teamsLogo },
      { key: "asana", label: "Asana", logo: asanaLogo },
      { key: "jira", label: "Jira", logo: jiraLogo },
      { key: "linear", label: "Linear", glyph: <LinearLogo /> },
      { key: "clickup", label: "ClickUp", glyph: <ClickUpLogo /> },
      { key: "notion", label: "Notion", glyph: <NotionLogo /> },
      { key: "monday", label: "monday.com", glyph: <MondayLogo /> },
      { key: "hubspot", label: "HubSpot", logo: hubspotLogo },
      { key: "pipedrive", label: "Pipedrive", glyph: <PipedriveLogo /> },
      { key: "github", label: "GitHub", glyph: <GitHubLogo /> },
      { key: "slack", label: "Slack", logo: slackLogo },
    ],
    [],
  );

  const [show, setShow] = React.useState(() => {
    const connectedDefaults = {
      teams: false,
      asana: false,
      jira: false,
      linear: false,
      clickup: false,
      notion: false,
      monday: false,
      hubspot: false,
      pipedrive: false,
      github: false,
      slack: false,
    };
    try {
      const saved = JSON.parse(
        sessionStorage.getItem("bd:selectedConnectors") || "null",
      );
      if (saved && typeof saved === "object") {
        return {
          teams: connectedDefaults.teams ? !!saved.teams : false,
          asana: connectedDefaults.asana ? !!saved.asana : false,
          jira: connectedDefaults.jira ? !!saved.jira : false,
          linear: connectedDefaults.linear ? !!saved.linear : false,
          clickup: connectedDefaults.clickup ? !!saved.clickup : false,
          notion: connectedDefaults.notion ? !!saved.notion : false,
          monday: connectedDefaults.monday ? !!saved.monday : false,
          hubspot: connectedDefaults.hubspot ? !!saved.hubspot : false,
          pipedrive: connectedDefaults.pipedrive ? !!saved.pipedrive : false,
          github: connectedDefaults.github ? !!saved.github : false,
          slack: connectedDefaults.slack ? !!saved.slack : false,
        };
      }
    } catch {}
    return connectedDefaults;
  });

  // Persist selection
  React.useEffect(() => {
    try {
      const persisted = {
        teams: teamsVerifiedConnected ? !!show.teams : false,
        asana: connected ? !!show.asana : false,
        jira: jiraLocalConnected ? !!show.jira : false,
        linear: linearConnected ? !!show.linear : false,
        clickup: clickupConnected ? !!show.clickup : false,
        notion: notionConnected ? !!show.notion : false,
        monday: mondayConnected ? !!show.monday : false,
        hubspot: hubspotConnected ? !!show.hubspot : false,
        pipedrive: pipedriveConnected ? !!show.pipedrive : false,
        github: githubConnected ? !!show.github : false,
        slack: slackVerifiedConnected ? !!show.slack : false,
      };
      sessionStorage.setItem("bd:selectedConnectors", JSON.stringify(persisted));
    } catch {}
  }, [
    show,
    teamsVerifiedConnected,
    connected,
    jiraLocalConnected,
    linearConnected,
    clickupConnected,
    notionConnected,
    mondayConnected,
    hubspotConnected,
    pipedriveConnected,
    githubConnected,
    slackVerifiedConnected,
  ]);

  // If something becomes connected externally, auto-show it (without unchecking user choices)
  React.useEffect(() => {
    if (teamsVerifiedConnected) setShow((s) => ({ ...s, teams: true }));
  }, [teamsVerifiedConnected]);
  React.useEffect(() => {
    if (connected) setShow((s) => ({ ...s, asana: true }));
  }, [connected]);
  React.useEffect(() => {
    if (jiraLocalConnected) setShow((s) => ({ ...s, jira: true }));
  }, [jiraLocalConnected]);
  React.useEffect(() => {
    if (linearConnected) setShow((s) => ({ ...s, linear: true }));
  }, [linearConnected]);
  React.useEffect(() => {
    if (clickupConnected) setShow((s) => ({ ...s, clickup: true }));
  }, [clickupConnected]);
  React.useEffect(() => {
    if (notionConnected) setShow((s) => ({ ...s, notion: true }));
  }, [notionConnected]);
  React.useEffect(() => {
    if (mondayConnected) setShow((s) => ({ ...s, monday: true }));
  }, [mondayConnected]);
  React.useEffect(() => {
    if (hubspotConnected) setShow((s) => ({ ...s, hubspot: true }));
  }, [hubspotConnected]);
  React.useEffect(() => {
    if (pipedriveConnected) setShow((s) => ({ ...s, pipedrive: true }));
  }, [pipedriveConnected]);
  React.useEffect(() => {
    if (githubConnected) setShow((s) => ({ ...s, github: true }));
  }, [githubConnected]);
  React.useEffect(() => {
    if (slackVerifiedConnected) setShow((s) => ({ ...s, slack: true }));
  }, [slackVerifiedConnected]);

  function toggleConnector(key, checked) {
    setShow((s) => ({ ...s, [key]: !!checked }));
  }

  // Which connected connectors are expanded (connected ones collapse by default)
  const [expandedConnectors, setExpandedConnectors] = React.useState({});
  const toggleExpanded = (k) =>
    setExpandedConnectors((s) => ({ ...s, [k]: !s[k] }));

  // A custom automation's "Connect" button: that tool on screen and open, ready to
  // finish (components/CustomAutomations.js). Planner comes with Teams.
  React.useEffect(() => {
    const open = (event) => {
      const wanted = String(event?.detail?.key || "");
      const key = wanted === "planner" ? "teams" : wanted;
      const name = CONNECTOR_NAMES[key];
      if (name) {
        setShow((s) => ({ ...s, [key]: true }));
        setExpandedConnectors((s) => ({ ...s, [key]: true }));
      }
      setTimeout(() => {
        const target =
          (name && document.getElementById(connectorAnchor(name))) ||
          document.getElementById("cr-connectors");
        if (!target) return;
        target.scrollIntoView?.({ behavior: "smooth", block: "start" });
        // So it is clear which one the button meant.
        target.classList.add("connectorJumped");
        setTimeout(() => target.classList.remove("connectorJumped"), 2600);
      }, 60);
    };
    window.addEventListener("bd:open-connector", open);
    return () => window.removeEventListener("bd:open-connector", open);
  }, []);

  /* --------- Reconnect banner: integrations with an expired token --------- */
  const [reconnectConns, setReconnectConns] = React.useState([]);
  const loadConnectionHealth = React.useCallback(() => {
    fetch(`${apiBase}/api/connections/health`, {
      credentials: "include",
      cache: "no-store",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (j?.ok) setReconnectConns(Array.isArray(j.connections) ? j.connections : []);
      })
      .catch(() => {});
  }, [apiBase]);
  React.useEffect(() => {
    loadConnectionHealth();
    const id = setInterval(loadConnectionHealth, 60000);
    return () => clearInterval(id);
  }, [loadConnectionHealth]);

  function reconnectConnection(conn) {
    if (!conn?.reconnectPath) return;
    const returnTo = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    const acct = conn.account
      ? `email=${encodeURIComponent(conn.account)}&`
      : "";
    const popup = window.open(
      `${apiBase}${conn.reconnectPath}?${acct}returnTo=${returnTo}`,
      "_blank",
      "width=560,height=760",
    );
    // Poll until the token is healthy again (the row clears server-side), then
    // refresh the banner and close the popup.
    watchSignInPopup(popup, {
      intervalMs: 1500,
      check: async () => {
        const r = await fetch(`${apiBase}/api/connections/health`, {
          credentials: "include",
          cache: "no-store",
        });
        if (!r.ok) return false;
        const j = await r.json();
        const list = Array.isArray(j?.connections) ? j.connections : [];
        const stillBroken = list.some(
          (c) =>
            c.provider === conn.provider &&
            String(c.account || "").toLowerCase() ===
              String(conn.account || "").toLowerCase(),
        );
        if (stillBroken) return false;
        setReconnectConns(list);
        return true;
      },
      onDone: (landed) => {
        if (!landed) loadConnectionHealth();
      },
    });
  }

  // Slack's list, re-read quietly: the status check above can flag Slack as disconnected
  // on a network hiccup, which a person coming back to the page should not cause.
  async function reloadSlackChannels() {
    const { ok, json } = await getConnectorJson(
      `${apiBase}/api/slack/channels?team=${encodeURIComponent(teamId)}`,
    );
    if (ok && Array.isArray(json?.channels)) setChannelList(json.channels);
  }

  useRefreshOnReturn(() => loadProjects(ws, { fresh: true }), connected && !!ws);
  useRefreshOnReturn(
    () => loadJiraProjects(jiraSiteId, { fresh: true }),
    !!jiraConnected && !!jiraSiteId,
  );
  useRefreshOnReturn(reloadSlackChannels, slackVerifiedConnected && !!teamId);

  // The Automations card is on another tab of this page (the Control Room), so open
  // that tab and scroll to it; the link's own #cr-automations is the fallback.
  function goToAutomations(event) {
    const target = document.getElementById("cr-automations");
    if (!target) return;
    event.preventDefault();
    showControlRoomTab("automations");
    target.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }

  /* ----------------- RENDER ----------------- */
  return (
    <div id="cr-connectors" className="panelSub" style={{ display: "grid", gap: 12 }}>
      <h3 className="crTitle">Connectors</h3>
      {reconnectConns.length > 0 && (
        <div className="panelSub isWarn" style={{ display: "grid", gap: 8 }}>
          <div className="crCardHead__title">⚠️ Reconnection needed</div>
          <div className="muted small">
            These integrations stopped working because their sign-in expired.
            Reconnect to restore automations (calendar, email, CRM, chat,
            tasks).
          </div>
          {reconnectConns.map((conn) => (
            <div
              key={`${conn.provider}:${conn.account}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "grid", minWidth: 0 }}>
                <span style={{ fontWeight: 600 }}>{conn.label}</span>
                {conn.account && (
                  <span className="muted small">{conn.account}</span>
                )}
              </div>
              <button
                type="button"
                className="btn"
                style={{ flex: "0 0 auto" }}
                onClick={() => reconnectConnection(conn)}
              >
                Reconnect
              </button>
            </div>
          ))}
        </div>
      )}
      {/* Available connectors picker */}
      {/* Available connectors picker */}
      <div className="panelSub" style={{ display: "grid", gap: 8 }}>
        <h3 className="crSubTitle">Available connectors</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {CONNECTORS.map((c) => {
            const checked = !!show[c.key];
            return (
              <label
                key={c.key}
                className={`connectorPick crTile crOptionCard crOptionCard--inline ${
                  checked ? "is-selected" : ""
                }`}
                title={`Show ${c.label} connector`}
              >
                <input
                  type="checkbox"
                  className="bd-check" // ← same styling as Team Directory
                  checked={checked}
                  onChange={(e) => toggleConnector(c.key, e.target.checked)}
                />
                {c.glyph || <ToolGlyph src={c.logo} label={c.label} />}
                <span>{c.label}</span>
              </label>
            );
          })}
        </div>
      </div>
      {/* Which tool each automation writes to (task tool, chat tool,
          calendar) is picked on that automation's own card in the Control
          Room's Automations section (components/ToolChoiceField.js), not
          here. */}
      <div className="muted small">
        Choose which tool each automation uses on its card in{" "}
        <a
          href="#cr-automations"
          onClick={goToAutomations}
          style={{ color: "inherit", fontWeight: 600, textDecoration: "underline" }}
        >
          Control Room &gt; Automations
        </a>
        .
      </div>

      {/* TEAMS */}
      {show.teams && (
        <ConnectorFrame
          logo={<img src={teamsLogo} alt="" className="toolLogo" />}
          name="Microsoft Teams"
          connected={!!teamsVerifiedConnected}
          expanded={!!expandedConnectors.teams}
          onToggle={() => toggleExpanded("teams")}
        >
          <TeamsConnector
            apiBase={apiBase}
            teamsConnected={teamsConnected}
            setTeamsConnected={setTeamsConnected}
            teamsPrefs={teamsPrefs}
            setTeamsPrefs={setTeamsPrefs}
            pullTeams={pullTeams}
          />
        </ConnectorFrame>
      )}

      {/* ASANA */}
      {show.asana && (
        <ConnectorFrame
          logo={<img src={asanaLogo} alt="" className="toolLogo" />}
          name="Asana"
          connected={!!connected}
          account={me?.name || me?.email || undefined}
          expanded={!!expandedConnectors.asana}
          onToggle={() => toggleExpanded("asana")}
        >
        <div className="panelSub">
          <h3 className="crCardHead">
            <span className="crCardHead__title">
              <img src={asanaLogo} alt="" className="toolLogo" />
              Asana
            </span>
            {connected ? (
              <span
                className="chip ok"
                style={{ marginLeft: "auto" }}
                title="Connection status"
              >
                <span className="chipDot" />
                Connected
              </span>
            ) : null}
          </h3>

          {!connected ? (
            <>
              <div
                className="muted"
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <span>Not connected</span>
                <button
                  className="btn primary"
                  onClick={openOAuth}
                  disabled={asanaMeta?.configured === false}
                >
                  Connect Asana
                </button>
              </div>
              {asanaMeta?.configured === false ? (
                <div className="muted small" style={{ marginTop: 8 }}>
                  {ASANA_MESSAGES.asana_not_configured}
                </div>
              ) : null}
              {asanaMsg ? (
                <div className="muted small" style={{ marginTop: 8 }} aria-live="polite">
                  {asanaMsg}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className="formRow" style={{ alignItems: "center" }}>
                <div
                  className="muted"
                  style={{
                    minHeight: 44,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {me?.name ? `Connected as ${me.name}` : "Connected"}
                  {me?.email ? ` (${me.email})` : ""}
                </div>
                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                  <button
                    className="btn"
                    onClick={syncNow}
                    disabled={loading || disconnecting}
                  >
                    {loading ? "Syncing…" : "Sync now"}
                  </button>
                  <button
                    className="btn btnDanger"
                    onClick={handleDisconnect}
                    disabled={loading || disconnecting}
                    title="Disconnect Asana from BossDrop"
                  >
                    {disconnecting ? "Disconnecting…" : "Disconnect"}
                  </button>
                </div>
              </div>

              {asanaMeta?.needsReconnect ? (
                <ReconnectNote label="Asana" onReconnect={openOAuth} />
              ) : null}
              {/* GET /api/asana/connected taskPullMode: "projects" when the
                  workspace's plan has no search API. */}
              {asanaMeta?.taskPullMode === "projects" ? (
                <div className="muted small" style={{ marginTop: 8 }}>
                  Your Asana plan doesn't include search, so BossDrop reads your
                  projects one by one.
                </div>
              ) : null}
              {asanaMsg ? (
                <div className="muted small" style={{ marginTop: 8 }} aria-live="polite">
                  {asanaMsg}
                </div>
              ) : null}

              <div className="formRow" style={{ marginTop: 10 }}>
                <label style={{ display: "grid", gap: 6 }}>
                  Workspace
                  <FancySelect
                    value={ws}
                    onChange={async (val) => {
                      setWs(val);
                      setProj("");
                      await loadProjects(val);
                    }}
                    options={wsOptions}
                    placeholder="Pick a workspace"
                    ariaLabel="Select Asana workspace"
                    disabled={!connected}
                    emptyText="No workspaces found"
                  />
                </label>

                <label style={{ display: "grid", gap: 6 }}>
                  Project (optional)
                  <FancySelect
                    value={proj}
                    onChange={(val) => setProj(val)}
                    options={projectOptions}
                    placeholder="All projects in the workspace"
                    ariaLabel="Select Asana project"
                    disabled={!ws}
                    emptyText={
                      ws
                        ? "No projects in this workspace"
                        : "Pick a workspace first"
                    }
                  />
                </label>

                <div style={{ alignSelf: "end" }}>
                  {saveMsg ? (
                    <span
                      className="muted"
                      aria-live="polite"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {saveMsg}
                    </span>
                  ) : null}
                </div>
              </div>

              <p className="muted" style={{ marginTop: 8 }}>
                BossDrop uses open tasks (and due dates) to build the weekly
                report, detect risks, and drive scheduling.
              </p>
            </>
          )}
        </div>
        </ConnectorFrame>
      )}

      {/* JIRA */}
      {show.jira && (
        <ConnectorFrame
          logo={<img src={jiraLogo} alt="" className="toolLogo" />}
          name="Jira"
          connected={!!jiraLocalConnected}
          account={jiraMe?.name || jiraMe?.email || undefined}
          expanded={!!expandedConnectors.jira}
          onToggle={() => toggleExpanded("jira")}
        >
        <div className="panelSub jiraConnectorPanel">
          <h3 className="jiraConnectorPanel__header">
            <span className="jiraConnectorPanel__title crCardHead__title">
              <img src={jiraLogo} alt="" className="toolLogo" />
              Jira
            </span>
            {jiraLocalConnected ? (
              <span
                className="chip ok jiraConnectorPanel__status"
                title="Connection status"
              >
                <span className="chipDot" />
                Connected
              </span>
            ) : null}
          </h3>

          {!jiraLocalConnected ? (
            <>
              <div
                className="muted jiraConnectorPanel__empty"
                style={{ display: "flex", alignItems: "center", gap: 10 }}
              >
                <span>Not connected</span>
                <button
                  className="btn primary"
                  onClick={openJiraOAuth}
                  disabled={jiraMeta?.configured === false}
                >
                  Connect Jira
                </button>
              </div>
              <div className="muted small" style={{ marginTop: 8 }}>
                {jiraMeta?.configured === false
                  ? JIRA_ERRORS.jira_not_configured
                  : "Connect with an Atlassian account that can see the projects BossDrop should read, and pick the Jira site on the Atlassian screen. BossDrop's comments and edits show up under that person's name."}
              </div>
              {jiraSyncMsg ? (
                <div className="muted small" style={{ marginTop: 8 }} aria-live="polite">
                  {jiraSyncMsg}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div
                className="formRow jiraConnectorPanel__bar"
                style={{ alignItems: "center" }}
              >
                <div
                  className="muted jiraConnectorPanel__identity"
                  style={{
                    minHeight: 44,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flexWrap: "wrap",
                  }}
                >
                  {jiraMe?.name ? `Connected as ${jiraMe.name}` : "Connected"}
                  {" (acts as the person who connected it)"}
                </div>
                <div
                  className="jiraConnectorPanel__actions"
                  style={{ marginLeft: "auto", display: "flex", gap: 8 }}
                >
                  <button
                    className="btn"
                    onClick={syncJiraNow}
                    disabled={jiraLoading || jiraDisconnecting}
                  >
                    {jiraLoading ? "Syncing…" : "Sync now"}
                  </button>
                  <button
                    className="btn btnDanger"
                    onClick={disconnectJiraAccount}
                    disabled={jiraLoading || jiraDisconnecting}
                    title="Disconnect Jira account from BossDrop"
                  >
                    {jiraDisconnecting ? "Disconnecting…" : "Disconnect"}
                  </button>
                </div>
              </div>

              {jiraMeta?.needsReconnect ? (
                <ReconnectNote label="Jira" onReconnect={openJiraOAuth} />
              ) : null}

              <div className="formRow jiraConnectorPanel__grid" style={{ marginTop: 10 }}>
                <label
                  className="jiraConnectorPanel__field"
                  style={{ display: "grid", gap: 6 }}
                >
                  Jira site
                  <FancySelect
                    value={jiraSiteId}
                    onChange={async (value) => {
                      setJiraSiteId(value);
                      setJiraProjectId("");
                      setJiraIssueTypeId("");
                      setJiraProjects([]);
                      setJiraIssueTypes([]);
                      await loadJiraProjects(value);
                    }}
                    options={jiraSiteOptions}
                    placeholder="Pick a Jira site"
                    ariaLabel="Select Jira site"
                    disabled={!jiraLocalConnected}
                    emptyText="No Jira sites found"
                    usePortal
                  />
                </label>

                <label
                  className="jiraConnectorPanel__field"
                  style={{ display: "grid", gap: 6 }}
                >
                  Project
                  <FancySelect
                    value={jiraProjectId}
                    onChange={async (value) => {
                      setJiraProjectId(value);
                      setJiraIssueTypeId("");
                      await loadJiraIssueTypes(jiraSiteId, value);
                    }}
                    options={jiraProjectOptions}
                    placeholder="Pick a project"
                    ariaLabel="Select Jira project"
                    disabled={!jiraSiteId}
                    emptyText={
                      jiraSiteId ? "No projects in this site" : "Pick a site first"
                    }
                    usePortal
                  />
                </label>

                <label
                  className="jiraConnectorPanel__field"
                  style={{ display: "grid", gap: 6 }}
                >
                  Default issue type
                  <FancySelect
                    value={jiraIssueTypeId}
                    onChange={(value) => setJiraIssueTypeId(value)}
                    options={jiraIssueTypeOptions}
                    placeholder="Pick an issue type"
                    ariaLabel="Select Jira issue type"
                    disabled={!jiraProjectId}
                    emptyText={
                      jiraProjectId
                        ? "No createable issue types found"
                        : "Pick a project first"
                    }
                    usePortal
                  />
                </label>

                <div
                  className="jiraConnectorPanel__saveWrap"
                  style={{ alignSelf: "end" }}
                >
                  {jiraSaveMsg ? (
                    <span
                      className="muted jiraConnectorPanel__save"
                      aria-live="polite"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      {jiraSaveMsg}
                    </span>
                  ) : null}
                </div>
              </div>

              {jiraSyncMsg ? (
                <div className="muted small" style={{ marginTop: 8 }} aria-live="polite">
                  {jiraSyncMsg}
                </div>
              ) : null}

              <p className="muted jiraConnectorPanel__note" style={{ marginTop: 8 }}>
                BossDrop reads every open issue in the selected project, plus
                issues closed recently, and can create or update issues there
                for tasks, bugs, blockers, and follow-up work.
              </p>
            </>
          )}
        </div>
        </ConnectorFrame>
      )}

      {/* LINEAR */}
      {show.linear && (
        <ConnectorFrame
          logo={<LinearLogo />}
          name="Linear"
          connected={!!linearConnected}
          account={
            Array.isArray(linearPrefs?.teamKeys) && linearPrefs.teamKeys.length
              ? linearPrefs.teamKeys.join(", ")
              : undefined
          }
          expanded={!!expandedConnectors.linear}
          onToggle={() => toggleExpanded("linear")}
        >
          <LinearConnector
            apiBase={apiBase}
            linearConnected={linearConnected}
            setLinearConnected={setLinearConnected}
            setLinearData={setLinearData}
            linearPrefs={linearPrefs}
            setLinearPrefs={setLinearPrefs}
          />
        </ConnectorFrame>
      )}

      {/* CLICKUP */}
      {show.clickup && (
        <ConnectorFrame
          logo={<ClickUpLogo />}
          name="ClickUp"
          connected={!!clickupConnected}
          account={
            Array.isArray(clickupPrefs?.listIds) && clickupPrefs.listIds.length
              ? countLabel(clickupPrefs.listIds.length, "list", "lists")
              : undefined
          }
          expanded={!!expandedConnectors.clickup}
          onToggle={() => toggleExpanded("clickup")}
        >
          <ClickUpConnector
            apiBase={apiBase}
            clickupConnected={clickupConnected}
            setClickupConnected={setClickupConnected}
            setClickupData={setClickupData}
            clickupPrefs={clickupPrefs}
            setClickupPrefs={setClickupPrefs}
          />
        </ConnectorFrame>
      )}

      {/* NOTION */}
      {show.notion && (
        <ConnectorFrame
          logo={<NotionLogo />}
          name="Notion"
          connected={!!notionConnected}
          account={
            Array.isArray(notionPrefs?.dataSourceIds) &&
            notionPrefs.dataSourceIds.length
              ? countLabel(
                  notionPrefs.dataSourceIds.length,
                  "database",
                  "databases",
                )
              : undefined
          }
          expanded={!!expandedConnectors.notion}
          onToggle={() => toggleExpanded("notion")}
        >
          <NotionConnector
            apiBase={apiBase}
            notionConnected={notionConnected}
            setNotionConnected={setNotionConnected}
            setNotionData={setNotionData}
            notionPrefs={notionPrefs}
            setNotionPrefs={setNotionPrefs}
          />
        </ConnectorFrame>
      )}

      {/* MONDAY.COM */}
      {show.monday && (
        <ConnectorFrame
          logo={<MondayLogo />}
          name="monday.com"
          connected={!!mondayConnected}
          account={
            Array.isArray(mondayPrefs?.boardIds) && mondayPrefs.boardIds.length
              ? countLabel(mondayPrefs.boardIds.length, "board", "boards")
              : undefined
          }
          expanded={!!expandedConnectors.monday}
          onToggle={() => toggleExpanded("monday")}
        >
          <MondayConnector
            apiBase={apiBase}
            mondayConnected={mondayConnected}
            setMondayConnected={setMondayConnected}
            setMondayData={setMondayData}
            mondayPrefs={mondayPrefs}
            setMondayPrefs={setMondayPrefs}
          />
        </ConnectorFrame>
      )}

      {/* HUBSPOT */}
      {show.hubspot && (
        <ConnectorFrame
          logo={<ToolGlyph src={hubspotLogo} label="HubSpot" />}
          name="HubSpot"
          connected={!!hubspotConnected}
          expanded={!!expandedConnectors.hubspot}
          onToggle={() => toggleExpanded("hubspot")}
        >
          <HubSpotConnector
            apiBase={apiBase}
            hubspotConnected={hubspotConnected}
            setHubspotConnected={setHubspotConnected}
            hubspotPrefs={hubspotPrefs}
            setHubspotPrefs={setHubspotPrefs}
          />
        </ConnectorFrame>
      )}

      {/* PIPEDRIVE */}
      {show.pipedrive && (
        <ConnectorFrame
          logo={<PipedriveLogo />}
          name="Pipedrive"
          connected={!!pipedriveConnected}
          expanded={!!expandedConnectors.pipedrive}
          onToggle={() => toggleExpanded("pipedrive")}
        >
          <PipedriveConnector
            apiBase={apiBase}
            pipedriveConnected={pipedriveConnected}
            setPipedriveConnected={setPipedriveConnected}
            pipedrivePrefs={pipedrivePrefs}
            setPipedrivePrefs={setPipedrivePrefs}
          />
        </ConnectorFrame>
      )}

      {/* GITHUB */}
      {show.github && (
        <ConnectorFrame
          logo={<GitHubLogo />}
          name="GitHub"
          connected={!!githubConnected}
          account={
            Array.isArray(githubPrefs?.repos) && githubPrefs.repos.length
              ? githubPrefs.repos.join(", ")
              : undefined
          }
          expanded={!!expandedConnectors.github}
          onToggle={() => toggleExpanded("github")}
        >
          <GitHubConnector
            apiBase={apiBase}
            githubConnected={githubConnected}
            setGithubConnected={setGithubConnected}
            githubPrefs={githubPrefs}
            setGithubPrefs={setGithubPrefs}
            teamMembers={teamMembers}
            canManageWorkspace={canManageWorkspace}
          />
        </ConnectorFrame>
      )}

      {/* SLACK */}
      {show.slack && (
        <ConnectorFrame
          logo={<img src={slackLogo} alt="" className="toolLogo" />}
          name="Slack"
          connected={!!slackVerifiedConnected}
          account={slackPrefs?.teamName || undefined}
          expanded={!!expandedConnectors.slack}
          onToggle={() => toggleExpanded("slack")}
        >
        <div className="panelSub">
          <h3 className="crCardHead">
            <span className="crCardHead__title">
              {slackLogo ? (
                <img src={slackLogo} alt="" className="toolLogo" />
              ) : null}
              Slack
            </span>
          </h3>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "center",
            }}
          >
            <label style={{ flex: "0 0 auto", display: "grid", gap: 6 }}>
              Workspace Team ID
              <input
                placeholder="e.g., T0123456"
                value={teamId}
                onChange={(e) =>
                  setSlackPrefs({
                    ...slackPrefs,
                    teamId: e.target.value.toUpperCase().trim(),
                  })
                }
                style={{
                  fontSize: 16,
                  minHeight: 44,
                  width: "24ch",
                  maxWidth: "100%",
                  fontFamily:
                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace",
                }}
              />
            </label>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {!slackConnected && (
                <button className="btn" type="button" onClick={startSlackOAuth}>
                  Connect Slack
                </button>
              )}
              {slackConnected && (
                <button
                  className="btn btnDanger movedownabit"
                  type="button"
                  onClick={slackDisconnect}
                  disabled={!teamId || !slackConnected}
                  title="Disconnect Slack workspace from BossDrop"
                >
                  Disconnect
                </button>
              )}
            </div>

            <div style={{ marginLeft: "auto" }}>
              {slackConnected ? (
                <span
                  className="chip ok"
                  title="Connection status"
                >
                  <span className="chipDot" />
                  Connected
                  {slackPrefs.teamName ? ` • ${slackPrefs.teamName}` : ""}
                </span>
              ) : (
                <span className="muted">Not connected</span>
              )}
            </div>
          </div>

          {slackFieldMsg ? (
            <div className="connectorFieldMsg" role="alert">
              {slackFieldMsg}
            </div>
          ) : null}

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "center",
              marginTop: 10,
            }}
          >
            <label>
              History window (days)
              <input
                type="number"
                min={1}
                max={365 * 5}
                value={slackPrefs.days ?? 14}
                onChange={(e) =>
                  setSlackPrefs({
                    ...slackPrefs,
                    days: Math.max(
                      1,
                      Math.min(365 * 5, Number(e.target.value) || 14),
                    ),
                  })
                }
                style={{ fontSize: 16, minHeight: 44, width: 120 }}
              />
            </label>

            <button
              className="btn primary"
              type="button"
              disabled={!slackConnected}
              onClick={() => pullSlackNow(false)}
              title="Fetch latest channel messages into BossDrop"
            >
              {pulling ? "Pulling…" : "Pull latest now"}
            </button>
          </div>

          <div style={{ marginTop: 10 }}>
            <input
              className="inputFancy"
              placeholder="Filter channels…"
              value={filterQ}
              onChange={(e) => setFilterQ(e.target.value)}
              style={{ fontSize: 16, minHeight: 44 }}
            />
          </div>

          <div className="split" style={{ marginTop: 10 }}>
            <div>
              <h4 className="crSubTitle">
                Include Channels{" "}
                <span className="muted" style={{ fontSize: 12 }}>
                  ({includeSet.size})
                </span>
              </h4>
              <div
                className="list"
                style={{ maxHeight: 260, overflow: "auto" }}
              >
                {includeCandidates.map((c) => {
                  const checked = includeSet.has(c.id);
                  return (
                    <label
                      key={c.id}
                      className="listItem"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                      title="Channels the app is a member of"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        className="bd-check"
                        onChange={() => toggleInclude(c.id)}
                        style={{ width: 18, height: 18 }}
                      />
                      <span>#{c.name}</span>
                    </label>
                  );
                })}
                {!includeCandidates.length ? (
                  <div className="muted" style={{ padding: 8 }}>
                    No channels (filtered or excluded).
                  </div>
                ) : null}
              </div>
            </div>

            <div>
              <h4 className="crSubTitle">
                Exclude Channels{" "}
                <span className="muted" style={{ fontSize: 12 }}>
                  ({excludeSet.size})
                </span>
              </h4>
              <div
                className="list"
                style={{ maxHeight: 260, overflow: "auto" }}
              >
                {excludeCandidates.map((c) => {
                  const checked = excludeSet.has(c.id);
                  return (
                    <label
                      key={c.id}
                      className="listItem toolOption"
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        className="bd-check"
                        onChange={() => toggleExclude(c.id)}
                        style={{ width: 18, height: 18 }}
                      />
                      <span>#{c.name}</span>
                    </label>
                  );
                })}
                {!excludeCandidates.length ? (
                  <div className="muted" style={{ padding: 8 }}>
                    No channels (filtered or included).
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <p className="muted" style={{ marginTop: 8 }}>
            Tip: In Slack, run <code>/invite @BossDrop</code> inside channels
            you want analyzed. Then use “Check status” here to refresh the list.
          </p>
        </div>
        </ConnectorFrame>
      )}
    </div>
  );
}
